<template>
  <div v-if="!authReady" class="auth-loading">正在加载...</div>
  <Login v-else-if="!authUser" @login="handleLogin" />
  <div v-else id="app" class="opinion-console">
        <section v-if="bigScreenEnabled && activeSection === 'bigScreen'" class="big-screen" v-loading="overviewLoading" :element-loading-text="overviewLoadingText" element-loading-background="rgba(2, 12, 38, 0.75)">
      <div class="big-screen-bg"></div>
      <header class="big-screen-header">
        <button class="screen-back" type="button" @click="goSection('dashboard')">
          <i class="mdi mdi-arrow-left"></i> 返回
        </button>
        <div class="screen-title">
          <div class="screen-title-line"></div>
          <h1>舆情监测大屏</h1>
          <button type="button" class="page-help-btn screen-help-btn" aria-label="字段说明" @click="pageHelpVisible = true">
            <i class="mdi mdi-help-circle-outline"></i>
          </button>
          <div class="screen-title-line"></div>
        </div>
        <div class="screen-controls">
          <el-select
            v-model="screenTaskId"
            size="mini"
            filterable
            clearable
            placeholder="请选择任务"
            class="screen-task-select"
            @change="onScreenTaskChange"
          >
            <el-option label="全部可见任务" :value="null" />
            <el-option
              v-for="task in tasks"
              :key="task.id"
              :label="task.taskName || task.taskNo"
              :value="task.id"
            >
              <span>{{ task.taskName || task.taskNo }}</span>
              <span class="screen-task-option-meta">{{ externalTaskText(task) }}</span>
            </el-option>
          </el-select>
          <button class="screen-refresh" type="button" @click="refreshCurrent">
            <i class="el-icon-refresh-right"></i> 刷新
          </button>
          <span><i class="el-icon-time"></i> 数据统计更新于：{{ screenUpdatedAt || '—' }}</span>
        </div>
      </header>

      <main class="screen-content">
        <aside class="screen-column left">
          <section class="screen-panel public-sentiment">
            <div class="screen-panel-title">舆情统计</div>
            <div class="screen-stat-grid">
              <div v-for="item in bigScreenStats" :key="item.label" :class="['screen-stat-item', item.type]">
                <i :class="item.icon"></i>
                <div>
                  <strong>{{ item.value }}</strong>
                  <span>{{ item.label }}</span>
                </div>
              </div>
            </div>
          </section>
          <section class="screen-panel">
            <div class="screen-panel-title">数据来源分析</div>
            <div ref="screenSourceChart" class="screen-chart"></div>
          </section>
        </aside>

        <section class="screen-column center">
          <section class="screen-map-panel">
            <div ref="screenChinaMapChart" class="screen-map-chart"></div>
          </section>
          <section class="screen-panel hot-events">
            <div class="screen-panel-title">关注热点事件排名</div>
            <div class="screen-event-list">
              <div v-if="!screenHotEvents.length" class="empty-hint small screen-empty">暂无热点数据</div>
              <div v-for="(item, index) in screenHotEvents" :key="item.name" class="screen-event-row">
                <span>{{ index + 1 }}</span>
                <strong>{{ item.name }}</strong>
                <div><i :style="{ width: item.percent + '%' }"></i></div>
                <em>{{ item.value }}</em>
              </div>
            </div>
          </section>
        </section>

        <aside class="screen-column right">
          <section class="screen-panel latest-screen-news">
            <div class="screen-panel-title">最新舆情</div>
            <div class="screen-news-list">
              <div v-if="!screenLatestNews.length" class="empty-hint small screen-empty">暂无最新舆情</div>
              <div
                v-else
                :class="['screen-news-scroll-track', { static: screenLatestNews.length <= 3 }]"
                :style="screenNewsScrollStyle"
              >
                <article
                  v-for="item in screenScrollingNews"
                  :key="item.loopKey"
                  @click="openDetail(item.source)"
                >
                  <div class="screen-news-source">
                    <i class="mdi mdi-newspaper-variant-outline"></i>
                    <span>{{ item.source.platform }}</span>
                  </div>
                  <div class="screen-news-content">
                    <h3>{{ item.source.title }}</h3>
                    <p>{{ item.source.publishTime }} · {{ item.source.sentiment }}</p>
                  </div>
                  <em :class="sentimentClass(item.source.sentiment)">{{ item.source.sentiment }}</em>
                </article>
              </div>
            </div>
          </section>
          <section class="screen-panel">
            <div class="screen-panel-title">发布者互动</div>
            <div ref="screenInteractionChart" class="screen-chart"></div>
          </section>
        </aside>
      </main>

      <footer class="screen-footer">
        <section class="screen-panel mini">
          <div class="screen-panel-title">情感占比</div>
          <div ref="screenSentimentChart" class="screen-mini-chart"></div>
        </section>
        <section class="screen-panel mini wide">
          <div class="screen-panel-title">热门信息</div>
          <div class="screen-ticker">
            <span v-if="!screenTicker.length" class="screen-empty">暂无热门信息</span>
            <span v-for="item in screenTicker" :key="item">{{ item }}</span>
          </div>
        </section>
        <section class="screen-panel mini">
          <div class="screen-panel-title">事件统计</div>
          <div ref="screenEventChart" class="screen-mini-chart"></div>
        </section>
      </footer>
    </section>

    <el-container v-else class="app-shell">
      <el-aside width="248px" class="sidebar">
        <div class="brand">
          <div class="brand-mark">舆</div>
          <div>
            <h1>舆情监测</h1>
            <p>Opinion Intelligence</p>
          </div>
        </div>
        <el-menu
          :default-active="activeSection"
          class="side-menu"
          background-color="transparent"
          text-color="#6b7280"
          active-text-color="#1d4ed8"
          @select="handleSectionSelect"
        >
          <el-menu-item index="dashboard">
            <i class="mdi mdi-view-dashboard-outline"></i>
            <span slot="title">舆情概览</span>
          </el-menu-item>
          <el-menu-item index="analysis">
            <i class="mdi mdi-monitor-dashboard"></i>
            <span slot="title">实时监测</span>
          </el-menu-item>
          <el-menu-item index="risk">
            <i class="mdi mdi-shield-alert-outline"></i>
            <span slot="title">风险预警</span>
          </el-menu-item>
          <el-menu-item index="keyword">
            <i class="mdi mdi-tag-search-outline"></i>
            <span slot="title">关键词分析</span>
          </el-menu-item>
          <el-menu-item v-if="bigScreenEnabled" index="bigScreen">
            <i class="mdi mdi-television-guide"></i>
            <span slot="title">舆情大屏</span>
          </el-menu-item>
          <el-menu-item index="report">
            <i class="mdi mdi-file-chart-outline"></i>
            <span slot="title">舆情报告</span>
          </el-menu-item>
          <el-menu-item index="workOrder">
            <i class="mdi mdi-clipboard-list-outline"></i>
            <span slot="title">检测任务</span>
          </el-menu-item>
          <el-menu-item v-if="isGroupLeader" index="groupAdmin">
            <i class="mdi mdi-account-group-outline"></i>
            <span slot="title">成员管理</span>
          </el-menu-item>
        </el-menu>
        <div class="side-card">
          <span>监测状态</span>
          <strong>{{ tasks.length }} 个任务接入</strong>
          <p>启用后自动创建任务并同步监测结果。</p>
        </div>
      </el-aside>

    <el-container>
      <el-header class="app-header">
        <div class="header-copy">
          <span class="eyebrow">PUBLIC OPINION MONITOR</span>
          <div class="header-title-row">
            <h2>{{ pageTitle }}</h2>
            <el-tooltip content="字段来源与评判标准" placement="bottom" effect="dark">
              <button type="button" class="page-help-btn" aria-label="字段说明" @click="pageHelpVisible = true">
                <i class="mdi mdi-help-circle-outline"></i>
              </button>
            </el-tooltip>
          </div>
        </div>
        <div class="header-actions">
          <div class="header-user">
            <i class="mdi mdi-account-circle-outline"></i>
            <div class="header-user-info">
              <strong>{{ authUser.displayName }}</strong>
              <span>{{ authUser.groupName }} · {{ isGroupLeader ? '租户管理员' : '成员' }}</span>
            </div>
          </div>
          <el-button @click="handleLogout">退出登录</el-button>
          <el-button icon="el-icon-refresh" @click="refreshCurrent">刷新</el-button>
        </div>
      </el-header>

      <el-main class="content">
        <section v-if="activeSection === 'dashboard'" class="page-section dashboard-page-section" v-loading="overviewLoading" :element-loading-text="overviewLoadingText">
          <div class="image-like-dashboard">
            <div class="overview-timebar">
              <div class="overview-timebar-label">
                <i class="mdi mdi-calendar-range"></i>
                <span>统计时间范围</span>
              </div>
              <el-radio-group
                v-model="overviewDays"
                class="overview-timebar-presets"
                size="small"
                @change="selectOverviewDays"
              >
                <el-radio-button
                  v-for="opt in overviewRangeOptions"
                  :key="opt.value"
                  :label="opt.value"
                >{{ opt.label }}</el-radio-button>
              </el-radio-group>
              <div class="overview-timebar-custom">
                <el-date-picker
                  v-model="overviewStartDateTime"
                  size="small"
                  type="datetime"
                  placeholder="开始时间"
                  format="yyyy-MM-dd HH:mm:ss"
                  value-format="yyyy-MM-dd HH:mm:ss"
                  :picker-options="overviewPickerOptions"
                />
                <span class="overview-timebar-separator">至</span>
                <el-date-picker
                  v-model="overviewEndDateTime"
                  size="small"
                  type="datetime"
                  placeholder="结束时间"
                  format="yyyy-MM-dd HH:mm:ss"
                  value-format="yyyy-MM-dd HH:mm:ss"
                  :picker-options="overviewPickerOptions"
                />
                <el-button size="small" type="primary" @click="applyOverviewDateTimeRange">确定</el-button>
                <span class="overview-timebar-hint">当前：{{ overviewRangeText }}</span>
                <span class="overview-timebar-hint">{{ overviewStatusText }}</span>
              </div>
            </div>

            <el-alert
              v-if="!overviewLoading && !overviewHasData && overviewEmptyHint"
              class="overview-empty-alert"
              type="warning"
              :closable="false"
              show-icon
            >
              <span slot="title">{{ overviewEmptyHint }}</span>
            </el-alert>

            <div class="metric-strip">
              <div
                v-for="item in dashboardMetrics"
                :key="item.label"
                class="metric-tile clickable-metric"
                role="button"
                tabindex="0"
                @click="navigateOverviewMetric(item)"
                @keyup.enter="navigateOverviewMetric(item)"
              >
                <div :class="['metric-icon', item.color]"><i :class="item.icon"></i></div>
                <div>
                  <span>{{ item.label }}</span>
                  <strong>{{ item.value }}</strong>
                  <em>{{ item.change }}</em>
                </div>
              </div>
            </div>

            <section class="overview-card wide today-summary-card">
              <div class="overview-card-title">
                <strong><i class="mdi mdi-text-box-outline"></i> 今日重点</strong>
                <span>3秒了解今天发生什么</span>
              </div>
              <p class="today-summary-text">{{ todaySummaryText }}</p>
            </section>

            <div class="dashboard-grid top">
              <section class="overview-card">
                <div
                  class="overview-card-title nav-card-title"
                  role="button"
                  tabindex="0"
                  @click="navigateOverviewCard('analysis')"
                  @keyup.enter="navigateOverviewCard('analysis')"
                >
                  <strong>情感占比</strong>
                  <span>Sentiment</span>
                </div>
                <div class="sentiment-donut-box">
                  <div class="css-donut" :style="{ background: sentimentDonutGradient }">
                    <div class="css-donut-inner">
                      <strong>{{ dominantSentiment.percent }}%</strong>
                      <span>{{ dominantSentiment.label }}</span>
                    </div>
                  </div>
                  <div class="sentiment-legend">
                    <div v-for="item in sentimentOverview" :key="item.label">
                      <i :style="{ background: item.color }"></i>
                      <span>{{ item.label }}</span>
                      <strong>{{ item.value }}</strong>
                      <em>{{ item.percent }}%</em>
                    </div>
                  </div>
                </div>
                <div ref="dashboardDonutChart" class="dashboard-chart donut hidden-echart"></div>
              </section>

              <section class="overview-card keyword-hit-card">
                <h2
                  class="nav-card-title"
                  role="button"
                  tabindex="0"
                  @click="navigateOverviewCard('events')"
                  @keyup.enter="navigateOverviewCard('events')"
                >方案命中主体词</h2>
                <p>方案配置关键词命中数量排名</p>
                <div class="keyword-hit-list">
                  <div v-for="item in dashboardRankings" :key="item.name" class="keyword-hit-row">
                    <div class="keyword-hit-meta">
                      <strong>{{ item.name }}</strong>
                      <span>{{ item.value }}</span>
                    </div>
                    <div class="keyword-hit-track">
                      <i :style="{ width: item.percent + '%', background: item.color }"></i>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <section class="overview-card wide">
              <div
                class="overview-card-title nav-card-title"
                role="button"
                tabindex="0"
                @click="navigateOverviewCard('analysis')"
                @keyup.enter="navigateOverviewCard('analysis')"
              >
                <strong>舆情声量趋势</strong>
                <span>按统计日期日粒度</span>
              </div>
              <div ref="dashboardTrendChart" class="dashboard-chart trend"></div>
            </section>

            <div class="dashboard-grid middle">
              <section class="overview-card">
                <div
                  class="overview-card-title nav-card-title"
                  role="button"
                  tabindex="0"
                  @click="navigateOverviewCard('events')"
                  @keyup.enter="navigateOverviewCard('events')"
                >
                  <strong>热词云</strong>
                  <span>Keyword Cloud</span>
                </div>
                <div class="word-cloud-demo">
                  <span
                    v-for="item in wordCloudTerms"
                    :key="item.text"
                    :style="{ fontSize: item.size + 'px', color: item.color }"
                  >
                    {{ item.text }}
                  </span>
                </div>
              </section>
              <section class="overview-card">
                <div
                  class="overview-card-title nav-card-title"
                  role="button"
                  tabindex="0"
                  @click="navigateOverviewCard('events')"
                  @keyup.enter="navigateOverviewCard('events')"
                >
                  <strong>热词排行</strong>
                  <span>Hot Words</span>
                </div>
                <table class="mini-rank-table">
                  <tr v-for="item in keywordAnalysis" :key="item.label">
                    <td>{{ item.label }}</td>
                    <td>{{ item.value }}</td>
                    <td>{{ Math.max(8, item.value * 3) }}%</td>
                  </tr>
                </table>
              </section>
            </div>

            <div class="dashboard-grid bottom">
              <section class="overview-card">
                <div
                  class="overview-card-title nav-card-title"
                  role="button"
                  tabindex="0"
                  @click="navigateOverviewCard('analysis')"
                  @keyup.enter="navigateOverviewCard('analysis')"
                >
                  <strong>平台声量</strong>
                  <span>Source Volume</span>
                </div>
                <div ref="dashboardBarChart" class="dashboard-chart small"></div>
              </section>
              <section class="overview-card">
                <div
                  class="overview-card-title nav-card-title"
                  role="button"
                  tabindex="0"
                  @click="navigateOverviewCard('analysis')"
                  @keyup.enter="navigateOverviewCard('analysis')"
                >
                  <strong>信息类型分布</strong>
                  <span>Category</span>
                </div>
                <div ref="dashboardRingChart" class="dashboard-chart small"></div>
              </section>
            </div>

            <div class="dashboard-grid bottom">
              <section class="overview-card">
                <div class="overview-card-title">
                  <strong>地域分析（按IP）</strong>
                  <span>Region</span>
                </div>
                <div class="rank-bar-list">
                  <div v-for="item in regionAnalysis" :key="item.label" class="rank-bar-row">
                    <span class="rank-bar-label">{{ item.label }}</span>
                    <div class="rank-bar-track">
                      <i :style="{ width: item.percent + '%', background: '#35b99f' }"></i>
                    </div>
                    <span class="rank-bar-value">{{ item.value }}</span>
                  </div>
                  <div v-if="!regionAnalysis.length" class="empty-hint small">当前范围内暂无地域数据</div>
                </div>
              </section>
              <section class="overview-card">
                <div class="overview-card-title">
                  <strong>热点关键词 TOP</strong>
                  <span>Hot Keywords</span>
                </div>
                <div class="hot-keyword-grid">
                  <div v-for="item in hotKeywordTop" :key="item.name" class="hot-keyword-item">
                    <span class="hot-keyword-rank" :class="{ top: item.rank <= 3 }">{{ item.rank }}</span>
                    <span class="hot-keyword-name">{{ item.name }}</span>
                    <div class="hot-keyword-track"><i :style="{ width: item.percent + '%' }"></i></div>
                    <span class="hot-keyword-value">{{ item.value }}</span>
                  </div>
                  <div v-if="!hotKeywordTop.length" class="empty-hint small">当前范围内暂无热点关键词</div>
                </div>
              </section>
            </div>

            <section class="overview-card wide latest-warning-card negative-emphasis">
              <div
                class="overview-card-title nav-card-title"
                role="button"
                tabindex="0"
                @click="navigateOverviewCard('risk')"
                @keyup.enter="navigateOverviewCard('risk')"
              >
                <strong><i class="mdi mdi-bell-alert-outline"></i> 最新预警</strong>
                <span>按风险排序</span>
              </div>
              <div v-if="!latestWarnings.length" class="empty-hint small">暂无预警</div>
              <div
                v-for="item in latestWarnings"
                :key="item.id || item.title"
                class="latest-warning-item"
                @click="openRiskDetail(item)"
              >
                <span class="warning-stars">{{ warningStars(item.level) }}</span>
                <el-tag v-if="item.level" size="mini" :type="riskLevelType(item.level)">{{ item.level }}</el-tag>
                <el-tag v-else size="mini" type="danger">P2</el-tag>
                <span class="latest-warning-title">{{ item.title }}</span>
              </div>
            </section>

            <section class="overview-card wide risk-events-card negative-emphasis">
              <div
                class="overview-card-title nav-card-title"
                role="button"
                tabindex="0"
                @click="navigateOverviewCard('risk')"
                @keyup.enter="navigateOverviewCard('risk')"
              >
                <strong><i class="mdi mdi-shield-alert-outline"></i> 风险事件</strong>
                <span>按风险评分排序 · 共 {{ riskEventCount }} 条</span>
              </div>
              <div v-if="!riskEvents.length" class="empty-hint small">当前时间范围内暂无高风险事件</div>
              <div
                v-for="item in riskEvents.slice(0, 5)"
                :key="item.id || item.title"
                class="risk-event-row"
                @click="openRiskDetail(item)"
              >
                <div class="risk-event-score">
                  <strong>{{ item.score }}</strong>
                  <el-tag size="mini" :type="riskLevelType(item.level)">{{ item.level }}</el-tag>
                </div>
                <div class="risk-event-main">
                  <h4>{{ item.title }}</h4>
                  <p>{{ item.reason }}</p>
                  <div class="risk-event-meta">
                    <span>{{ item.platform }}</span>
                    <span>{{ item.authorName }}</span>
                    <span>曝光 {{ formatCompact(item.exposure) }}</span>
                    <span>互动 {{ formatCompact(item.interaction) }}</span>
                  </div>
                </div>
                <i class="mdi mdi-chevron-right risk-event-arrow"></i>
              </div>
            </section>

            <section class="overview-card wide negative-emphasis">
              <div
                class="overview-card-title nav-card-title"
                role="button"
                tabindex="0"
                @click="navigateOverviewCard('risk')"
                @keyup.enter="navigateOverviewCard('risk')"
              >
                <strong>负面预警信息</strong>
                <span>需关注内容 · P1-P4 风险等级</span>
              </div>
              <div v-if="!warningArticles.length" class="empty-hint small">暂无预警</div>
              <div
                v-for="item in warningArticles"
                :key="`warn-${item.id || item.title}`"
                class="warning-row clickable"
                @click="openRiskDetail(item)"
              >
                <i class="mdi mdi-alert-circle-outline"></i>
                <div>
                  <h4>{{ item.title }}</h4>
                  <p>{{ item.reason || item.content }}</p>
                </div>
                <el-tag size="mini" :type="riskLevelType(item.level)">{{ item.level || 'P4' }}</el-tag>
              </div>
            </section>

          </div>
        </section>

        <section v-if="activeSection === 'analysis'" class="page-section">
          <div class="daily-monitor-page" v-loading="monitorPageLoading" element-loading-text="加载监测数据...">
            <div class="monitor-filter-card">
              <div class="selected-line">
                <div class="filter-label active">已选条件</div>
                <div class="selected-tags">
                  <span v-for="tag in monitorSelectedTags" :key="tag">{{ tag }}</span>
                  <span v-if="!monitorSelectedTags.length" class="empty-tag">未设置筛选条件</span>
                </div>
                <button v-if="hasMonitorFilters" class="more-filter" type="button" @click="resetMonitorQuery">清空</button>
              </div>
              <div v-for="group in monitorFilterGroups" :key="group.label" class="filter-line">
                <div class="filter-label">{{ group.label }}</div>
                <div class="filter-options">
                  <span
                    v-for="item in group.items"
                    :key="`${group.label}-${item.name}`"
                    :class="['filter-pill', { active: isMonitorGroupActive(group.label, item.name) }]"
                    @click="applyMonitorGroupFilter(group.label, item.name)"
                  >
                    {{ item.name }}<em v-if="item.count != null">({{ item.count }})</em>
                  </span>
                </div>
              </div>
            </div>

            <div class="monitor-workspace">
              <main class="monitor-list-panel" v-loading="monitorLoading">
                <div class="monitor-query-bar">
                  <el-input
                    v-model="monitorQuery.keyword"
                    size="small"
                    clearable
                    placeholder="关键词 / 标题 / 作者"
                    prefix-icon="mdi mdi-magnify"
                    class="mq-keyword"
                    @keyup.enter.native="searchMonitor"
                  />
                  <el-select v-model="monitorQuery.platform" size="small" placeholder="全部平台" clearable class="mq-select" @change="searchMonitor">
                    <el-option v-for="p in monitorPlatforms" :key="p" :label="p" :value="p" />
                  </el-select>
                  <el-select v-model="monitorQuery.sentiment" size="small" placeholder="全部情感" clearable class="mq-select" @change="searchMonitor">
                    <el-option label="正面/正向" value="正面" />
                    <el-option label="中性" value="中性" />
                    <el-option label="负面/负向" value="负面" />
                  </el-select>
                  <el-input
                    v-model="monitorQuery.author"
                    size="small"
                    clearable
                    placeholder="账号 / 作者"
                    prefix-icon="mdi mdi-account-outline"
                    class="mq-select"
                  />
                  <el-date-picker
                    v-model="monitorQuery.dateRange"
                    size="small"
                    type="daterange"
                    value-format="yyyy-MM-dd"
                    range-separator="~"
                    start-placeholder="开始"
                    end-placeholder="结束"
                    class="mq-date"
                  />
                  <el-button size="small" type="primary" @click="searchMonitor">查询</el-button>
                  <el-button size="small" @click="resetMonitorQuery">重置</el-button>
                </div>

                <div class="monitor-toolbar">
                  <div>
                    <strong>实时监测信息</strong>
                    <span>共 {{ monitorTotal }} 条，数据来自实时监测</span>
                  </div>
                </div>

                <div class="monitor-tabs">
                  <span v-for="tab in monitorTabs" :key="tab.key" :class="{ active: monitorTab === tab.key }" @click="switchMonitorTab(tab.key)">
                    {{ tab.label }}<em v-if="tab.key === 'favorite'">({{ favoriteIds.length }})</em>
                  </span>
                </div>

                <div v-if="!filteredMonitorArticles.length" class="empty-hint">没有符合条件的信息</div>

                <article v-for="item in filteredMonitorArticles" :key="articleKey(item)" class="monitor-article">
                  <div class="article-check">
                    <el-checkbox :value="isMarked(item)" @change="toggleMark(item)" />
                  </div>
                  <div class="article-main">
                    <div class="article-title-row">
                      <h3 @click="openDetail(item)">{{ item.title }}</h3>
                      <el-tag size="mini" :type="riskType(item.riskLevel)">{{ item.riskLevel }}风险</el-tag>
                      <el-tag v-if="isMarked(item)" size="mini" type="warning">已标记</el-tag>
                    </div>
                    <p>{{ item.content }}</p>
                    <div class="article-tags">
                      <span class="ntag">{{ item.platform }}</span>
                      <span class="categorylable">{{ item.sentiment }}</span>
                      <span v-if="item.matchedKeywordsJson" class="ipotag">{{ item.matchedKeywordsJson }}</span>
                      <span v-if="item.imageCount" class="mediatag"><i class="mdi mdi-image-multiple-outline"></i> 图 {{ item.imageCount }}</span>
                      <span v-if="item.videoCount" class="mediatag"><i class="mdi mdi-play-circle-outline"></i> 视频</span>
                    </div>
                    <div class="article-meta">
                      <span><i class="mdi mdi-account-outline"></i> {{ item.authorName }}</span>
                      <span><i class="mdi mdi-account-group-outline"></i> 粉丝 {{ formatCompact(item.fansCount || 0) }}</span>
                      <span><i class="mdi mdi-clock-time-four-outline"></i> {{ item.publishTime }}</span>
                      <span><i class="mdi mdi-eye-outline"></i> 阅读 {{ item.readCount || 0 }}</span>
                      <span><i class="mdi mdi-comment-text-outline"></i> 评论 {{ item.commentCount || 0 }}</span>
                      <span><i class="mdi mdi-share-variant-outline"></i> 转发 {{ item.repostCount || 0 }}</span>
                      <span><i class="mdi mdi-thumb-up-outline"></i> 点赞 {{ item.likeCount || 0 }}</span>
                      <span><i class="mdi mdi-star-outline"></i> 收藏 {{ item.collectCount || 0 }}</span>
                      <span><i class="mdi mdi-share-outline"></i> 分享 {{ item.shareCount || 0 }}</span>
                    </div>
                  </div>
                  <div class="article-actions">
                    <el-button size="mini" type="text" @click="openDetail(item)">
                      <i class="mdi mdi-file-eye-outline"></i> 查看详情
                    </el-button>
                    <el-button size="mini" type="text" @click="toggleMark(item)">
                      <i class="mdi mdi-flag-outline"></i> {{ isMarked(item) ? '取消标记' : '标记' }}
                    </el-button>
                    <el-button size="mini" type="text" @click="shareArticle(item)">
                      <i class="mdi mdi-share-variant-outline"></i> 转发分享
                    </el-button>
                    <el-button size="mini" type="text" :class="{ 'is-fav': isFavorite(item) }" @click="toggleFavorite(item)">
                      <i :class="isFavorite(item) ? 'mdi mdi-star' : 'mdi mdi-star-outline'"></i> {{ isFavorite(item) ? '已收藏' : '收藏' }}
                    </el-button>
                  </div>
                </article>

                <div class="monitor-pagination">
                  <span>显示 {{ monitorPageFrom }}-{{ monitorPageTo }} 条，共 {{ monitorTotal }} 条</span>
                  <el-pagination
                    small
                    layout="prev, pager, next"
                    :total="monitorTotal"
                    :page-size="monitorPageSize"
                    :current-page.sync="monitorPageNo"
                    @current-change="onMonitorPageChange"
                  />
                </div>
              </main>
            </div>
          </div>
        </section>

        <section v-if="activeSection === 'risk'" class="page-section">
          <div class="insight-page risk-center-page" v-loading="riskPageLoading" element-loading-text="加载风险数据...">
            <div class="event-timebar">
              <div class="event-timebar-label">
                <i class="mdi mdi-calendar-range"></i>
                <span>统计时间范围</span>
              </div>
              <div class="event-timebar-presets">
                <button
                  v-for="opt in riskRangeOptions"
                  :key="opt.value"
                  type="button"
                  :class="['time-pill', { active: !riskStatStartDate && !riskStatEndDate && riskStatDays === opt.value }]"
                  @click="selectRiskStatDays(opt.value)"
                >{{ opt.label }}</button>
              </div>
              <div class="event-timebar-custom">
                <el-date-picker
                  v-model="riskStatStartDate"
                  size="small"
                  type="date"
                  value-format="yyyy-MM-dd"
                  placeholder="开始日期"
                  class="event-date-input"
                  @change="onRiskStatDateChange"
                />
                <el-date-picker
                  v-model="riskStatEndDate"
                  size="small"
                  type="date"
                  value-format="yyyy-MM-dd"
                  placeholder="结束日期"
                  class="event-date-input"
                  @change="onRiskStatDateChange"
                />
                <span class="event-timebar-hint">{{ riskRangeText }}</span>
                <el-tag v-if="riskKeyword" size="mini" closable @close="clearRiskKeyword">关键词：{{ riskKeyword }}</el-tag>
                <el-select
                  v-model="riskTaskId"
                  size="small"
                  filterable
                  clearable
                  placeholder="按任务筛选"
                  class="risk-task-select"
                  @change="selectRiskTask"
                >
                  <el-option
                    v-for="task in tasks"
                    :key="task.id"
                    :label="task.taskName"
                    :value="task.id"
                  >
                    <span>{{ task.taskName }}</span>
                    <span class="results-task-option-meta">{{ extractTaskKeyword(task) }}</span>
                  </el-option>
                </el-select>
              </div>
            </div>

            <div class="risk-type-grid">
              <el-card class="panel-card risk-type-card negative">
                <div slot="header" class="section-header">
                  <strong><i class="mdi mdi-emoticon-sad-outline"></i> 负面情感风险</strong>
                  <el-tag size="mini" type="danger">{{ riskNegativeCount }} 条</el-tag>
                </div>
                <p class="risk-type-desc">识别情绪偏负面的内容，并按风险高低排序，方便优先处理。</p>
                <div v-if="!riskItems.length" class="empty-hint small">暂无负面风险内容</div>
                <div v-for="item in pagedRiskCardItems" :key="'neg-' + item.id" class="risk-type-item" @click="openRiskDetail(item)">
                  <el-tag size="mini" :type="riskLevelType(item.level)">{{ item.level }}</el-tag>
                  <span class="risk-type-item-title">{{ item.title }}</span>
                  <span class="risk-type-item-meta">{{ item.platform }} · 评分 {{ item.score }}</span>
                </div>
                <div v-if="riskItems.length" class="risk-card-pagination">
                  <el-pagination
                    layout="prev, pager, next"
                    :total="riskItems.length"
                    :page-size="riskCardPageSize"
                    :current-page.sync="riskCardPage"
                    small
                    background
                  />
                </div>
              </el-card>

              <el-card class="panel-card risk-type-card hotspot">
                <div slot="header" class="section-header">
                  <strong><i class="mdi mdi-fire"></i> 热点聚集风险</strong>
                  <el-tag size="mini" type="warning">{{ riskHotspotClusters.length }} 组</el-tag>
                </div>
                <p class="risk-type-desc">同关键词在统计时段内高频聚集（≥2 条负面），按发文量与传播量评估。</p>
                <div v-if="!riskHotspotClusters.length" class="empty-hint small">暂无热点聚集</div>
                <div v-for="cluster in pagedHotspotClusters" :key="cluster.keyword" class="risk-type-item" @click="openHotspotDetail(cluster)">
                  <el-tag size="mini" :type="riskLevelType(cluster.level)">{{ cluster.level }}</el-tag>
                  <span class="risk-type-item-title">{{ cluster.keyword }}</span>
                  <span class="risk-type-item-meta">{{ cluster.articleCount }} 条 · 曝光 {{ formatCompact(cluster.exposure) }}</span>
                </div>
                <div v-if="riskHotspotClusters.length" class="risk-card-pagination">
                  <el-pagination
                    layout="prev, pager, next"
                    :total="riskHotspotClusters.length"
                    :page-size="riskCardPageSize"
                    :current-page.sync="hotspotCardPage"
                    small
                    background
                  />
                </div>
              </el-card>

              <el-card class="panel-card risk-type-card kol">
                <div slot="header" class="section-header">
                  <strong><i class="mdi mdi-account-star"></i> KOL 参与风险</strong>
                  <el-tag size="mini" type="primary">{{ riskKolRisks.length }} 个账号</el-tag>
                </div>
                <p class="risk-type-desc">粉丝数 ≥ 10 万的高影响账号参与负面传播，按粉丝量与负面发文评估。</p>
                <div v-if="!riskKolRisks.length" class="empty-hint small">暂无 KOL 参与风险</div>
                <div v-for="kol in pagedKolRisks" :key="kol.id" class="risk-type-item" @click="openKolRiskDetail(kol)">
                  <el-tag size="mini" :type="riskLevelType(kol.level)">{{ kol.level }}</el-tag>
                  <span class="risk-type-item-title">{{ kol.authorName }}</span>
                  <span class="risk-type-item-meta">{{ kol.platform }} · 粉丝 {{ formatCompact(kol.fans) }} · 负面 {{ kol.negativePosts }} 条</span>
                </div>
                <div v-if="riskKolRisks.length" class="risk-card-pagination">
                  <el-pagination
                    layout="prev, pager, next"
                    :total="riskKolRisks.length"
                    :page-size="riskCardPageSize"
                    :current-page.sync="kolRiskCardPage"
                    small
                    background
                  />
                </div>
              </el-card>
            </div>

            <div class="risk-level-strip">
              <div v-for="lv in riskLevels" :key="lv.name" :class="['risk-level-tile', lv.name.toLowerCase()]">
                <span class="risk-level-name">{{ lv.name }}</span>
                <strong>{{ lv.value }}</strong>
                <em>{{ riskLevelLabel(lv.name).split(' ')[1] }}</em>
              </div>
            </div>

            <el-card class="panel-card">
              <div slot="header" class="section-header">
                <div>
                  <strong>风险预警列表</strong>
                  <span class="section-sub">按风险评分排序，规则：负面情感 + 曝光/互动/粉丝影响力 → P1–P4</span>
                </div>
                <el-button size="small" icon="el-icon-refresh" @click="refreshRiskPage">刷新</el-button>
              </div>
              <div class="risk-list-filter">
                <el-select v-model="riskListLevel" size="small" clearable placeholder="风险等级" class="risk-list-filter-item" @change="riskListPage = 1">
                  <el-option label="P1" value="P1" />
                  <el-option label="P2" value="P2" />
                  <el-option label="P3" value="P3" />
                  <el-option label="P4" value="P4" />
                </el-select>
                <el-select v-model="riskListPlatform" size="small" clearable filterable placeholder="平台" class="risk-list-filter-item" @change="riskListPage = 1">
                  <el-option v-for="p in riskItemPlatforms" :key="p" :label="p" :value="p" />
                </el-select>
                <el-input
                  v-model="riskListSearch"
                  size="small"
                  clearable
                  placeholder="搜索标题/内容/账号"
                  prefix-icon="el-icon-search"
                  class="risk-list-filter-search"
                  @input="riskListPage = 1"
                />
                <span class="risk-list-filter-total">共 {{ filteredRiskItems.length }} 条</span>
              </div>
              <div v-if="!filteredRiskItems.length" class="empty-hint">当前筛选条件下暂无负面风险数据</div>
              <div v-for="item in pagedRiskItems" :key="item.id" class="risk-item" @click="openRiskDetail(item)">
                <div class="risk-item-score">
                  <div class="risk-score-ring" :style="{ '--score': item.score }">
                    <strong>{{ item.score }}</strong>
                  </div>
                  <el-tag size="mini" :type="riskLevelType(item.level)">{{ riskLevelLabel(item.level) }}</el-tag>
                </div>
                <div class="risk-item-main">
                  <h4>{{ item.title }}</h4>
                  <p class="risk-reason"><i class="mdi mdi-alert-circle-outline"></i> {{ item.reason }}</p>
                  <div class="risk-factors">
                    <span v-for="f in riskFactors(item)" :key="f" class="risk-factor-chip">{{ f }}</span>
                  </div>
                  <div class="risk-item-meta">
                    <span><i class="mdi mdi-web"></i> {{ item.platform }}</span>
                    <span><i class="mdi mdi-account-outline"></i> {{ item.authorName }}</span>
                    <span><i class="mdi mdi-eye-outline"></i> 曝光 {{ formatCompact(item.exposure) }}</span>
                    <span><i class="mdi mdi-gesture-tap"></i> 互动 {{ formatCompact(item.interaction) }}</span>
                    <span><i class="mdi mdi-clock-outline"></i> {{ formatDateTime(item.publishTime) }}</span>
                  </div>
                </div>
                <div class="risk-item-action">
                  <el-link v-if="item.url" :href="item.url" target="_blank" type="primary" :underline="false" @click.stop>查看原文</el-link>
                </div>
              </div>
              <div v-if="filteredRiskItems.length" class="risk-list-pagination">
                <el-pagination
                  layout="total, sizes, prev, pager, next, jumper"
                  :total="filteredRiskItems.length"
                  :page-size="riskListPageSize"
                  :page-sizes="[10, 20, 50, 100]"
                  :current-page.sync="riskListPage"
                  background
                  @size-change="onRiskListSizeChange"
                />
              </div>
            </el-card>

            <el-card class="panel-card" v-loading="kolLoading">
              <div slot="header" class="section-header">
                <div>
                  <strong>KOL 影响力排行</strong>
                  <span class="section-sub">按粉丝量与互动量综合影响力排序，识别高影响力账号</span>
                </div>
                <el-button size="small" icon="el-icon-refresh" @click="loadKol">刷新</el-button>
              </div>
              <el-table :data="kolItems" stripe>
                <el-table-column type="index" label="#" width="60" />
                <el-table-column prop="name" label="账号" min-width="140">
                  <template slot-scope="scope">
                    <div class="kol-name"><i class="mdi mdi-account-circle-outline"></i> {{ scope.row.name }}</div>
                  </template>
                </el-table-column>
                <el-table-column prop="platform" label="平台" width="80" />
                <el-table-column label="等级" width="80">
                  <template slot-scope="scope">
                    <el-tag size="mini" :type="kolTierType(scope.row)">{{ kolTier(scope.row) }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="认证" width="90">
                  <template slot-scope="scope">{{ kolVerified(scope.row) }}</template>
                </el-table-column>
                <el-table-column label="粉丝" width="90">
                  <template slot-scope="scope">{{ formatCompact(scope.row.fans) }}</template>
                </el-table-column>
                <el-table-column prop="posts" label="发文" width="70" />
                <el-table-column label="平均互动" width="90">
                  <template slot-scope="scope">{{ formatCompact(kolAvgInteraction(scope.row)) }}</template>
                </el-table-column>
                <el-table-column label="情感倾向" width="100">
                  <template slot-scope="scope">
                    <el-tag size="mini" :type="kolTendencyType(scope.row.sentimentTendency)">{{ scope.row.sentimentTendency }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="历史风险" width="90">
                  <template slot-scope="scope">
                    <el-tag size="mini" :type="kolRiskType(scope.row)">{{ kolRiskLevel(scope.row) }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="影响力" width="130">
                  <template slot-scope="scope">
                    <div class="kol-influence">
                      <div class="rank-bar-track">
                        <i :style="{ width: scope.row.influenceScore + '%', background: '#2f5bff' }"></i>
                      </div>
                      <strong>{{ scope.row.influenceScore }}</strong>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="140" fixed="right">
                  <template slot-scope="scope">
                    <el-button size="mini" type="text" @click="openKolProfile(scope.row)">画像</el-button>
                  </template>
                </el-table-column>
              </el-table>
            </el-card>
          </div>
        </section>

        <section v-if="activeSection === 'keyword'" class="page-section keyword-page-section" v-loading="brandLoading" element-loading-text="加载关键词分析...">
          <div class="insight-page">
            <div class="event-timebar">
              <div class="event-timebar-label">
                <i class="mdi mdi-tag-search-outline"></i>
                <span>选择任务</span>
              </div>
              <el-select
                v-model="keywordTaskId"
                size="small"
                filterable
                clearable
                placeholder="选择已提交的任务"
                class="keyword-task-select"
                @change="selectKeywordTask"
              >
                <el-option
                  v-for="task in tasks"
                  :key="task.id"
                  :label="task.taskName"
                  :value="task.id"
                >
                  <span>{{ task.taskName }}</span>
                  <span class="results-task-option-meta">{{ extractTaskKeyword(task) }}</span>
                </el-option>
              </el-select>
              <el-tag v-if="keywordText" size="small" type="primary" style="margin-left: 8px">关键词：{{ keywordText }}</el-tag>
              <el-button size="mini" icon="el-icon-refresh" style="margin-left: 8px" @click="loadKeywordAnalysis">刷新</el-button>
            </div>

            <div class="event-timebar">
              <div class="event-timebar-label">
                <i class="mdi mdi-calendar-range"></i>
                <span>统计时间范围</span>
              </div>
              <div class="event-timebar-presets">
                <button
                  v-for="opt in eventRangeOptions"
                  :key="opt.value"
                  type="button"
                  :class="['time-pill', { active: !eventStatStartDate && !eventStatEndDate && eventStatDays === opt.value }]"
                  @click="selectKeywordDays(opt.value)"
                >{{ opt.label }}</button>
              </div>
              <div class="event-timebar-custom">
                <el-date-picker
                  v-model="eventStatStartDate"
                  size="small"
                  type="date"
                  value-format="yyyy-MM-dd"
                  placeholder="开始日期"
                  class="event-date-input"
                  @change="onKeywordDateChange"
                />
                <el-date-picker
                  v-model="eventStatEndDate"
                  size="small"
                  type="date"
                  value-format="yyyy-MM-dd"
                  placeholder="结束日期"
                  class="event-date-input"
                  @change="onKeywordDateChange"
                />
                <span class="event-timebar-hint">{{ eventRangeText }}</span>
              </div>
            </div>

            <div v-if="!keywordTaskId" class="empty-hint">请选择一个已提交的任务，系统将按其配置的关键词进行分析</div>

            <template v-else>
            <div class="keyword-section-title">
              <strong><i class="mdi mdi-chart-box-outline"></i> 关键词概览</strong>
              <span>该关键词在所选时间范围内的整体健康度、声量与情感</span>
            </div>
            <el-alert
              v-if="!keywordHasData"
              type="info"
              show-icon
              :closable="false"
              class="keyword-empty-alert"
              :title="`当前时间范围（${eventRangeText}）内暂无「${keywordText}」的相关数据，健康指数按 0 分显示`"
            />
            <div class="brand-detail">
              <div class="brand-health-card">
                <div class="brand-health-ring" :style="{ '--health': activeBrand.healthIndex, '--health-color': keywordHasData ? brandHealthColor(activeBrand.healthIndex) : '#c0c8d4' }">
                  <div class="brand-health-inner">
                    <strong>{{ activeBrand.healthIndex }}</strong>
                    <span>健康指数</span>
                  </div>
                </div>
                <div class="brand-health-meta">
                  <h3>{{ keywordText }}</h3>
                  <el-tag size="small" :type="brandHealthType(activeBrand.healthLabel)">舆情健康度：{{ activeBrand.healthLabel }}</el-tag>
                  <p>综合曝光、情感、传播、投诉等维度计算</p>
                </div>
              </div>

              <div class="brand-stat-grid">
                <div class="brand-stat clickable" title="查看相关舆情" @click="jumpKeywordToMonitor('')"><span>今日声量</span><strong>{{ activeBrand.volume }}</strong></div>
                <div class="brand-stat clickable" title="查看相关舆情" @click="jumpKeywordToMonitor('')"><span>曝光量</span><strong>{{ formatCompact(activeBrand.exposure) }}</strong></div>
                <div class="brand-stat clickable" title="查看相关舆情" @click="jumpKeywordToMonitor('')"><span>互动量</span><strong>{{ formatCompact(activeBrand.interaction) }}</strong></div>
                <div class="brand-stat negative clickable" title="查看负面风险" @click="jumpKeywordToRisk"><span>负面/投诉</span><strong>{{ activeBrand.negative }}</strong></div>
              </div>

              <div class="brand-grid">
                <el-card class="panel-card">
                  <div slot="header" class="section-header clickable-header" title="查看相关舆情" @click="jumpKeywordToMonitor('')"><strong>声量趋势</strong><span class="section-sub">点击查看相关舆情 ›</span></div>
                  <div class="mini-bar-chart">
                    <div v-for="item in activeBrandTrend" :key="item.label" class="mini-bar-col">
                      <div class="mini-bar" :style="{ height: item.height + '%' }" :title="item.value"></div>
                      <span>{{ item.label }}</span>
                    </div>
                  </div>
                </el-card>

                <el-card class="panel-card">
                  <div slot="header" class="section-header"><strong>情感分布</strong><span class="section-sub">点击某项按情感筛选 ›</span></div>
                  <div class="sentiment-bars">
                    <div
                      v-for="item in activeBrandSentiment"
                      :key="item.label"
                      class="sentiment-bar-row clickable"
                      :title="`查看${item.label}舆情`"
                      @click="jumpKeywordToMonitor(item.label)"
                    >
                      <span>{{ item.label }}</span>
                      <div class="rank-bar-track">
                        <i :style="{ width: item.percent + '%', background: item.color }"></i>
                      </div>
                      <span>{{ item.percent }}%</span>
                    </div>
                  </div>
                </el-card>

                <el-card v-if="activeEvent" class="panel-card">
                  <div slot="header" class="section-header"><strong>首发账号</strong><span class="section-sub">热点事件最早发布</span></div>
                  <div v-if="activeEventFirstAccount" class="first-account">
                    <i class="mdi mdi-account-star-outline"></i>
                    <div>
                      <strong>{{ activeEventFirstAccount.authorName || '未知账号' }}</strong>
                      <p>{{ activeEventFirstAccount.platform }}　{{ activeEventFirstAccount.publishTime }}</p>
                    </div>
                  </div>
                  <div v-else class="empty-hint small">暂无数据</div>
                </el-card>

                <el-card v-if="activeEvent" class="panel-card">
                  <div slot="header" class="section-header"><strong>传播平台分布</strong><span class="section-sub">热点事件传播渠道</span></div>
                  <div v-if="activeEventPlatforms.length" class="rank-bar-list">
                    <div v-for="p in activeEventPlatforms" :key="p.name" class="rank-bar-row">
                      <span class="rank-bar-label">{{ p.name }}</span>
                      <div class="rank-bar-track"><i :style="{ width: p.percent + '%', background: '#128bed' }"></i></div>
                      <span class="rank-bar-value">{{ p.value }}</span>
                    </div>
                  </div>
                  <div v-else class="empty-hint small">暂无数据</div>
                </el-card>
              </div>
            </div>

            <div class="keyword-section-title">
              <strong><i class="mdi mdi-lightning-bolt-outline"></i> 相关事件</strong>
              <span>该关键词下的聚类事件与传播详情</span>
              <el-select
                v-if="keywordEvents.length > 1"
                v-model="selectedEventId"
                size="mini"
                placeholder="切换事件"
                class="keyword-event-switch"
              >
                <el-option
                  v-for="ev in keywordEvents"
                  :key="ev.id"
                  :label="ev.name"
                  :value="ev.id"
                >
                  <span>{{ ev.name }}</span>
                  <span class="results-task-option-meta">热度 {{ ev.heat }}</span>
                </el-option>
              </el-select>
            </div>

            <div class="event-center">
            <main class="event-detail-panel" v-if="activeEvent">
              <div class="event-detail-head">
                <div class="event-detail-headmain">
                  <h3>{{ activeEvent.name }}</h3>
                  <div class="event-detail-tags">
                    <el-tag size="mini" :type="eventStageType(activeEvent.stage)">{{ activeEvent.stage }}</el-tag>
                    <el-tag size="mini" :type="sentimentType(activeEvent.dominantSentiment)">{{ activeEvent.dominantSentiment }}</el-tag>
                    <span class="event-time">{{ formatDateTime(activeEvent.firstSeen) }} ~ {{ formatDateTime(activeEvent.lastSeen) }}</span>
                  </div>
                  <div class="event-detail-metrics">
                    <span><i class="mdi mdi-fire"></i> 热度 {{ activeEvent.heat }}</span>
                    <span><i class="mdi mdi-file-document-outline"></i> 相关文章 {{ activeEvent.articleCount }}</span>
                    <span class="neg"><i class="mdi mdi-alert-circle-outline"></i> 负面 {{ activeEvent.negativeCount }}</span>
                  </div>
                </div>
                <div class="event-jump-actions">
                  <el-button size="small" type="primary" icon="el-icon-search" @click="jumpEventToAnalysis(activeEvent)">查看相关舆情</el-button>
                  <el-button size="small" type="warning" plain icon="el-icon-warning-outline" @click="jumpEventToRisk(activeEvent)">查看负面风险</el-button>
                </div>
              </div>

              <div class="event-lifecycle">
                <div
                  v-for="stage in lifecycleStages"
                  :key="stage"
                  :class="['lifecycle-node', {
                    done: eventStageIndex(stage) < eventStageIndex(activeEvent.stage),
                    current: stage === activeEvent.stage
                  }]"
                >
                  <span class="lifecycle-dot"></span>
                  <span class="lifecycle-label">{{ stage }}</span>
                </div>
              </div>

              <el-card class="panel-card event-summary-card">
                <p class="today-summary-text"><i class="mdi mdi-text-box-outline"></i> {{ activeEvent.summary }}</p>
              </el-card>

              <el-card class="panel-card">
                <div slot="header" class="section-header"><strong>代表内容</strong><span class="section-sub">按曝光量排序</span></div>
                <div v-if="!activeEventSamples.length" class="empty-hint small">暂无代表内容</div>
                <div v-for="item in activeEventSamples" :key="item.externalOpinionId || item.title" class="event-sample">
                  <h4 @click="openDetail(item)">{{ item.title }}</h4>
                  <p>{{ item.content }}</p>
                  <div class="event-sample-meta">
                    <span>{{ item.platform }}</span>
                    <span>{{ item.authorName }}</span>
                    <span>{{ item.publishTime }}</span>
                    <el-tag size="mini" :type="sentimentType(item.sentiment)">{{ item.sentiment }}</el-tag>
                  </div>
                </div>
              </el-card>
            </main>

            <main v-else class="event-detail-panel event-detail-empty">
              <div class="empty-hint">当前时间范围内暂无聚类事件，请调整任务或统计时段后重试</div>
            </main>
            </div>
            </template>
          </div>
        </section>

        <section v-if="activeSection === 'report'" class="page-section report-page">
          <el-card class="panel-card">
            <div slot="header" class="section-header">
              <div>
                <strong>舆情报告</strong>
                <span class="section-sub">基于当前监测数据一键生成，适合直接汇报或二次编辑</span>
              </div>
              <div class="wo-header-actions">
                <el-select v-model="reportTaskId" size="small" filterable placeholder="请选择监测任务" style="width: 220px">
                  <el-option v-for="task in tasks" :key="task.id" :label="task.taskName" :value="task.id" />
                </el-select>
                <el-radio-group v-model="reportType" size="small">
                  <el-radio-button label="daily">日报</el-radio-button>
                  <el-radio-button label="weekly">周报</el-radio-button>
                  <el-radio-button label="monthly">月报</el-radio-button>
                </el-radio-group>
                <el-button size="small" type="primary" icon="mdi mdi-auto-fix" :loading="reportGenerating" :disabled="!reportTaskId" @click="generateReport">一键生成</el-button>
              </div>
            </div>

            <div v-if="reportJobStatusText" class="report-job-status">
              {{ reportJobStatusText }}
            </div>

            <div class="report-history">
              <div class="report-history-header">
                <strong>历史报告</strong>
                <el-button size="mini" type="text" :loading="reportHistoryLoading" @click="loadReportHistory">刷新</el-button>
              </div>
              <el-table
                v-if="reportHistory.length"
                :data="reportHistory"
                size="mini"
                border
                class="report-history-table"
              >
                <el-table-column prop="createTime" label="生成时间" width="160" />
                <el-table-column label="类型" width="80">
                  <template slot-scope="{ row }">{{ reportTypeText(row.reportType) }}</template>
                </el-table-column>
                <el-table-column prop="queryText" label="分析范围" min-width="220" show-overflow-tooltip />
                <el-table-column label="状态" width="100">
                  <template slot-scope="{ row }">
                    <el-tag size="mini" :type="reportStatusType(row.status)">{{ reportStatusText(row.status) }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="90">
                  <template slot-scope="{ row }">
                    <el-button
                      size="mini"
                      type="text"
                      :disabled="row.status !== 'COMPLETED' || !row.report"
                      @click="openReportHistory(row)"
                    >
                      查看
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
              <div v-else class="report-history-empty">暂无历史报告</div>
            </div>

            <div v-if="!report" class="report-empty">
              <i class="mdi mdi-file-chart-outline"></i>
              <p>点击「一键生成」，系统将汇总总体概述、热点、事件、风险、品牌健康并给出处置建议。</p>
            </div>

            <div v-else class="report-body">
              <div class="report-doc">
                <h1>{{ report.title }}</h1>
                <p class="report-meta">生成时间：{{ report.generatedAt }}　|　统计范围：{{ report.range }}</p>
                <div v-for="sec in report.sections" :key="sec.title" class="report-section">
                  <h2>{{ sec.title }}</h2>
                  <ul>
                    <li v-for="(line, idx) in sec.lines" :key="idx">{{ line }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </el-card>
        </section>

        <section v-if="activeSection === 'workOrder'" class="page-section">
          <el-card class="panel-card work-order-apply-card">
            <div slot="header" class="section-header">
              <div>
                <span class="eyebrow">TASK APPLICATION</span>
                <h3>任务申请</h3>
                <el-tag v-if="appliesOffline" size="mini" type="warning" style="margin-left: 8px">后端未连接，当前为演示数据</el-tag>
              </div>
              <div class="apply-header-actions">
                <el-button icon="el-icon-magic-stick" @click="openApplyAssist">Agent 辅助申请</el-button>
                <el-button type="primary" icon="el-icon-plus" @click="openCreateDialog">提交任务申请</el-button>
              </div>
            </div>
              <el-table :data="pagedApplies" border class="data-table">
                <el-table-column prop="applyNo" label="申请编号" width="190" />
                <el-table-column prop="taskName" label="任务名称" min-width="160" />
                <el-table-column label="渠道" min-width="180" show-overflow-tooltip>
                  <template slot-scope="{ row }">
                    <span class="channel-cell-text">{{ formatChannelLabels(row.channel) }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="采集模式" width="140">
                  <template slot-scope="{ row }">
                    {{ formatCollectModeLabel(row.collectMode) }}
                  </template>
                </el-table-column>
                <el-table-column label="预估帖子" min-width="220" show-overflow-tooltip>
                  <template slot-scope="{ row }">
                    {{ formatEstimateText(row.estimatedCount, row.estimatedAt, row.estimateError) }}
                  </template>
                </el-table-column>
                <el-table-column label="启用状态" width="120">
                  <template slot-scope="{ row }">
                    <el-tag :type="applyStatusType(row.applyStatus)">{{ applyStatusText(row.applyStatus) }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="申请人" width="100">
                  <template slot-scope="{ row }">
                    {{ formatUserName(row.applyUserId) }}
                  </template>
                </el-table-column>
                <el-table-column prop="createTime" label="申请时间" width="180" />
                <el-table-column label="操作" fixed="right" width="240" align="center">
                  <template slot-scope="{ row }">
                    <div class="table-action-group">
                      <el-button-group>
                    <el-button size="mini" @click="openApplyDetail(row)">查看</el-button>
                    <el-button
                      v-if="canEditApply(row)"
                      size="mini"
                      type="primary"
                      plain
                      @click="openEditApplyDialog(row)"
                    >编辑</el-button>
                    <el-button
                      v-if="canPauseApply(row)"
                      size="mini"
                      type="warning"
                      plain
                      @click="pauseApplyRow(row)"
                    >暂停</el-button>
                    <el-button
                      v-if="canResumeApply(row)"
                      size="mini"
                      type="success"
                      plain
                      @click="resumeApplyRow(row)"
                    >恢复</el-button>
                      </el-button-group>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
              <div v-if="applies.length > applyListPageSize" class="risk-card-pagination">
                <el-pagination
                  layout="prev, pager, next"
                  :total="applies.length"
                  :page-size="applyListPageSize"
                  :current-page.sync="applyListPage"
                  small
                  background
                />
              </div>
            </el-card>
        </section>

        <section v-if="activeSection === 'groupAdmin' && isGroupLeader" class="page-section">
          <GroupAdmin
            :auth-user="authUser"
            @applies-changed="refreshAppliesAndTasks"
          />
        </section>

      </el-main>
    </el-container>
    </el-container>

    <el-drawer
      :title="applyDialogTitle"
      :visible.sync="createVisible"
      direction="rtl"
      size="640px"
      custom-class="apply-drawer"
      :destroy-on-close="false"
      @closed="resetApplyDialog"
    >
      <div class="apply-drawer-body">
      <el-form ref="applyForm" :model="form" :rules="rules" label-width="110px">
        <el-form-item label="任务名称" prop="taskName">
          <el-input v-model="form.taskName" placeholder="请输入" />
        </el-form-item>
        <el-form-item label="渠道" prop="channelPaths">
          <el-cascader
            v-model="form.channelPaths"
            :options="channelOptions"
            :props="channelCascaderProps"
            collapse-tags
            clearable
            filterable
            placeholder="请选择渠道（可多选）"
            style="width: 100%"
          />
          <div v-if="selectedChannelLabels.length" class="channel-selected-hint">
            已选 {{ selectedChannelLabels.length }} 个：{{ selectedChannelLabelsText }}
          </div>
        </el-form-item>
        <el-form-item label="采集模式" prop="collectMode">
          <el-select v-model="form.collectMode" style="width: 100%" @change="mode => onCollectModeChange(mode, 'form')">
            <el-option v-for="item in collectModeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="采集周期" prop="dateRange">
          <el-date-picker
            :key="'apply-range-' + applyDateRangeKey"
            v-model="form.dateRange"
            type="datetimerange"
            range-separator="→"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            format="yyyy-MM-dd HH:mm:ss"
            :default-time="['00:00:00', '23:59:59']"
            :picker-options="applyDatePickerOptions"
            style="width: 100%"
          />
          <span class="hint"><i class="el-icon-info"></i> {{ applyDateRangeHint }}</span>
        </el-form-item>
        <el-form-item label="场景规则">
          <span class="hint"><i class="el-icon-info"></i> 输入关键词触发社媒采集，同组内的关键词关系为“或”，不同组间的关系为“且”</span>
        </el-form-item>
        <el-form-item label="关键词" prop="keywordGroups">
          <div class="keyword-editor">
            <div class="keyword-editor-toolbar">
              <el-button
                type="text"
                icon="el-icon-magic-stick"
                class="keyword-recommend-entry"
                @click="openKeywordRecommendDialog"
              >智能推荐</el-button>
              <span class="keyword-recommend-tip">输入监控对象，自动生成关键词组合方案</span>
            </div>
            <div v-for="(group, gi) in form.keywordGroups" :key="'kg-' + gi" class="keyword-group">
              <div v-if="form.keywordGroups.length > 1" class="keyword-group-head">
                <el-button
                  type="text"
                  class="keyword-group-remove"
                  @click="removeKeywordGroup(gi)"
                >删除该组</el-button>
              </div>
              <div class="keyword-tags">
                <el-tag
                  v-for="(word, wi) in group.words"
                  :key="'kw-' + gi + '-' + wi"
                  size="small"
                  closable
                  @close="removeKeyword(gi, wi)"
                >{{ word }}</el-tag>
                <el-input
                  v-if="group.inputVisible"
                  :ref="'kwInput' + gi"
                  v-model="group.inputValue"
                  size="mini"
                  class="keyword-tag-input"
                  @keyup.enter.native="confirmKeyword(gi)"
                  @blur="confirmKeyword(gi)"
                />
                <el-button
                  v-else
                  size="mini"
                  class="keyword-tag-add"
                  icon="el-icon-plus"
                  @click="showKeywordInput(gi)"
                >添加关键词</el-button>
              </div>
            </div>
            <el-button
              size="mini"
              type="primary"
              plain
              icon="el-icon-plus"
              class="keyword-group-new"
              @click="addKeywordGroup"
            >新增</el-button>
          </div>
        </el-form-item>
        <el-form-item label="排除词">
          <div class="keyword-tags">
            <el-tag
              v-for="(word, wi) in form.excludeWords"
              :key="'ex-' + wi"
              size="small"
              type="info"
              closable
              @close="removeExcludeWord(wi)"
            >{{ word }}</el-tag>
            <el-input
              v-if="excludeInputVisible"
              ref="excludeInput"
              v-model="excludeInputValue"
              size="mini"
              class="keyword-tag-input"
              @keyup.enter.native="confirmExcludeWord"
              @blur="confirmExcludeWord"
            />
            <el-button
              v-else
              size="mini"
              class="keyword-tag-add"
              icon="el-icon-plus"
              @click="showExcludeInput"
            >添加排除词</el-button>
          </div>
        </el-form-item>
        <el-form-item label="采集范围">
          <el-checkbox v-model="form.collectPosts" disabled>帖子（必选）</el-checkbox>
          <el-checkbox v-model="form.collectComments">帖子评论</el-checkbox>
          <el-checkbox v-model="form.collectInteractions">互动量（转发、评论、赞等）</el-checkbox>
        </el-form-item>
        <el-collapse class="apply-advanced-collapse">
          <el-collapse-item name="advanced">
            <template slot="title">
              <span class="apply-advanced-title"><i class="el-icon-warning-outline"></i> 更新机制配置后，会带来更多额度消耗</span>
            </template>
            <el-form-item label="主贴下评论更新">
              <span class="apply-update-text">采集后每日 0 点更新，更新频次持续</span>
              <el-select v-model="form.commentUpdateDays" size="small" class="days-select">
                <el-option v-for="day in days" :key="day" :label="day" :value="day" />
              </el-select>
              <span class="apply-update-text">天</span>
            </el-form-item>
            <el-form-item label="主贴互动量更新">
              <span class="apply-update-text">采集后每日 0 点更新，更新频次持续</span>
              <el-select v-model="form.interactionUpdateDays" size="small" class="days-select">
                <el-option v-for="day in days" :key="day" :label="day" :value="day" />
              </el-select>
              <span class="apply-update-text">天</span>
            </el-form-item>
          </el-collapse-item>
        </el-collapse>

        <div class="apply-section-divider">数据量控制</div>
        <el-form-item label="预估帖子数量">
          <el-button plain :loading="estimateLoading" @click="estimatePostCount">预估帖子数量</el-button>
          <span v-if="estimatedCount != null" class="apply-estimate-value">{{ formatCompact(estimatedCount) }} 条</span>
          <div v-if="estimatedAt" class="apply-estimate-meta">预估时间 {{ formatEstimateTime(estimatedAt) }}</div>
          <div v-if="estimateError" class="apply-estimate-error">{{ estimateError }}</div>
        </el-form-item>
        <el-form-item label="控量阻断限值">
          <el-input v-model="form.blockLimit" placeholder="请输入正整数" />
          <div class="hint">不填写默认全量采集，填写则按先后顺序统计达限值后阻断</div>
        </el-form-item>
      </el-form>
      </div>
      <div class="apply-drawer-footer">
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" @click="submitApply">确认</el-button>
      </div>
    </el-drawer>

    <el-dialog
      title="关键词智能推荐"
      :visible.sync="keywordRecommendVisible"
      width="640px"
      append-to-body
      custom-class="keyword-recommend-dialog"
      :close-on-click-modal="false"
      @closed="resetKeywordRecommendDialog"
    >
      <div class="keyword-recommend-body">
        <el-form label-width="90px" class="keyword-recommend-form">
          <el-form-item label="监控对象" required>
            <el-input
              v-model="keywordRecommendForm.object"
              placeholder="如：朱梓骁、快手蛋蛋"
              maxlength="50"
              clearable
              @keyup.enter.native="fetchKeywordRecommend"
            />
          </el-form-item>
          <el-form-item label="对象说明">
            <el-input
              v-model="keywordRecommendForm.hint"
              placeholder="选填，用于消除歧义，如：快手主播"
              maxlength="100"
              clearable
              @keyup.enter.native="fetchKeywordRecommend"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="keywordRecommendLoading" @click="fetchKeywordRecommend">
              {{ keywordRecommendSchemes.length ? '重新生成' : '生成推荐方案' }}
            </el-button>
          </el-form-item>
        </el-form>
        <keyword-recommend-schemes
          v-if="keywordRecommendSchemes.length"
          :schemes="keywordRecommendSchemes"
          :selected="keywordRecommendSelected"
          @update:selected="keywordRecommendSelected = $event"
        />
      </div>
      <div slot="footer" class="keyword-recommend-footer">
        <el-button @click="keywordRecommendVisible = false">取消</el-button>
        <el-button
          type="primary"
          :disabled="keywordRecommendSelected < 0 || keywordRecommendSelected >= keywordRecommendSchemes.length"
          @click="applyKeywordRecommendScheme"
        >回填到表单</el-button>
      </div>
    </el-dialog>

    <el-drawer title="账号画像" :visible.sync="kolProfileVisible" size="420px">
      <div v-if="kolProfile" class="kol-profile">
        <div class="kol-profile-head">
          <i class="mdi mdi-account-circle"></i>
          <div>
            <h3>{{ kolProfile.name }}</h3>
            <p>{{ kolProfile.platform }}　{{ kolVerified(kolProfile) }}</p>
          </div>
        </div>
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item label="账号等级">
            <el-tag size="mini" :type="kolTierType(kolProfile)">{{ kolTier(kolProfile) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="粉丝量">{{ formatCompact(kolProfile.fans) }}</el-descriptions-item>
          <el-descriptions-item label="历史发文">{{ kolProfile.posts }} 篇</el-descriptions-item>
          <el-descriptions-item label="累计互动">{{ formatCompact(kolProfile.interactions) }}</el-descriptions-item>
          <el-descriptions-item label="平均互动">{{ formatCompact(kolAvgInteraction(kolProfile)) }}</el-descriptions-item>
          <el-descriptions-item label="累计曝光">{{ formatCompact(kolProfile.exposure) }}</el-descriptions-item>
          <el-descriptions-item label="负面发文">{{ kolProfile.negativePosts }} 篇</el-descriptions-item>
          <el-descriptions-item label="情感倾向">
            <el-tag size="mini" :type="kolTendencyType(kolProfile.sentimentTendency)">{{ kolProfile.sentimentTendency }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="历史风险">
            <el-tag size="mini" :type="kolRiskType(kolProfile)">{{ kolRiskLevel(kolProfile) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="影响力评分">{{ kolProfile.influenceScore }} / 100</el-descriptions-item>
          <el-descriptions-item label="风险原因">{{ kolProfile.riskReason || '暂无风险原因' }}</el-descriptions-item>
          <el-descriptions-item label="建议动作">{{ kolProfile.suggestion || '持续关注' }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-drawer>

    <el-drawer
      :title="(pageHelp && pageHelp.title) || '字段说明'"
      :visible.sync="pageHelpVisible"
      size="480px"
      custom-class="page-help-drawer"
      append-to-body
    >
      <div v-if="pageHelp" class="page-help-body">
        <p class="page-help-intro">{{ pageHelp.intro }}</p>
        <div v-for="(group, gi) in pageHelp.groups" :key="'phg-' + gi" class="page-help-group">
          <h4>{{ group.title }}</h4>
          <div v-for="(item, ii) in group.items" :key="'phi-' + gi + '-' + ii" class="page-help-item">
            <div class="page-help-field">{{ item.field }}</div>
            <div class="page-help-meta">
              <span class="page-help-label">来源</span>
              <p>{{ item.source }}</p>
            </div>
            <div v-if="item.rule" class="page-help-meta">
              <span class="page-help-label">评判标准</span>
              <p>{{ item.rule }}</p>
            </div>
          </div>
        </div>
      </div>
    </el-drawer>

    <el-drawer title="申请详情" :visible.sync="applyDetailVisible" size="520px">
      <div v-if="selectedApply" class="detail">
        <h2>{{ selectedApply.taskName }}</h2>
        <el-descriptions :column="1" border>
          <el-descriptions-item label="申请编号">{{ selectedApply.applyNo }}</el-descriptions-item>
          <el-descriptions-item label="渠道">{{ formatChannelLabels(selectedApply.channel) }}</el-descriptions-item>
          <el-descriptions-item label="采集模式">{{ formatCollectModeLabel(selectedApply.collectMode) }}</el-descriptions-item>
          <el-descriptions-item label="采集周期">
            {{ formatApplyDateRange(selectedApply) }}
          </el-descriptions-item>
          <el-descriptions-item label="预估帖子">
            {{ formatEstimateText(selectedApply.estimatedCount, selectedApply.estimatedAt, selectedApply.estimateError) }}
          </el-descriptions-item>
          <el-descriptions-item label="关键词">
            <div v-if="applyKeywordGroups.length" class="detail-keyword-groups">
              <div v-for="(group, gi) in applyKeywordGroups" :key="'dk-' + gi" class="detail-keyword-group">
                <span class="detail-keyword-group-name">组{{ gi + 1 }}</span>
                <el-tag v-for="(word, wi) in group" :key="'dkw-' + gi + '-' + wi" size="mini">{{ word }}</el-tag>
              </div>
            </div>
            <span v-else>—</span>
          </el-descriptions-item>
          <el-descriptions-item label="排除词">
            <template v-if="applyExcludeWords.length">
              <el-tag v-for="(word, wi) in applyExcludeWords" :key="'dex-' + wi" size="mini" type="info" class="detail-exclude-tag">{{ word }}</el-tag>
            </template>
            <span v-else>—</span>
          </el-descriptions-item>
          <el-descriptions-item label="启用状态">
            <el-tag :type="applyStatusType(selectedApply.applyStatus)">
              {{ applyStatusText(selectedApply.applyStatus) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="申请人">{{ formatUserName(selectedApply.applyUserId) }}</el-descriptions-item>
          <el-descriptions-item label="申请时间">{{ selectedApply.createTime }}</el-descriptions-item>
          <el-descriptions-item v-if="selectedApply.reviewRemark" label="启用意见">{{ selectedApply.reviewRemark }}</el-descriptions-item>
          <el-descriptions-item v-if="linkedTaskForApply(selectedApply)" label="统计任务">
            {{ linkedTaskForApply(selectedApply).taskNo }}（第三方任务：{{ externalTaskText(linkedTaskForApply(selectedApply)) }}）
          </el-descriptions-item>
          <el-descriptions-item v-if="linkedTaskForApply(selectedApply)" label="正式任务预估">
            {{ formatEstimateText(linkedTaskForApply(selectedApply).estimatedCount, linkedTaskForApply(selectedApply).estimatedAt, linkedTaskForApply(selectedApply).estimateError) }}
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </el-drawer>

    <el-drawer title="风险详情" :visible.sync="riskDetailVisible" size="46%">
      <div v-if="riskDetail" class="detail risk-detail">
        <div class="risk-detail-header">
          <div class="risk-score-ring large" :style="{ '--score': riskDetail.score }">
            <strong>{{ riskDetail.score }}</strong>
          </div>
          <div>
            <el-tag :type="riskLevelType(riskDetail.level)">{{ riskLevelLabel(riskDetail.level) }}</el-tag>
            <h2>{{ riskDetail.title }}</h2>
            <p class="risk-reason"><i class="mdi mdi-alert-circle-outline"></i> {{ riskDetail.reason }}</p>
          </div>
        </div>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="平台">{{ riskDetail.platform }}</el-descriptions-item>
          <el-descriptions-item label="作者">{{ riskDetail.authorName }}</el-descriptions-item>
          <el-descriptions-item label="发布时间">{{ riskDetail.publishTime }}</el-descriptions-item>
          <el-descriptions-item label="情感倾向">{{ riskDetail.sentiment }}</el-descriptions-item>
          <el-descriptions-item label="曝光量">{{ formatCompact(riskDetail.exposure) }}</el-descriptions-item>
          <el-descriptions-item label="互动量">{{ formatCompact(riskDetail.interaction) }}</el-descriptions-item>
          <el-descriptions-item label="作者粉丝">{{ formatCompact(riskDetail.fans) }}</el-descriptions-item>
          <el-descriptions-item label="原文链接">
            <el-link v-if="riskDetail.url" :href="riskDetail.url" target="_blank" type="primary">查看原文</el-link>
            <span v-else>—</span>
          </el-descriptions-item>
        </el-descriptions>
        <div class="risk-detail-content">
          <strong>原文内容</strong>
          <p>{{ riskDetail.content || '暂无原文内容' }}</p>
        </div>
        <div class="risk-detail-factors">
          <span v-for="f in riskFactors(riskDetail)" :key="f" class="risk-factor-chip">{{ f }}</span>
        </div>
        <div class="risk-detail-suggestion">
          <strong>处置建议</strong>
          <p>{{ riskDetail.suggestion || '建议持续观察传播变化。' }}</p>
        </div>
        <div v-if="activeSection !== 'risk'" class="risk-detail-actions">
          <el-button @click="goToRiskCenter">前往风险中心</el-button>
        </div>
      </div>
    </el-drawer>

    <el-drawer title="舆情详情" :visible.sync="detailVisible" size="50%">
      <div v-if="detail" class="detail">
        <h2>{{ detail.title }}</h2>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="平台">{{ detail.platform }}</el-descriptions-item>
          <el-descriptions-item label="作者">{{ detail.authorName }}</el-descriptions-item>
          <el-descriptions-item label="发布时间">{{ detail.publishTime }}</el-descriptions-item>
          <el-descriptions-item label="情感倾向">{{ detail.sentiment }}</el-descriptions-item>
          <el-descriptions-item label="风险等级">{{ detail.riskLevel }}</el-descriptions-item>
          <el-descriptions-item label="原文链接">
            <el-link :href="detail.sourceUrl" target="_blank" type="primary">查看原文</el-link>
          </el-descriptions-item>
        </el-descriptions>
        <el-card class="detail-card">
          <h3>正文内容</h3>
          <p>{{ detail.content }}</p>
          <el-tag size="mini" type="warning">{{ detail.matchedKeywordsJson }}</el-tag>
        </el-card>
        <el-row :gutter="12">
          <el-col :span="6"><el-card>阅读 {{ detail.readCount || 0 }}</el-card></el-col>
          <el-col :span="6"><el-card>评论 {{ detail.commentCount || 0 }}</el-card></el-col>
          <el-col :span="6"><el-card>转发 {{ detail.repostCount || 0 }}</el-card></el-col>
          <el-col :span="6"><el-card>点赞 {{ detail.likeCount || 0 }}</el-card></el-col>
        </el-row>
      </div>
    </el-drawer>

    <task-apply-agent-drawer
      :visible.sync="applyAssistVisible"
      @adopt="openCreateDialogWithDraft"
    />
  </div>
</template>

<script>
import * as echarts from 'echarts/core'
import { BarChart, EffectScatterChart, LineChart, MapChart, PieChart, ScatterChart } from 'echarts/charts'
import { GeoComponent, GridComponent, LegendComponent, TooltipComponent, VisualMapComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import chinaMapJson from 'china-geojson/src/geojson/china.json'
import {
  createReportJob,
  createApply,
  estimateApply,
  getDataRange,
  getBrandAnalysis,
  getEvents,
  getKol,
  getMe,
  getMonitor,
  getOverview,
  getRisk,
  getTaskSummary,
  getResultDetail,
  getReportJob,
  listApplies,
  listReportJobs,
  listResults,
  listTasks,
  logout,
  pauseApply,
  pauseGroupApply,
  recommendKeywordSchemes,
  resumeApply,
  resumeGroupApply,
  setUnauthorizedHandler,
  updateApply
} from './api'
import Login from './Login.vue'
import GroupAdmin from './GroupAdmin.vue'
import KeywordRecommendSchemes from './KeywordRecommendSchemes.vue'
import TaskApplyAgentDrawer from './TaskApplyAgentDrawer.vue'
import { mapDraftToForm } from './taskApplyDraft'
import {
  buildUserNameMap,
  clearAuthSession,
  isGroupLeader as checkGroupLeader,
  loadAuthSession,
  saveAuthSession
} from './auth'
import {
  CHANNEL_CASCADER_PROPS,
  CHANNEL_OPTIONS,
  channelPathsToEntries,
  entriesToChannelPaths,
  formatChannelLabels as formatChannelDisplay,
  parseChannelEntries
} from './channelOptions'
import {
  COLLECT_MODE_HISTORY,
  COLLECT_MODE_OPTIONS,
  COLLECT_RANGE_MAX_MONTHS,
  buildCollectModePickerOptions,
  buildDateTimeRange,
  defaultRealtimeDateRange,
  formatCollectModeLabel as getCollectModeLabel,
  formatEstimateText,
  isRealtimeCollectMode,
  normalizeCollectModeValue,
  normalizeDateTimeForPicker,
  toApiDateTime,
  validateCollectDateRange
} from './taskFormUtils'

const SINGLE_NON_EMPTY_GROUP_MAX_WORDS = 10
const SINGLE_GROUP_TOO_WIDE_MESSAGE = '同组是「或」，超过 10 个会把采集面拉得太宽，请再开一组（组间为「且」）'

/** 与后端 parseKeywordGroups 一致：trim 后非空才算词，空组不计入 */
function nonEmptyKeywordWords(group) {
  if (!group || !Array.isArray(group.words)) {
    return []
  }
  return group.words.map(word => String(word || '').trim()).filter(Boolean)
}

function isSingleNonEmptyGroupTooWide(groups) {
  const nonEmpty = (groups || []).map(nonEmptyKeywordWords).filter(words => words.length > 0)
  return nonEmpty.length === 1 && nonEmpty[0].length > SINGLE_NON_EMPTY_GROUP_MAX_WORDS
}

const ROUTE_SECTIONS = [
  'dashboard',
  'analysis',
  'risk',
  'keyword',
  'report',
  'workOrder',
  'groupAdmin',
  'bigScreen'
]

echarts.use([
  BarChart,
  EffectScatterChart,
  LineChart,
  MapChart,
  PieChart,
  ScatterChart,
  GeoComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  VisualMapComponent,
  CanvasRenderer
])
echarts.registerMap('china', chinaMapJson)

export default {
  name: 'App',
  components: { Login, GroupAdmin, KeywordRecommendSchemes, TaskApplyAgentDrawer },
  data() {
    return {
      activeSection: 'dashboard',
      bigScreenEnabled: true,
      channelOptions: CHANNEL_OPTIONS,
      channelCascaderProps: CHANNEL_CASCADER_PROPS,
      applies: [],
      // 任务申请列表客户端分页（固定每页 10 条）
      applyListPage: 1,
      applyListPageSize: 10,
      tasks: [],
      appliesOffline: false,
      tasksOffline: false,
      authUser: null,
      authReady: false,
      results: [],
      resultsTotal: 0,
      resultsPageNo: 1,
      resultsPageSize: 20,
      currentTask: null,
      selectedApply: null,
      createVisible: false,
      applyAssistVisible: false,
      editingApplyId: null,
      taskSummaryMap: {},
      currentTaskSummary: { totalCount: 0, negativeCount: 0, positiveCount: 0, neutralCount: 0, platforms: [] },
      applyDetailVisible: false,
      detailVisible: false,
      detail: null,
      riskDetailVisible: false,
      riskDetail: null,
      overview: null,
      riskData: null,
      riskLoading: false,
      riskPageLoading: false,
      riskKeyword: '',
      riskTaskId: null,
      riskStatDays: 7,
      riskStatStartDate: null,
      riskStatEndDate: null,
      riskListLevel: '',
      riskListPlatform: '',
      riskListSearch: '',
      riskListPage: 1,
      riskListPageSize: 10,
      // 三张风险摘要卡客户端分页（固定每页 5 条）
      riskCardPage: 1,
      hotspotCardPage: 1,
      kolRiskCardPage: 1,
      riskCardPageSize: 5,
      riskRangeOptions: [
        { label: '24小时', value: 1 },
        { label: '近3天', value: 3 },
        { label: '近7天', value: 7 },
        { label: '近15天', value: 15 },
        { label: '近30天', value: 30 }
      ],
      kolData: null,
      kolLoading: false,
      eventData: null,
      eventLoading: false,
      eventPageLoading: false,
      eventStatDays: 7,
      eventStatStartDate: null,
      eventStatEndDate: null,
      eventRangeOptions: [
        { label: '24小时', value: 1 },
        { label: '近3天', value: 3 },
        { label: '近7天', value: 7 },
        { label: '近15天', value: 15 },
        { label: '近30天', value: 30 }
      ],
      activeEventId: null,
      brandLoading: false,
      activeBrandName: null,
      activeBrandData: null,
      keywordTaskId: null,
      analysisTrendGranularity: 'day',
      reportType: 'daily',
      reportTaskId: null,
      reportGenerating: false,
      reportJobId: null,
      reportJobStatusText: '',
      reportPollTimer: null,
      reportHistory: [],
      reportHistoryLoading: false,
      report: null,
      pageHelpVisible: false,
      kolProfileVisible: false,
      kolProfile: null,
      monitorQuery: { keyword: '', platform: '', sentiment: '', author: '', dateRange: null },
      monitorTab: 'all',
      monitorData: null,
      monitorLoading: false,
      monitorPageLoading: false,
      monitorPageNo: 1,
      monitorPageSize: 20,
      monitorStatDays: 7,
      monitorStatStartDate: null,
      monitorStatEndDate: null,
      monitorTabs: [
        { key: 'all', label: '全部信息' },
        { key: 'warning', label: '预警信息' },
        { key: 'marked', label: '重点信息' },
        { key: 'favorite', label: '已收藏' }
      ],
      favoriteIds: [],
      markedIds: [],
      overviewDays: 7,
      overviewRange: null,
      overviewStartDateTime: null,
      overviewEndDateTime: null,
      overviewLoading: false,
      overviewRequestSeq: 0,
      overviewPollTimer: null,
      overviewLastRequestParams: null,
      overviewLoadMessage: '',
      overviewDataRange: null,
      overviewRangeOptions: [
        { label: '24小时', value: 1 },
        { label: '近3天', value: 3 },
        { label: '近7天', value: 7 },
        { label: '近15天', value: 15 },
        { label: '近30天', value: 30 }
      ],
      chartInstances: {},
      screenTaskId: null,
      screenUpdatedAt: '',
      collectModeOptions: COLLECT_MODE_OPTIONS,
      applyDateRangeKey: 0,
      applyDateAnchor: null,
      days: [0, 1, 3, 7, 15, 30],
      form: {
        taskName: '',
        channelPaths: [],
        collectMode: COLLECT_MODE_HISTORY,
        dateRange: [],
        keywordGroups: [{ words: [], inputVisible: false, inputValue: '' }],
        excludeWords: [],
        collectPosts: true,
        collectComments: false,
        collectInteractions: false,
        commentUpdateDays: 0,
        interactionUpdateDays: 0,
        applyReason: '',
        blockLimit: ''
      },
      estimatedCount: null,
      estimatedAt: null,
      estimateError: '',
      estimateLoading: false,
      excludeInputVisible: false,
      excludeInputValue: '',
      keywordRecommendVisible: false,
      keywordRecommendLoading: false,
      keywordRecommendForm: { object: '', hint: '' },
      keywordRecommendSchemes: [],
      keywordRecommendSelected: -1,
      rules: {
        taskName: [{ required: true, message: '请输入任务名称', trigger: 'blur' }],
        channelPaths: [{ required: true, type: 'array', min: 1, message: '请至少选择一个渠道', trigger: 'change' }],
        collectMode: [{ required: true, message: '请选择采集模式', trigger: 'change' }],
        dateRange: [
          { required: true, message: '请选择采集周期', trigger: 'change' },
          {
            validator: (rule, value, callback) => {
              const message = validateCollectDateRange(this.form.collectMode, value)
              if (message) {
                callback(new Error(message))
                return
              }
              callback()
            },
            trigger: 'change'
          }
        ],
        keywordGroups: [{ validator: (rule, value, callback) => {
          const hasWord = Array.isArray(value) && value.some(group => nonEmptyKeywordWords(group).length > 0)
          if (!hasWord) {
            callback(new Error('请至少添加一个关键词'))
            return
          }
          if (isSingleNonEmptyGroupTooWide(value)) {
            callback(new Error(SINGLE_GROUP_TOO_WIDE_MESSAGE))
            return
          }
          callback()
        }, trigger: 'change' }]
      }
    }
  },
  computed: {
    isGroupLeader() {
      return checkGroupLeader(this.authUser)
    },
    userNameMap() {
      return buildUserNameMap(this.authUser)
    },
    pageTitle() {
      return {
        dashboard: '舆情态势总览',
        analysis: '实时监测',
        risk: '风险预警中心',
        keyword: '关键词分析',
        report: '舆情报告',
        workOrder: '检测任务',
        groupAdmin: '成员管理',
        bigScreen: '舆情监测大屏'
      }[this.activeSection]
    },
    pageHelp() {
      const guides = {
        dashboard: {
          title: '舆情概览 · 字段说明',
          intro: '本页展示您有权限查看的任务，在所选时间范围内的整体舆情态势，方便快速掌握“今天发生了什么、有没有风险”。',
          groups: [
            {
              title: '核心指标',
              items: [
                { field: '今日舆情 / 总量', source: '所选时间范围内采集到的全部相关内容。', rule: '按条数统计，覆盖您可见任务下的全部数据，不是抽样。' },
                { field: '负面数量', source: '系统判定为负面情绪的内容。', rule: '判定标准：情绪为「负面」或「负向」即计入；负面占比 = 负面条数 ÷ 总条数。' },
                { field: '风险事件', source: '负面内容中风险较高的条目。', rule: '判定标准：风险分 ≥55（即 P3 及以上）计入；风险分计算见「风险预警」页说明。' },
                { field: '高影响账号', source: '粉丝量很大的发声账号。', rule: '判定标准：粉丝数 ≥100 万的账号计为高影响账号（去重统计）。' },
                { field: '今日曝光', source: '内容的潜在触达规模。', rule: '计算规则：因数据源未提供阅读/播放量，采用发帖账号粉丝数(fans)作为潜在曝光代理值，按内容累加。' },
                { field: '今日传播', source: '内容被二次扩散的次数。', rule: '计算规则：转发量 + 分享量。' }
              ]
            },
            {
              title: '图表与列表',
              items: [
                { field: '情感占比', source: '正面、中性、负面内容的比例。', rule: '计算规则：各类条数 ÷ 总条数；暂无数据时环形图默认显示正面色。' },
                { field: '今日重点', source: '根据总量、负面情况和主要平台自动汇总的一句话。', rule: '汇总口径：总条数、负面占比、主要平台、高影响账号数等，用于快速扫读。' },
                { field: '最新预警', source: '按风险高低排序的最新风险内容。', rule: '排序规则：风险分从高到低，优先展示更紧急、传播更广的内容。' },
                { field: '方案命中主体词', source: '任务关键词被命中的次数排名。', rule: '计算规则：按关键词命中次数降序；次数越高，该主题越热。' }
              ]
            }
          ]
        },
        analysis: {
          title: '实时监测 · 字段说明',
          intro: '本页像“舆情流水线”，可按平台、关键词、账号、情感、时间筛选，实时查看每条内容的详情与互动情况。',
          groups: [
            {
              title: '筛选条件',
              items: [
                { field: '平台', source: '内容发布所在的渠道，如微博、抖音等。', rule: '选中后只看该平台内容。' },
                { field: '关键词', source: '标题或正文中出现的检索词。', rule: '输入后筛选包含该词的内容。' },
                { field: '账号', source: '发帖作者名称。', rule: '可按作者名模糊查找。' },
                { field: '情感', source: '系统对内容情绪的判断。', rule: '可选正面、中性、负面。' },
                { field: '发布时间', source: '内容实际发布的时间。', rule: '按您选择的起止日期过滤。' }
              ]
            },
            {
              title: '内容卡片字段',
              items: [
                { field: '标题 / 正文 / 原文链接', source: '平台上的原始标题、正文和跳转链接。', rule: '可直接阅读摘要，或点链接查看原文。' },
                { field: '阅读 / 评论 / 转发 / 点赞等', source: '各平台公开的互动数据。', rule: '用来判断这条内容的热度和讨论强度。' },
                { field: '风险标签', source: '系统对该条内容的风险提示。', rule: '分为高、中、低，便于快速识别需关注的内容。' },
                { field: '收藏 / 标记', source: '您在本系统中的个人操作。', rule: '方便稍后回看或重点跟进，不影响原始数据。' }
              ]
            }
          ]
        },
        events: {
          title: '事件中心 · 字段说明',
          intro: '系统会把讨论同一主题的内容归并成“事件”，帮您从“一条条帖子”升级到“一件件事”来看舆情走势。',
          groups: [
            {
              title: '事件识别',
              items: [
                { field: '事件名称', source: '同一主题下反复出现的关键词。', rule: '归并规则：相同关键词归为一个事件；列表按热度从高到低展示。' },
                { field: '相关文章数', source: '该事件下关联到的内容条数。', rule: '计算规则：该主题下内容总条数。' },
                { field: '负面数量', source: '该事件中偏负面的内容条数。', rule: '判定标准：情绪为负面/负向的条数。' }
              ]
            },
            {
              title: '热度与阶段',
              items: [
                {
                  field: '热度',
                  source: '综合文章量、曝光和互动得出的热度分（0–100）。',
                  rule: '计算规则（满分 100）：基础分 35；相关文章 ≥10 条 +25、≥5 条 +15、≥2 条 +8；曝光 ≥10 万 +20、≥1 万 +10；互动 ≥1 万 +15、≥1000 +8。'
                },
                {
                  field: '生命周期阶段',
                  source: '根据近几日讨论量变化判断所处阶段。',
                  rule: '判定规则：无数据或总量很少→萌芽；最新日为 0→结束；最新日创新高且较前一日增长超 50%、且当日≥10 条→爆发；仍在上升且接近峰值→扩散，否则发酵；较前一日下降→衰减。'
                },
                {
                  field: '主导情感',
                  source: '该事件整体情绪倾向。',
                  rule: '判定规则：负面占比 ≥40%→负面；≥15%→中性偏负；否则→中性偏正。负面占比 = 负面条数 ÷ 相关文章数。'
                },
                { field: '事件摘要', source: '系统根据规模、负面情况和阶段自动整理的说明。', rule: '摘要会写明关联篇数、负面占比、累计曝光和当前阶段，并给出跟进建议。' }
              ]
            },
            {
              title: '详情面板',
              items: [
                { field: '传播趋势', source: '该事件每天新增讨论量。', rule: '计算规则：按天统计该主题新增条数；上升表示升温，回落表示降温。' },
                { field: '传播平台', source: '事件在各平台的分布。', rule: '计算规则：各平台条数 ÷ 事件总条数。' },
                { field: '观点聚类', source: '正面、中性、负面观点的大致占比。', rule: '计算规则：按情绪三类分别计数后算占比。' },
                { field: '首发账号', source: '该事件中最早发声的账号。', rule: '判定规则：取该事件中发布时间最早的一条内容对应账号。' },
                { field: '代表内容', source: '该事件中传播较广的典型帖文。', rule: '选取规则：优先取曝光较高的若干条样本。' }
              ]
            }
          ]
        },
        risk: {
          title: '风险预警 · 字段说明',
          intro: '本页聚焦可能带来口碑或传播风险的内容，并给出可解释的风险等级与处置建议，方便优先处理。',
          groups: [
            {
              title: '风险评分与等级',
              items: [
                {
                  field: '风险分（单条负面内容）',
                  source: '在负面内容基础上，结合曝光、互动和作者粉丝量综合打分。',
                  rule: '计算规则（满分 100）：基础分 55；曝光 ≥10 万 +20、≥1 万 +10；互动 ≥1 万 +15、≥1000 +8；作者粉丝 ≥100 万 +10、≥10 万 +5。'
                },
                {
                  field: '风险等级 P1–P4',
                  source: '由风险分换算成的处置优先级。',
                  rule: '判定规则：风险分 ≥85→P1（最高）；≥70→P2；≥55→P3；其余→P4（观察）。'
                },
                {
                  field: '评分因子',
                  source: '系统列出“为什么给这个分”的依据标签。',
                  rule: '命中规则示例：曝光≥10万/≥1万、互动≥1万/≥1000、作者粉丝≥100万/≥10万时，会显示对应因子。'
                },
                {
                  field: '处置建议',
                  source: '按风险等级给出的行动建议。',
                  rule: '判定规则：P1/P2→建议立即建工单并人工研判；P3→重点观察；P4→常规监测。'
                }
              ]
            },
            {
              title: '风险类型',
              items: [
                {
                  field: '负面情感风险',
                  source: '情绪偏负面的单条内容。',
                  rule: '纳入规则：情绪为负面/负向即进入列表；再按上方「风险分」规则打分排序。'
                },
                {
                  field: '热点聚集风险',
                  source: '同一话题短时间内负面内容扎堆出现。',
                  rule: '计算规则（满分 100）：基础分 35；聚集条数 ≥10 +25、≥5 +15、≥2 +8；曝光 ≥10 万 +20、≥1 万 +10；互动 ≥1 万 +15、≥1000 +8。等级仍按 ≥85/70/55 映射 P1–P4。'
                },
                {
                  field: 'KOL 参与风险',
                  source: '大粉丝量账号参与负面讨论。',
                  rule: '计算规则（满分 100）：基础分 45；粉丝 ≥100 万 +20、≥50 万 +15、≥10 万 +10；负面发文 ≥5 条 +15、≥2 条 +8；曝光 ≥100 万 +12、≥10 万 +8；互动 ≥10 万 +10、≥1 万 +5。等级同样按 ≥85/70/55 映射 P1–P4。'
                }
              ]
            }
          ]
        },
        dataAnalysis: {
          title: '数据分析 · 字段说明',
          intro: '本页从多个角度拆解舆情结构，适合做周报、月报和专题汇报的数据支撑。',
          groups: [
            {
              title: '分析维度',
              items: [
                { field: '趋势分析', source: '讨论量随时间的变化。', rule: '可按日、周、月查看，判断是升温还是回落。' },
                { field: '平台分析', source: '各社交/资讯平台上的内容占比。', rule: '帮助判断主要舆论场在哪里。' },
                { field: '情感分析', source: '正面、中性、负面的整体结构。', rule: '用于评估口碑健康度。' },
                { field: '热点关键词', source: '当前讨论最多的主题词。', rule: '排名越靠前，说明该话题越受关注。' },
                { field: '地域分析', source: '内容发布者所在地区分布。', rule: '可发现是否存在区域性集中讨论。' }
              ]
            }
          ]
        },
        brand: {
          title: '品牌分析 · 字段说明',
          intro: '从品牌视角看声量、口碑和健康度，适合品牌公关与市场团队日常盯盘。',
          groups: [
            {
              title: '品牌指标',
              items: [
                { field: '声量', source: '该品牌相关内容的讨论总量。', rule: '计算规则：统计区间内相关内容总条数。' },
                { field: '情感分布', source: '该品牌相关内容的正负面结构。', rule: '计算规则：正面 / 中性 / 负面各自条数及占比。' },
                { field: '曝光 / 互动', source: '品牌相关内容的潜在触达与被互动规模。', rule: '计算规则：曝光 = 发帖账号粉丝数(fans)累加（潜在触达代理值，因源数据无阅读/播放量）；互动 = 评论+转发+点赞等合计。' },
                {
                  field: '健康指数',
                  source: '综合情感结构、负面占比和传播热度给出的品牌健康分（0–100）。',
                  rule: '计算规则：情感分（正面按 100、中性 60、负面 20 加权平均）占 65%；「1−负面占比」再乘 25 分；热度分（由曝光+互动换算，上限 100）占 10%。等级：≥80 优秀、≥60 良好、≥40 一般、否则预警。'
                },
                { field: '声量趋势', source: '品牌讨论量的每日变化。', rule: '计算规则：按天统计该品牌相关条数。' }
              ]
            }
          ]
        },
        kol: {
          title: 'KOL 分析 · 字段说明',
          intro: '帮助识别关键意见领袖：谁在发声、影响力多大、情绪偏正还是偏负、要不要重点盯防。',
          groups: [
            {
              title: '账号指标',
              items: [
                {
                  field: '账号等级',
                  source: '按粉丝规模划分的账号层级。',
                  rule: '判定规则：粉丝 ≥100 万→头部；≥10 万→腰部；≥1 万→肩部；其余→尾部。'
                },
                { field: '粉丝数', source: '账号在平台上的粉丝规模。', rule: '直接取账号粉丝数；粉丝越多，单次发声影响通常越大。' },
                {
                  field: '发文 / 负面发文',
                  source: '该账号相关发文总量及其中负面条数。',
                  rule: '计算规则：发文数 = 相关内容条数；负面发文 = 其中情绪为负面的条数；负面占比 = 负面发文 ÷ 发文数。'
                },
                {
                  field: '平均互动',
                  source: '平均每条内容获得的评论、转发、点赞等。',
                  rule: '计算规则：互动总量 ÷ 发文数（发文为 0 时按 0）。'
                },
                {
                  field: '影响力评分',
                  source: '综合粉丝、互动和曝光给出的影响力分（0–100）。',
                  rule: '计算规则：对粉丝、互动、曝光分别取对数后加权相加（粉丝权重最高，其次互动，再次曝光），结果限制在 0–100。'
                },
                {
                  field: '情感倾向',
                  source: '该账号近期内容的整体情绪。',
                  rule: '判定规则：负面占比 ≥50%→偏负面；≥20%→中性偏负；否则→中性偏正。'
                },
                {
                  field: '风险等级',
                  source: '结合影响力与负面发文比例的风险提示。',
                  rule: '判定规则：影响力 ≥80 且负面占比 ≥30%→高；影响力 ≥60 或负面占比 ≥20%→中；否则→低。'
                }
              ]
            }
          ]
        },
        bigScreen: {
          title: '舆情大屏 · 字段说明',
          intro: '面向指挥中心或汇报场景的可视化大屏。左上角可按正式任务筛选，展示与该任务（或全部可见任务）一致的真实监测结果，无假数据。',
          groups: [
            {
              title: '展示模块',
              items: [
                { field: '任务筛选', source: '当前账号有权限查看的正式任务列表。', rule: '可选「全部可见任务」或某一个任务；切换后大屏指标与图表同步刷新。' },
                { field: '核心指标', source: '与舆情概览一致的总量、情感、风险等关键数字。', rule: '随所选任务和时间范围同步变化。' },
                { field: '地图点位', source: '各地区讨论量在地图上的分布。', rule: '有地域数据时显示；点越大通常表示该地区讨论越多。' },
                { field: '热点事件排名', source: '当前最热的主题排序。', rule: '排名越靠前，说明该主题越受关注。' },
                { field: '发布者互动', source: '统计区间内全部可见内容按发布账号汇总的点赞+评论+转发；源数据为 0 时按发文条数计。', rule: '按互动量取前 5 名展示。' },
                { field: '最新舆情 / 滚动播报', source: '最新出现的舆情标题。', rule: '用于大屏滚动提醒，方便现场快速扫一眼。' }
              ]
            }
          ]
        },
        report: {
          title: '舆情报告 · 字段说明',
          intro: '一键汇总当前监测与分析结果，生成结构化报告，减少手工写周报、月报的时间。',
          groups: [
            {
              title: '报告内容',
              items: [
                { field: '日报 / 周报 / 月报', source: '对应时间范围内的概览、热点、事件、风险、品牌等结果。', rule: '按报告类型自动组织章节，适合直接汇报或二次编辑。' }
              ]
            }
          ]
        },
        aiCapability: {
          title: '分析能力中心 · 字段说明',
          intro: '集中展示系统当前在跑的几类分析能力及其运行摘要，方便了解“系统帮您自动看了哪些面”。',
          groups: [
            {
              title: '分析模块',
              items: [
                { field: '实时监测分析', source: '监测总量、覆盖平台和最新样本情况。', rule: '声量升降判定：对比统计窗口第一天与最后一天的内容量，后者不低于前者视为持平或上升，否则视为回落。' },
                { field: '风险评分分析', source: '负面内容、热点聚集和大 V 风险概况。', rule: '展示口径：负面条数、热点聚集数、KOL 风险数；并列出最高风险项及其 P1–P4 等级分布。' },
                { field: '关键账号识别', source: '高影响力账号及其情绪倾向。', rule: '高影响判定：影响力评分 ≥80 计为高影响；同时统计情感倾向为「偏负面」的账号数量。' },
                { field: '运行记录', source: '每次刷新留下的分析运行摘要。', rule: '方便回溯“什么时候跑过、结论是什么”。' }
              ]
            }
          ]
        },
        workOrder: {
          title: '检测任务 · 字段说明',
          intro: '集中管理监测任务：提交任务申请后进入待启用，启用后生成正式采集任务。',
          groups: [
            {
              title: '任务申请字段',
              items: [
                { field: '渠道', source: '您希望监测的平台范围。', rule: '启用后，系统按这些渠道采集相关内容。' },
                { field: '采集模式', source: '采集历史数据，或采集实时数据。', rule: '采集实时数据默认看近 7 天，也可自行调整起止时间。' },
                { field: '关键词', source: '触发采集的主题词，可分组添加。', rule: '匹配规则：同一组内任一关键词命中即可（或关系）；不同组之间需同时满足（且关系），用于更精确圈定话题。' },
                { field: '排除词', source: '不希望纳入结果的干扰词。', rule: '过滤规则：内容命中任一排除词即不纳入结果。' },
                { field: '预估帖子', source: '瓴羊官方预估合计条数。', rule: '保存申请时若尚未成功预估会由后端再拉一次；失败仍可保存，此时只显示失败原因和预估时间。' },
                { field: '启用状态', source: '管理员对申请的启用处理结果。', rule: '状态说明：待启用 / 启用中 / 暂停 / 已停用；暂停后仍可查看已入库数据，已停用不能看数。' }
              ]
            }
          ]
        },
        tasks: {
          title: '正式任务 · 字段说明',
          intro: '申请启用后形成正式监测任务，并记录关联的第三方任务 ID。',
          groups: [
            {
              title: '关联信息',
              items: [
                { field: '第三方任务', source: '启用时录入的第三方采集任务 ID。', rule: '用于到“舆情结果”页实时查询第三方任务数据。' },
                { field: '预估帖子', source: '启用时从申请复制的瓴羊预估，之后与申请独立。', rule: '租户只可查看条数、时间和失败原因，不能重拉。' },
                { field: '创建时间', source: '正式任务创建时间。', rule: '仅表示关联记录生成时间，不代表后台同步进度。' }
              ]
            }
          ]
        },
        results: {
          title: '舆情结果 · 字段说明',
          intro: '按任务查看采集到的全部结果明细，并给出该任务的情感、平台等全量统计。',
          groups: [
            {
              title: '列表与统计',
              items: [
                { field: '任务选择', source: '您有权限查看的正式任务。', rule: '切换任务后，列表和统计会同步切换。' },
                { field: '结果列表', source: '该任务下的舆情内容明细。', rule: '支持分页浏览，可查看全部结果，不人为限制总条数。' },
                { field: '情感 / 平台统计', source: '该任务全部结果的汇总，不只是当前页。', rule: '用于判断整体口碑和主要传播平台。' },
                { field: '发布时间', source: '内容在平台上的发布时间。', rule: '便于按时间顺序阅读和研判。' },
                { field: '原文内容', source: '内容正文。', rule: '详情中可完整查看，便于核实与引用。' }
              ]
            }
          ]
        }
      }
      return guides[this.activeSection] || {
        title: '字段说明',
        intro: '当前页面暂无单独说明。',
        groups: []
      }
    },
    pendingApplyCount() {
      return this.applies.filter(item => item.applyStatus === 'PENDING_REVIEW').length
    },
    selectedApplyLinkedTask() {
      if (!this.selectedApply) return null
      return this.linkedTaskForApply(this.selectedApply)
    },
    applyKeywordGroups() {
      if (!this.selectedApply) return []
      return this.keywordGroupsFromJson(this.selectedApply.keywordsJson)
    },
    applyExcludeWords() {
      if (!this.selectedApply) return []
      return this.parseExcludeWords(this.selectedApply.excludeWordsJson)
    },
    selectedChannelLabels() {
      // 用带形态后缀的 name，避免同一媒体的网站与 APP 被当成一个
      return channelPathsToEntries(this.form.channelPaths).map(entry => entry.name)
    },
    selectedChannelLabelsText() {
      const labels = this.selectedChannelLabels
      const max = 10
      if (labels.length <= max) return labels.join('、')
      return labels.slice(0, max).join('、') + ` 等 ${labels.length} 个`
    },
    applyDialogTitle() {
      return this.editingApplyId ? '编辑采集任务' : '新建采集任务'
    },
    applyDialogSubmitText() {
      return this.editingApplyId ? '保存修改' : '提交审核'
    },
    runningTaskCount() {
      return 0
    },
    totalResultCount() {
      return this.overview && this.overview.totalCount != null ? Number(this.overview.totalCount) : 0
    },
    recentApplies() {
      return this.applies.slice(0, 5)
    },
    recentTasks() {
      return this.tasks.slice(0, 5)
    },
    totalMonitorCount() {
      if (this.activeSection === 'analysis' && this.monitorData && this.monitorData.total != null) {
        return Number(this.monitorData.total)
      }
      return this.overview && this.overview.totalCount != null ? Number(this.overview.totalCount) : 0
    },
    monitorSelectedTags() {
      const tags = []
      if (this.monitorStatStartDate && this.monitorStatEndDate) {
        tags.push(`统计：${this.monitorStatStartDate} ~ ${this.monitorStatEndDate}`)
      } else if (this.monitorStatDays !== 7) {
        tags.push(`统计：近${this.monitorStatDays}天`)
      }
      if (this.monitorQuery.platform) tags.push('平台：' + this.monitorQuery.platform)
      if (this.monitorQuery.sentiment) tags.push('情感：' + this.monitorQuery.sentiment)
      if ((this.monitorQuery.keyword || '').trim()) tags.push('关键词：' + this.monitorQuery.keyword.trim())
      if ((this.monitorQuery.author || '').trim()) tags.push('作者：' + this.monitorQuery.author.trim())
      if (this.monitorQuery.dateRange && this.monitorQuery.dateRange.length === 2) {
        tags.push(`发布：${this.monitorQuery.dateRange[0]} ~ ${this.monitorQuery.dateRange[1]}`)
      }
      if (this.monitorTab === 'warning') tags.push('类型：预警信息')
      return tags
    },
    hasMonitorFilters() {
      return !!(this.monitorQuery.platform || this.monitorQuery.sentiment ||
        (this.monitorQuery.keyword || '').trim() || (this.monitorQuery.author || '').trim() ||
        (this.monitorQuery.dateRange && this.monitorQuery.dateRange.length === 2) ||
        this.monitorStatStartDate || this.monitorStatEndDate ||
        this.monitorStatDays !== 7 || this.monitorTab !== 'all')
    },
    monitorFilterGroups() {
      const total = Number(this.monitorData && this.monitorData.total != null
        ? this.monitorData.total
        : (this.overview && this.overview.totalCount) || 0)
      const buildItems = (list, take) => {
        const items = [{ name: '全部', count: total }]
        ;(list || []).slice(0, take).forEach(item => {
          items.push({ name: item.name, count: Number(item.value || 0) })
        })
        return items
      }
      const groups = []
      const sources = (this.overview && this.overview.sources && this.overview.sources.length)
        ? this.overview.sources
        : this.monitorPlatforms.map(name => ({ name, value: 0 }))
      if (sources.length) {
        groups.push({ label: '数据来源', items: buildItems(sources, 6) })
      }
      const sentiments = (this.overview && this.overview.sentiments && this.overview.sentiments.length)
        ? this.overview.sentiments.map(item => ({
          name: this.displaySentimentLabel(item.name),
          value: item.value
        })).filter(item => ['正面', '中性', '负面'].includes(item.name))
        : [
          { name: '正面', value: 0 },
          { name: '中性', value: 0 },
          { name: '负面', value: 0 }
        ]
      groups.push({ label: '情感属性', items: buildItems(sentiments, 5) })
      const keywords = (this.overview && this.overview.keywords) || []
      if (keywords.length) {
        groups.push({ label: '命中主题词', items: buildItems(keywords, 8) })
      }
      return groups
    },
    monitorPlatformFilters() {
      return ['全部', ...this.monitorPlatforms]
    },
    monitorTimePresets() {
      return [
        { label: '24小时', days: 1 },
        { label: '3天', days: 3 },
        { label: '7天', days: 7 },
        { label: '15天', days: 15 },
        { label: '30天', days: 30 }
      ]
    },
    monitorSentimentFilters() {
      return [
        { label: '全部', value: '', className: '', icon: '' },
        { label: '正面', value: '正面', className: 'positive', icon: 'mdi mdi-emoticon-happy' },
        { label: '中性', value: '中性', className: 'neutral', icon: 'mdi mdi-emoticon-neutral' },
        { label: '负面', value: '负面', className: 'negative', icon: 'mdi mdi-emoticon-sad' }
      ]
    },
    dailyMonitorArticles() {
      if (this.overview && this.overview.latestOpinions && this.overview.latestOpinions.length) {
        return this.overview.latestOpinions
      }
      return this.results
    },
    monitorPlatforms() {
      if (this.monitorData && this.monitorData.platforms && this.monitorData.platforms.length) {
        return this.monitorData.platforms
      }
      const set = new Set()
      this.dailyMonitorArticles.forEach(item => {
        if (item.platform) set.add(item.platform)
      })
      return Array.from(set)
    },
    monitorRecords() {
      return (this.monitorData && this.monitorData.records) || []
    },
    filteredMonitorArticles() {
      return this.monitorRecords.filter(item => {
        if (this.monitorTab === 'marked' && !this.isMarked(item)) return false
        if (this.monitorTab === 'favorite' && !this.isFavorite(item)) return false
        return true
      })
    },
    monitorTotal() {
      if (this.monitorTab === 'marked' || this.monitorTab === 'favorite') {
        return this.filteredMonitorArticles.length
      }
      return this.monitorData && this.monitorData.total != null ? Number(this.monitorData.total) : 0
    },
    monitorPageFrom() {
      if (!this.monitorTotal) return 0
      return (this.monitorPageNo - 1) * this.monitorPageSize + 1
    },
    monitorPageTo() {
      if (!this.monitorTotal) return 0
      return Math.min(this.monitorPageNo * this.monitorPageSize, this.monitorTotal)
    },
    dashboardMetrics() {
      const total = this.totalMonitorCount
      const negative = this.overview && this.overview.negativeCount != null ? this.overview.negativeCount : 0
      const negativePercent = total > 0 ? Math.round((negative / total) * 100) : 0
      return [
        {
          label: '今日舆情',
          value: total,
          change: this.overviewRangeText,
          icon: 'mdi mdi-chart-timeline-variant',
          color: 'blue',
          targetSection: 'analysis'
        },
        {
          label: '负面数量',
          value: negative,
          change: `占比 ${negativePercent}%`,
          icon: 'mdi mdi-alert-decagram-outline',
          color: 'red',
          targetSection: 'risk'
        },
        {
          label: '风险事件',
          value: this.riskEventCount,
          change: 'P3 及以上风险',
          icon: 'mdi mdi-shield-alert-outline',
          color: 'purple',
          targetSection: 'risk'
        },
        {
          label: '高影响账号',
          value: this.highImpactAccountCount,
          change: '百万级粉丝账号',
          icon: 'mdi mdi-account-star-outline',
          color: 'red',
          targetSection: 'kol'
        },
        {
          label: '今日曝光',
          value: this.exposureText,
          change: '阅读 + 播放量',
          icon: 'mdi mdi-eye-outline',
          color: 'blue',
          targetSection: 'analysis'
        },
        {
          label: '今日传播',
          value: this.spreadText,
          change: '转发 + 分享量',
          icon: 'mdi mdi-share-variant-outline',
          color: 'green',
          targetSection: 'analysis'
        }
      ]
    },
    riskEventCount() {
      if (this.overview && this.overview.riskEventCount != null) {
        return Number(this.overview.riskEventCount)
      }
      return this.riskItems.filter(item => (item.score || 0) >= 55).length
    },
    riskEvents() {
      if (this.overview && this.overview.riskEvents && this.overview.riskEvents.length) {
        return this.overview.riskEvents
      }
      return this.riskItems.slice(0, 8)
    },
    hotTopicList() {
      return this.dailyMonitorArticles.slice(0, 5)
    },
    dashboardRankings() {
      if (this.overview && this.overview.keywords && this.overview.keywords.length) {
        const max = Math.max(...this.overview.keywords.map(item => Number(item.value || 0)), 1)
        const colors = ['#2f5bff', '#43b7e8', '#35b99f', '#f02f5f', '#ffae28', '#8b5cf6', '#10b981', '#f59e0b']
        return this.overview.keywords.slice(0, 8).map((item, index) => ({
          name: item.name,
          value: Number(item.value || 0),
          percent: Math.max(6, Math.round((Number(item.value || 0) / max) * 100)),
          color: colors[index % colors.length]
        }))
      }
      return []
    },
    exposureText() {
      const value = this.overview && this.overview.exposure != null ? Number(this.overview.exposure) : 0
      return this.formatCompact(value)
    },
    spreadText() {
      const value = this.overview && this.overview.spread != null ? Number(this.overview.spread) : 0
      return this.formatCompact(value)
    },
    highImpactAccountCount() {
      return this.overview && this.overview.highImpactAccountCount != null
        ? Number(this.overview.highImpactAccountCount)
        : 0
    },
    todaySummaryText() {
      if (this.overview && this.overview.todaySummary) {
        return this.overview.todaySummary
      }
      return ''
    },
    latestWarnings() {
      const list = this.riskEvents.length ? this.riskEvents : this.riskItems
      return list.slice(0, 5)
    },
    riskItems() {
      if (this.riskData && this.riskData.items && this.riskData.items.length) {
        return this.riskData.items
      }
      return []
    },
    riskItemPlatforms() {
      const set = []
      this.riskItems.forEach(item => {
        if (item.platform && !set.includes(item.platform)) set.push(item.platform)
      })
      return set
    },
    filteredRiskItems() {
      const kw = (this.riskListSearch || '').trim().toLowerCase()
      return this.riskItems.filter(item => {
        if (this.riskListLevel && item.level !== this.riskListLevel) return false
        if (this.riskListPlatform && item.platform !== this.riskListPlatform) return false
        if (kw) {
          const hay = `${item.title || ''} ${item.reason || ''} ${item.content || ''} ${item.authorName || ''}`.toLowerCase()
          if (!hay.includes(kw)) return false
        }
        return true
      })
    },
    pagedRiskItems() {
      const start = (this.riskListPage - 1) * this.riskListPageSize
      return this.filteredRiskItems.slice(start, start + this.riskListPageSize)
    },
    // 任务申请列表客户端分页切片
    pagedApplies() {
      const start = (this.applyListPage - 1) * this.applyListPageSize
      return this.applies.slice(start, start + this.applyListPageSize)
    },
    // 负面情感风险卡分页切片
    pagedRiskCardItems() {
      const start = (this.riskCardPage - 1) * this.riskCardPageSize
      return this.riskItems.slice(start, start + this.riskCardPageSize)
    },
    // 热点聚集风险卡分页切片
    pagedHotspotClusters() {
      const start = (this.hotspotCardPage - 1) * this.riskCardPageSize
      return this.riskHotspotClusters.slice(start, start + this.riskCardPageSize)
    },
    // KOL 参与风险卡分页切片
    pagedKolRisks() {
      const start = (this.kolRiskCardPage - 1) * this.riskCardPageSize
      return this.riskKolRisks.slice(start, start + this.riskCardPageSize)
    },
    riskLevels() {
      if (this.riskData && this.riskData.levels && this.riskData.levels.length) {
        return this.riskData.levels
      }
      return ['P1', 'P2', 'P3', 'P4'].map(name => ({ name, value: 0 }))
    },
    riskNegativeCount() {
      if (this.riskData && this.riskData.negativeCount != null) {
        return Number(this.riskData.negativeCount)
      }
      return this.riskItems.length
    },
    riskHotspotClusters() {
      return (this.riskData && this.riskData.hotspotClusters) ? this.riskData.hotspotClusters : []
    },
    riskKolRisks() {
      return (this.riskData && this.riskData.kolRisks) ? this.riskData.kolRisks : []
    },
    riskRangeText() {
      if (this.riskStatStartDate && this.riskStatEndDate) {
        return `${this.riskStatStartDate} 至 ${this.riskStatEndDate}`
      }
      const opt = this.riskRangeOptions.find(item => item.value === this.riskStatDays)
      return opt ? opt.label : `近${this.riskStatDays}天`
    },
    kolItems() {
      if (this.kolData && this.kolData.items && this.kolData.items.length) {
        return this.kolData.items
      }
      return []
    },
    eventItems() {
      if (this.eventData && this.eventData.items && this.eventData.items.length) {
        return this.eventData.items
      }
      return []
    },
    eventRangeText() {
      if (this.eventStatStartDate && this.eventStatEndDate) {
        return `${this.eventStatStartDate} 至 ${this.eventStatEndDate}`
      }
      const opt = this.eventRangeOptions.find(item => item.value === this.eventStatDays)
      return opt ? opt.label : `近${this.eventStatDays}天`
    },
    keywordText() {
      const task = this.tasks.find(item => item.id === this.keywordTaskId)
      if (!task) return ''
      return this.extractTaskKeyword(task)
    },
    keywordEvents() {
      const items = (this.eventData && this.eventData.items) ? this.eventData.items : []
      const kw = (this.keywordText || '').trim().toLowerCase()
      if (!kw) return items
      const matched = items.filter(ev => {
        const name = String(ev.name || '').toLowerCase()
        return name.includes(kw) || kw.includes(name)
      })
      return matched.length ? matched : items
    },
    activeEvent() {
      const items = this.keywordEvents
      if (!items.length) return null
      return items.find(item => item.id === this.activeEventId) || items[0]
    },
    selectedEventId: {
      get() {
        return this.activeEvent ? this.activeEvent.id : null
      },
      set(value) {
        this.activeEventId = value
      }
    },
    lifecycleStages() {
      return ['萌芽', '发酵', '扩散', '爆发', '衰减', '结束']
    },
    activeEventTrend() {
      if (!this.activeEvent || !this.activeEvent.trend || !this.activeEvent.trend.length) return []
      const max = Math.max(...this.activeEvent.trend.map(item => Number(item.value || 0)), 1)
      return this.activeEvent.trend.map(item => ({
        label: this.formatStatDate(item.name),
        value: Number(item.value || 0),
        height: Math.max(6, Math.round((Number(item.value || 0) / max) * 100))
      }))
    },
    activeEventSamples() {
      return this.activeEvent && this.activeEvent.samples ? this.activeEvent.samples : []
    },
    activeEventPlatforms() {
      const list = this.activeEvent && this.activeEvent.platforms
      if (list && list.length) {
        const total = list.reduce((sum, item) => sum + Number(item.value || 0), 0) || 1
        return list.map(item => ({
          name: item.name,
          value: Number(item.value || 0),
          percent: Math.max(4, Math.round((Number(item.value || 0) / total) * 100))
        }))
      }
      const counts = {}
      this.activeEventSamples.forEach(item => {
        const key = item.platform || '其他'
        counts[key] = (counts[key] || 0) + 1
      })
      const total = this.activeEventSamples.length || 1
      return Object.keys(counts)
        .map(name => ({ name, value: counts[name], percent: Math.round((counts[name] / total) * 100) }))
        .sort((a, b) => b.value - a.value)
    },
    activeEventFirstAccount() {
      if (this.activeEvent && this.activeEvent.firstAccount) {
        return this.activeEvent.firstAccount
      }
      const withTime = this.activeEventSamples.filter(item => item.publishTime)
      if (!withTime.length) return null
      return withTime.slice().sort((a, b) => String(a.publishTime).localeCompare(String(b.publishTime)))[0]
    },
    activeEventClusters() {
      const list = this.activeEvent && this.activeEvent.sentiments
      if (list && list.length) {
        const total = list.reduce((sum, item) => sum + Number(item.value || 0), 0) || 1
        const colorMap = {
          正面: '#0f8f55', 正向: '#0f8f55',
          中性: '#64748b',
          负面: '#ef4444', 负向: '#ef4444'
        }
        return list.map(item => ({
          label: this.displaySentimentLabel(item.name),
          value: Number(item.value || 0),
          percent: Math.max(4, Math.round((Number(item.value || 0) / total) * 100)),
          color: colorMap[item.name] || '#64748b'
        })).sort((a, b) => b.value - a.value)
      }
      const clusters = [
        { label: '正面观点', key: 'positive', color: '#0f8f55', value: 0 },
        { label: '中性观点', key: 'neutral', color: '#64748b', value: 0 },
        { label: '负面观点', key: 'negative', color: '#ef4444', value: 0 }
      ]
      this.activeEventSamples.forEach(item => {
        const kind = this.normalizeSentiment(item.sentiment)
        const hit = clusters.find(c => c.key === kind)
        if (hit) hit.value += 1
      })
      const total = this.activeEventSamples.length || 1
      clusters.forEach(c => { c.percent = Math.round((c.value / total) * 100) })
      return clusters
    },
    activeBrand() {
      const b = this.activeBrandData
      if (b) return b
      return {
        name: this.keywordText || '',
        healthIndex: 0,
        healthLabel: '无数据',
        volume: 0,
        exposure: 0,
        interaction: 0,
        positive: 0,
        neutral: 0,
        negative: 0,
        trend: []
      }
    },
    keywordHasData() {
      const b = this.activeBrandData
      if (!b) return false
      return Number(b.volume || 0) > 0
        || (Number(b.positive || 0) + Number(b.neutral || 0) + Number(b.negative || 0)) > 0
        || Number(b.exposure || 0) > 0
        || Number(b.interaction || 0) > 0
    },
    activeBrandSentiment() {
      const brand = this.activeBrand
      if (!brand) return []
      const total = Math.max(Number(brand.positive || 0) + Number(brand.neutral || 0) + Number(brand.negative || 0), 1)
      return [
        { label: '正面', value: Number(brand.positive || 0), color: '#128bed' },
        { label: '中性', value: Number(brand.neutral || 0), color: '#54b7ff' },
        { label: '负面', value: Number(brand.negative || 0), color: '#f59e0b' }
      ].map(item => ({ ...item, percent: Math.round((item.value / total) * 100) }))
    },
    activeBrandTrend() {
      const brand = this.activeBrand
      if (!brand || !brand.trend || !brand.trend.length) return []
      const max = Math.max(...brand.trend.map(item => Number(item.value || 0)), 1)
      return brand.trend.map(item => ({
        label: item.name,
        value: Number(item.value || 0),
        height: Math.max(6, Math.round((Number(item.value || 0) / max) * 100))
      }))
    },
    regionAnalysis() {
      if (this.overview && this.overview.regions && this.overview.regions.length) {
        const max = Math.max(...this.overview.regions.map(item => Number(item.value || 0)), 1)
        return this.overview.regions.slice(0, 10).map(item => ({
          label: item.name,
          value: Number(item.value || 0),
          percent: Math.max(4, Math.round((Number(item.value || 0) / max) * 100))
        }))
      }
      return []
    },
    hotKeywordTop() {
      const source = (this.overview && this.overview.keywords && this.overview.keywords.length)
        ? this.overview.keywords
        : this.dashboardRankings
      const max = Math.max(...source.map(item => Number(item.value || 0)), 1)
      return source.slice(0, 20).map((item, index) => ({
        rank: index + 1,
        name: item.name,
        value: Number(item.value || 0),
        percent: Math.max(4, Math.round((Number(item.value || 0) / max) * 100))
      }))
    },
    wordCloudTerms() {
      const colors = ['#128bed', '#54b7ff', '#f59e0b', '#ef4444', '#1c6356', '#8b5cf6', '#64748b']
      const keywords = (this.overview && this.overview.keywords) || []
      if (!keywords.length) return []
      const values = keywords.map(item => Number(item.value || 0))
      const max = Math.max(...values, 1)
      const min = Math.min(...values, 0)
      const range = Math.max(max - min, 1)
      return keywords.slice(0, 30).map((item, index) => ({
        text: item.name,
        size: Math.round(16 + ((Number(item.value || 0) - min) / range) * 22),
        color: colors[index % colors.length]
      }))
    },
    warningArticles() {
      if (this.riskEvents.length) {
        return this.riskEvents.slice(0, 6)
      }
      if (this.riskItems.length) {
        return this.riskItems.slice(0, 6)
      }
      if (this.overview && this.overview.warningOpinions && this.overview.warningOpinions.length) {
        return this.overview.warningOpinions
      }
      return this.dailyMonitorArticles
        .filter(item => item.riskLevel === '高' || item.sentiment === '负面')
        .slice(0, 4)
    },
    bigScreenStats() {
      const negativeItem = this.sentimentOverview.find(item => this.normalizeSentiment(item.label) === 'negative')
      return [
        { label: '全部信息', value: this.totalMonitorCount, icon: 'mdi mdi-database-outline', type: 'publicsentiment' },
        { label: '非消极信息', value: this.sentimentOverview.filter(item => this.normalizeSentiment(item.label) !== 'negative').reduce((total, item) => total + item.value, 0), icon: 'mdi mdi-emoticon-happy-outline', type: 'nosensitive' },
        { label: '消极信息', value: negativeItem ? negativeItem.value : 0, icon: 'mdi mdi-alert-circle-outline', type: 'sensitive' }
      ]
    },
    screenMapCoords() {
      return {
        北京: [116.4074, 39.9042], 天津: [117.2, 39.13], 上海: [121.4737, 31.2304],
        重庆: [106.55, 29.57], 广东: [113.2644, 23.1291], 广州: [113.2644, 23.1291],
        深圳: [114.0579, 22.5431], 江苏: [118.78, 32.04], 浙江: [120.15, 30.28],
        山东: [117.0, 36.65], 河南: [113.65, 34.76], 四川: [104.0665, 30.5728],
        成都: [104.0665, 30.5728], 湖北: [114.3054, 30.5931], 武汉: [114.3054, 30.5931],
        湖南: [113.0, 28.21], 福建: [119.3, 26.08], 陕西: [108.95, 34.27],
        安徽: [117.27, 31.86], 河北: [114.48, 38.03], 辽宁: [123.38, 41.8],
        江西: [115.89, 28.68], 云南: [102.71, 25.05], 广西: [108.33, 22.84],
        山西: [112.53, 37.87], 黑龙江: [126.63, 45.75], 吉林: [125.35, 43.88],
        贵州: [106.71, 26.57], 新疆: [87.68, 43.77], 甘肃: [103.73, 36.03],
        海南: [110.35, 20.02], 内蒙古: [111.65, 40.82], 宁夏: [106.27, 38.47],
        青海: [101.74, 36.56], 西藏: [91.11, 29.97], 香港: [114.17, 22.28],
        澳门: [113.54, 22.19], 台湾: [121.5, 25.05]
      }
    },
    screenNormalizedRegions() {
      const coords = this.screenMapCoords
      const regions = (this.overview && this.overview.regions) || []
      const map = {}
      regions.forEach(item => {
        const province = this.normalizeScreenProvince(item.name)
        if (!province || province === '未知' || !coords[province]) return
        if (!map[province]) map[province] = 0
        map[province] += Number(item.value || 0)
      })
      return Object.keys(map)
        .map(name => ({ name, value: map[name] }))
        .sort((a, b) => b.value - a.value)
    },
    screenMapPoints() {
      const coords = this.screenMapCoords
      return this.screenNormalizedRegions.map(item => ({
        name: item.name,
        province: item.name,
        value: [coords[item.name][0], coords[item.name][1], item.value]
      }))
    },
    screenProvinceHits() {
      return this.screenNormalizedRegions.map(item => ({ name: item.name, value: item.value }))
    },
    screenHotEvents() {
      let source = this.hotKeywordTop.filter(item => item.name && item.name !== '未命中')
      if (!source.length && this.overview && this.overview.sources && this.overview.sources.length) {
        const max = Math.max(...this.overview.sources.map(item => Number(item.value || 0)), 1)
        source = this.overview.sources.slice(0, 5).map((item, index) => ({
          rank: index + 1,
          name: item.name,
          value: Number(item.value || 0),
          percent: Math.max(4, Math.round((Number(item.value || 0) / max) * 100))
        }))
      }
      if (!source.length) return []
      return source.slice(0, 5).map(item => ({
        name: item.name,
        value: item.value,
        percent: item.percent
      }))
    },
    screenLatestNews() {
      return this.dailyMonitorArticles.slice(0, 8)
    },
    screenScrollingNews() {
      const list = this.screenLatestNews
      if (!list.length) {
        return []
      }
      const loopList = list.length > 3 ? list.concat(list) : list
      return loopList.map((item, index) => ({
        source: item,
        loopKey: `${index}-${item.id || item.externalOpinionId || item.title || 'news'}`
      }))
    },
    screenNewsScrollStyle() {
      const duration = Math.max(18, this.screenLatestNews.length * 4)
      return {
        '--screen-news-scroll-duration': `${duration}s`
      }
    },
    screenTicker() {
      return this.dailyMonitorArticles
        .slice(0, 8)
        .map(item => item.title)
        .filter(Boolean)
    },
    screenAuthorInteractions() {
      const fromOverview = (this.overview && this.overview.authorInteractions) || []
      if (fromOverview.length) {
        return fromOverview.slice(0, 5).map(item => ({
          name: item.name || '未知账号',
          value: Number(item.value || 0)
        }))
      }
      const records = this.dailyMonitorArticles || []
      const map = {}
      records.forEach(item => {
        const name = (item.authorName || '').trim() || '未知账号'
        let interaction = Number(item.likeCount || 0) + Number(item.commentCount || 0) + Number(item.repostCount || 0)
        if (interaction <= 0) interaction = 1
        if (!map[name]) {
          map[name] = { name, value: 0 }
        }
        map[name].value += interaction
      })
      return Object.keys(map)
        .map(key => map[key])
        .sort((a, b) => b.value - a.value)
        .slice(0, 5)
    },
    sentimentOverview() {
      if (this.overview && this.overview.sentiments && this.overview.sentiments.length) {
        const total = Math.max(this.overview.sentiments.reduce((sum, item) => sum + Number(item.value || 0), 0), 1)
        return this.overview.sentiments.map(item => ({
          label: item.name,
          color: this.sentimentColor(item.name),
          value: Number(item.value || 0),
          percent: Math.round((Number(item.value || 0) / total) * 100)
        }))
      }
      const items = [
        { label: '正面', color: this.sentimentColor('正面') },
        { label: '中性', color: this.sentimentColor('中性') },
        { label: '负面', color: this.sentimentColor('负面') }
      ]
      const total = Math.max(this.dailyMonitorArticles.length, 1)
      return items.map(item => {
        const value = this.dailyMonitorArticles.filter(article => article.sentiment === item.label).length
        return {
          ...item,
          value,
          percent: Math.round((value / total) * 100)
        }
      })
    },
    dominantSentiment() {
      if (!this.sentimentOverview.length) {
        return { label: '中性', percent: 0 }
      }
      return this.sentimentOverview.reduce((current, item) => (
        item.percent > current.percent ? item : current
      ), this.sentimentOverview[0])
    },
    sentimentDonutGradient() {
      const totalPercent = this.sentimentOverview.reduce((sum, item) => sum + Number(item.percent || 0), 0)
      if (totalPercent <= 0) {
        const positiveColor = this.sentimentColor('正面')
        return `conic-gradient(${positiveColor} 0% 100%)`
      }
      let start = 0
      const segments = this.sentimentOverview.map(item => {
        const end = start + item.percent
        const segment = `${item.color} ${start}% ${end}%`
        start = end
        return segment
      })
      return `conic-gradient(${segments.join(', ')})`
    },
    overviewPickerOptions() {
      return {
        disabledDate(date) {
          return date.getTime() > Date.now()
        }
      }
    },
    applyDateRangeHint() {
      const limit = `单次最长 ${COLLECT_RANGE_MAX_MONTHS} 个月`
      return isRealtimeCollectMode(this.form.collectMode)
        ? `实时采集只能选择今天及以后的时间，结束时间需晚于今天，默认近 7 天，${limit}`
        : `历史采集只能选择昨天（T-1）及以前的时间，${limit}`
    },
    applyDatePickerOptions() {
      const options = buildCollectModePickerOptions(this.form.collectMode, () => this.applyDateAnchor)
      // 选中第一个端点后以它为基准收窄可选范围，两端都选完即解除
      options.onPick = ({ minDate, maxDate }) => {
        this.applyDateAnchor = maxDate ? null : (minDate || null)
      }
      return options
    },
    overviewRangeText() {
      if (this.overviewRange && this.overviewRange.length === 2) {
        return `${this.overviewRange[0]} 至 ${this.overviewRange[1]}`
      }
      const opt = this.overviewRangeOptions.find(item => item.value === this.overviewDays)
      return opt ? opt.label : `近${this.overviewDays}天`
    },
    overviewHasData() {
      if (!this.overview) return false
      return Number(this.overview.totalCount || 0) > 0 ||
        Boolean((this.overview.latestOpinions || []).length) ||
        Boolean((this.overview.riskEvents || []).length) ||
        Boolean((this.overview.keywords || []).length)
    },
    overviewStatusText() {
      if (this.overviewLoading) {
        return this.overviewLoadMessage ? `状态：${this.overviewLoadMessage}` : '状态：加载中...'
      }
      if (this.overviewLoadMessage) {
        return `状态：${this.overviewLoadMessage}`
      }
      if (this.overviewLastRequestParams && !this.overviewHasData) {
        return '状态：暂无数据'
      }
      return '状态：已更新'
    },
    overviewLoadingText() {
      if (this.overviewLoadMessage) {
        return this.overviewLoadMessage
      }
      return this.activeSection === 'bigScreen' ? '加载大屏数据...' : '加载概览数据...'
    },
    overviewEmptyHint() {
      const range = this.overviewDataRange
      if (!range || !range.latestDate) {
        return '当前所选时间范围内，你的任务没有数据。请调整时间范围后重试。'
      }
      const latest = this.formatStatDate(range.latestDate)
      const earliest = this.formatStatDate(range.earliestDate)
      const span = earliest && latest ? `${earliest} 至 ${latest}` : latest
      return `当前所选时间范围内，你的任务没有数据。你的数据实际覆盖：${span}（最近更新 ${latest}）。可把时间范围调整到该区间查看。`
    },
    sentimentIndex() {
      if (this.overview && this.overview.sentimentIndex != null) {
        return Math.max(0, Math.min(100, Number(this.overview.sentimentIndex)))
      }
      const senti = this.sentimentOverview
      const total = senti.reduce((sum, item) => sum + Number(item.value || 0), 0)
      if (!total) return 0
      const negative = senti
        .filter(item => this.normalizeSentiment(item.label) === 'negative')
        .reduce((sum, item) => sum + Number(item.value || 0), 0)
      return Math.max(0, Math.min(100, Math.round(100 - (negative / total) * 100)))
    },
    sentimentIndexText() {
      if (this.sentimentIndex >= 80) return '整体态势平稳'
      if (this.sentimentIndex >= 60) return '存在局部波动'
      return '建议重点关注'
    },
    trendData() {
      if (!this.overview) return []
      const range = this.getOverviewTrendRange()
      const valueMap = {}
      ;(this.overview.trends || []).forEach(item => {
        const key = this.normalizeTrendDateKey(item.name)
        if (!key) return
        valueMap[key] = (valueMap[key] || 0) + Number(item.value || 0)
      })
      let points = this.buildDailyTrendPoints(range, valueMap)
      const pointSum = points.reduce((sum, item) => sum + item.value, 0)
      const rawTrends = this.overview.trends || []
      const rawSum = rawTrends.reduce((sum, item) => sum + Number(item.value || 0), 0)
      if (pointSum === 0 && rawSum > 0) {
        points = rawTrends.map(item => ({
          label: this.normalizeTrendDateKey(item.name) || String(item.name || ''),
          value: Number(item.value || 0),
          height: 0
        })).filter(item => item.label)
      }
      const max = Math.max(...points.map(item => item.value), 1)
      return points.map(item => ({
        ...item,
        height: Math.round((item.value / max) * 100)
      }))
    },
    sourceAnalysis() {
      if (this.overview && this.overview.sources && this.overview.sources.length) {
        return this.overview.sources
          .map(item => ({ label: item.name, value: Number(item.value || 0) }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 8)
      }
      const counts = this.results.reduce((total, item) => {
        const key = item.platform || '其他'
        total[key] = (total[key] || 0) + 1
        return total
      }, {})
      return Object.entries(counts)
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 5)
    },
    riskAnalysis() {
      const colors = { 高风险: '#ef4444', 中风险: '#f59e0b', 低风险: '#10b981' }
      if (this.overview && this.overview.riskDistribution && this.overview.riskDistribution.length) {
        const total = Math.max(this.overview.riskDistribution.reduce((sum, item) => sum + Number(item.value || 0), 0), 1)
        return this.overview.riskDistribution.map(item => ({
          label: item.name,
          value: Number(item.value || 0),
          color: colors[item.name] || '#8b5cf6',
          percent: Math.min(100, Math.round((Number(item.value || 0) / total) * 100))
        }))
      }
      const items = [
        { label: '高风险', key: '高', color: '#ef4444' },
        { label: '中风险', key: '中', color: '#f59e0b' },
        { label: '低风险', key: '低', color: '#10b981' }
      ]
      const totalForPercent = Math.max(this.results.length, 1)
      return items.map(item => {
        const value = this.riskCount(item.key)
        return {
          label: item.label,
          value,
          color: item.color,
          percent: Math.min(100, Math.round((value / totalForPercent) * 100))
        }
      })
    },
    keywordAnalysis() {
      if (this.overview && this.overview.keywords && this.overview.keywords.length) {
        const types = ['', 'success', 'danger', 'warning', 'info', '', 'success', 'warning']
        return this.overview.keywords.slice(0, 8).map((item, index) => ({
          label: item.name,
          type: types[index % types.length],
          value: Number(item.value || 0)
        }))
      }
      return []
    },
    adviceList() {
      return [
        '高风险内容优先进入人工复核，确认是否需要快速响应。',
        '声量异常上升时，先从来源平台和关键词热度定位传播入口。',
        '定期复盘启用中任务的关键词规则，减少无效采集和误报。'
      ]
    }
  },
  watch: {
    activeSection(val) {
      if (val === 'dashboard') {
        this.$nextTick(() => this.updateDashboardCharts())
      }
    },
    trendData: {
      handler() {
        if (this.activeSection === 'dashboard') {
          this.$nextTick(() => this.renderDashboardTrendChart())
        }
      },
      deep: true
    }
  },
  created() {
    setUnauthorizedHandler(this.handleUnauthorized)
    // 先用本地会话判断租户管理员，避免刷新 #/groupAdmin 时鉴权未完成被踢到首页
    const cached = loadAuthSession()
    if (cached && cached.token) {
      this.authUser = cached
    }
    this.activeSection = this.sectionFromRoute()
    this.restoreAuth()
  },
  mounted() {
    window.addEventListener('popstate', this.handleBrowserRouteChange)
    window.addEventListener('hashchange', this.handleBrowserRouteChange)
    this.syncBrowserRoute(this.activeSection, true)
    window.addEventListener('resize', this.resizeCharts)
    this.updateDashboardCharts()
  },
  beforeDestroy() {
    window.removeEventListener('popstate', this.handleBrowserRouteChange)
    window.removeEventListener('hashchange', this.handleBrowserRouteChange)
    window.removeEventListener('resize', this.resizeCharts)
    this.clearReportPollTimer()
    this.clearOverviewPollTimer()
    Object.values(this.chartInstances).forEach(chart => chart && chart.dispose())
  },
  methods: {
    formatChannelLabels(channel) {
      return formatChannelDisplay(channel)
    },
    formatUserName(userId) {
      if (userId == null) return '—'
      return this.userNameMap[userId] || `用户${userId}`
    },
    async restoreAuth() {
      const cached = loadAuthSession()
      if (!cached || !cached.token) {
        this.authUser = null
        this.authReady = true
        return
      }
      saveAuthSession(cached)
      try {
        this.authUser = await getMe()
        saveAuthSession(this.authUser)
        this.bootstrapData()
      } catch (error) {
        clearAuthSession()
        this.authUser = null
      } finally {
        this.authReady = true
      }
    },
    handleLogin(session) {
      this.authUser = session
      saveAuthSession(session)
      this.bootstrapData()
    },
    async handleLogout() {
      try {
        await logout()
      } catch (error) {
        clearAuthSession()
      }
      this.authUser = null
    },
    handleUnauthorized(message) {
      clearAuthSession()
      this.authUser = null
      if (message) {
        this.$message.warning(message)
      }
    },
    bootstrapData() {
      this.loadOverview()
      this.loadApplies()
      this.loadTasks()
      if (this.activeSection !== 'dashboard') {
        this.goSection(this.activeSection, { skipRoute: true })
      }
    },
    formatCollectModeLabel(value) {
      return getCollectModeLabel(value)
    },
    isRealtimeCollectMode,
    formatApplyDateRange(apply) {
      const range = buildDateTimeRange(apply && apply.startTime, apply && apply.endTime)
      return range.length === 2 ? `${range[0]} ~ ${range[1]}` : '—'
    },
    onCollectModeChange(mode, formKey) {
      const target = this[formKey]
      if (!target) return
      if (isRealtimeCollectMode(mode)) {
        target.dateRange = defaultRealtimeDateRange()
      } else {
        target.dateRange = []
      }
      if (formKey === 'form') {
        this.applyDateAnchor = null
        this.applyDateRangeKey = Date.now()
        this.$nextTick(() => {
          if (this.$refs.applyForm) {
            this.$refs.applyForm.clearValidate('dateRange')
          }
        })
      }
    },
    encodeChannelJson(paths) {
      return JSON.stringify(channelPathsToEntries(paths))
    },
    defaultForm() {
      return {
        taskName: '',
        channelPaths: [],
        collectMode: COLLECT_MODE_HISTORY,
        dateRange: [],
        keywordGroups: [this.newKeywordGroup()],
        excludeWords: [],
        collectPosts: true,
        collectComments: false,
        collectInteractions: false,
        commentUpdateDays: 0,
        interactionUpdateDays: 0,
        applyReason: '',
        blockLimit: ''
      }
    },
    newKeywordGroup(words) {
      return { words: Array.isArray(words) ? words.slice() : [], inputVisible: false, inputValue: '' }
    },
    addKeywordGroup() {
      this.form.keywordGroups.push(this.newKeywordGroup())
    },
    removeKeywordGroup(index) {
      this.form.keywordGroups.splice(index, 1)
      if (!this.form.keywordGroups.length) {
        this.form.keywordGroups.push(this.newKeywordGroup())
      }
      if (this.$refs.applyForm) this.$refs.applyForm.validateField('keywordGroups')
    },
    showKeywordInput(index) {
      const group = this.form.keywordGroups[index]
      group.inputVisible = true
      this.$nextTick(() => {
        const ref = this.$refs['kwInput' + index]
        const input = Array.isArray(ref) ? ref[0] : ref
        if (input) input.focus()
      })
    },
    confirmKeyword(index) {
      const group = this.form.keywordGroups[index]
      const value = (group.inputValue || '').trim()
      if (value && group.words.indexOf(value) === -1) {
        const preview = this.form.keywordGroups.map((item, i) => {
          if (i !== index) return item
          return { words: item.words.concat([value]) }
        })
        if (isSingleNonEmptyGroupTooWide(preview)) {
          this.$message.error(SINGLE_GROUP_TOO_WIDE_MESSAGE)
        } else {
          group.words.push(value)
        }
        if (this.$refs.applyForm) this.$refs.applyForm.validateField('keywordGroups')
      }
      group.inputValue = ''
      group.inputVisible = false
    },
    removeKeyword(groupIndex, wordIndex) {
      this.form.keywordGroups[groupIndex].words.splice(wordIndex, 1)
      if (this.$refs.applyForm) this.$refs.applyForm.validateField('keywordGroups')
    },
    showExcludeInput() {
      this.excludeInputVisible = true
      this.$nextTick(() => {
        if (this.$refs.excludeInput) this.$refs.excludeInput.focus()
      })
    },
    confirmExcludeWord() {
      const value = (this.excludeInputValue || '').trim()
      if (value && this.form.excludeWords.indexOf(value) === -1) {
        this.form.excludeWords.push(value)
      }
      this.excludeInputValue = ''
      this.excludeInputVisible = false
    },
    removeExcludeWord(index) {
      this.form.excludeWords.splice(index, 1)
    },
    openKeywordRecommendDialog() {
      this.keywordRecommendVisible = true
    },
    resetKeywordRecommendDialog() {
      this.keywordRecommendForm = { object: '', hint: '' }
      this.keywordRecommendSchemes = []
      this.keywordRecommendSelected = -1
      this.keywordRecommendLoading = false
    },
    async fetchKeywordRecommend() {
      const objectName = (this.keywordRecommendForm.object || '').trim()
      if (!objectName) {
        this.$message.warning('请输入监控对象')
        return
      }
      // 防重复点击：请求进行中直接忽略
      if (this.keywordRecommendLoading) return
      this.keywordRecommendLoading = true
      try {
        const result = await recommendKeywordSchemes({
          object: objectName,
          hint: (this.keywordRecommendForm.hint || '').trim()
        })
        this.keywordRecommendSchemes = result && Array.isArray(result.schemes) ? result.schemes : []
        this.keywordRecommendSelected = this.keywordRecommendSchemes.length ? 0 : -1
      } catch (error) {
        this.keywordRecommendSchemes = []
        this.keywordRecommendSelected = -1
        this.$message.error((error && error.message) || '智能推荐失败，请稍后重试')
      } finally {
        this.keywordRecommendLoading = false
      }
    },
    applyKeywordRecommendScheme() {
      const scheme = this.keywordRecommendSchemes[this.keywordRecommendSelected]
      if (!scheme) return
      const groups = (Array.isArray(scheme.keywordGroups) ? scheme.keywordGroups : [])
        .map(words => this.newKeywordGroup(words))
        .filter(group => group.words.length > 0)
      this.form.keywordGroups = groups.length ? groups : [this.newKeywordGroup()]
      this.form.excludeWords = (Array.isArray(scheme.excludeWords) ? scheme.excludeWords : [])
        .map(word => String(word).trim())
        .filter(Boolean)
      this.keywordRecommendVisible = false
      this.$nextTick(() => {
        if (this.$refs.applyForm) this.$refs.applyForm.validateField('keywordGroups')
      })
      this.$message.success('已回填关键词方案，可继续编辑后提交')
    },
    formatEstimateText,
    formatEstimateTime(value) {
      return normalizeDateTimeForPicker(value) || '—'
    },
    resetEstimatePreview() {
      this.estimatedCount = null
      this.estimatedAt = null
      this.estimateError = ''
      this.estimateLoading = false
    },
    applyEstimateResult(result) {
      this.estimatedCount = result && result.estimatedCount != null ? result.estimatedCount : null
      this.estimatedAt = result && result.estimatedAt ? result.estimatedAt : null
      this.estimateError = result && result.estimateError ? result.estimateError : ''
    },
    buildApplyPayload() {
      return {
        taskName: this.form.taskName,
        channel: this.encodeChannelJson(this.form.channelPaths),
        collectMode: this.form.collectMode,
        startTime: toApiDateTime(this.form.dateRange[0]),
        endTime: toApiDateTime(this.form.dateRange[1]),
        keywordsJson: JSON.stringify(this.buildKeywordGroups()),
        excludeWordsJson: JSON.stringify(this.form.excludeWords.slice()),
        collectPosts: this.form.collectPosts,
        collectComments: this.form.collectComments,
        collectInteractions: this.form.collectInteractions,
        commentUpdateDays: this.form.commentUpdateDays,
        interactionUpdateDays: this.form.interactionUpdateDays,
        applyReason: this.form.applyReason,
        blockLimit: this.form.blockLimit !== '' && this.form.blockLimit != null ? Number(this.form.blockLimit) : null
      }
    },
    estimatePostCount() {
      if (!this.ensureCollectDateRangeValid()) return
      this.$refs.applyForm.validate(async valid => {
        if (!valid) return
        this.estimateLoading = true
        try {
          const result = await estimateApply(this.buildApplyPayload())
          this.applyEstimateResult(result)
          if (result && result.estimateError) {
            this.$message.error(result.estimateError)
          }
        } catch (error) {
          this.estimatedCount = null
          this.estimatedAt = null
          this.estimateError = (error && error.message) || '预估失败'
          this.$message.error(this.estimateError)
        } finally {
          this.estimateLoading = false
        }
      })
    },
    openCreateDialog() {
      this.applyAssistVisible = false
      this.editingApplyId = null
      this.form = this.defaultForm()
      this.excludeInputVisible = false
      this.excludeInputValue = ''
      this.resetEstimatePreview()
      this.applyDateAnchor = null
      this.applyDateRangeKey = Date.now()
      this.createVisible = true
      this.$nextTick(() => {
        if (this.$refs.applyForm) {
          this.$refs.applyForm.clearValidate()
        }
      })
    },
    openApplyAssist() {
      this.createVisible = false
      this.applyAssistVisible = true
    },
    openCreateDialogWithDraft(draft) {
      this.applyAssistVisible = false
      this.editingApplyId = null
      this.$nextTick(() => {
        this.form = mapDraftToForm(draft, this.defaultForm(), words => this.newKeywordGroup(words))
        this.excludeInputVisible = false
        this.excludeInputValue = ''
        this.resetEstimatePreview()
        this.applyDateAnchor = null
        this.applyDateRangeKey = Date.now()
        this.createVisible = true
        this.$nextTick(() => {
          if (this.$refs.applyForm) {
            this.$refs.applyForm.clearValidate()
          }
        })
      })
    },
    resetApplyDialog() {
      this.editingApplyId = null
      this.form = this.defaultForm()
      this.excludeInputVisible = false
      this.excludeInputValue = ''
      this.resetEstimatePreview()
    },
    canEditApply(row) {
      return !!(this.authUser && row.applyUserId === this.authUser.id && row.applyStatus === 'PENDING_REVIEW')
    },
    canOperatePauseResume(row) {
      if (!this.authUser || !row) return false
      if (this.isGroupLeader) return true
      return row.applyUserId === this.authUser.id
    },
    canPauseApply(row) {
      return !!(row && row.applyStatus === 'APPROVED' && this.canOperatePauseResume(row))
    },
    canResumeApply(row) {
      return !!(row && row.applyStatus === 'PAUSED' && this.canOperatePauseResume(row))
    },
    async pauseApplyRow(row) {
      if (!this.canPauseApply(row)) return
      try {
        await this.$confirm(
          '暂停后本系统不再把该申请视为在跑采集；已入库数据仍可查看。暂停不会调用瓴羊停采。',
          '暂停申请',
          { confirmButtonText: '暂停', cancelButtonText: '取消', type: 'warning' }
        )
        if (this.isGroupLeader) {
          await pauseGroupApply(row.id, { remark: '暂停' })
        } else {
          await pauseApply(row.id, { remark: '暂停' })
        }
        this.$message.success('已暂停')
        this.refreshAppliesAndTasks()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error((error && error.message) || '暂停失败')
        }
      }
    },
    async resumeApplyRow(row) {
      if (!this.canResumeApply(row)) return
      try {
        await this.$confirm(
          '恢复后回到启用中，沿用原正式任务和第三方 ID，不会重新走启用表单。',
          '恢复申请',
          { confirmButtonText: '恢复', cancelButtonText: '取消', type: 'warning' }
        )
        if (this.isGroupLeader) {
          await resumeGroupApply(row.id, { remark: '恢复' })
        } else {
          await resumeApply(row.id, { remark: '恢复' })
        }
        this.$message.success('已恢复')
        this.refreshAppliesAndTasks()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error((error && error.message) || '恢复失败')
        }
      }
    },
    buildFormFromApply(row) {
      return {
        taskName: row.taskName || '',
        channelPaths: entriesToChannelPaths(parseChannelEntries(row.channel)),
        collectMode: normalizeCollectModeValue(row.collectMode),
        dateRange: buildDateTimeRange(row.startTime, row.endTime),
        keywordGroups: this.parseKeywordGroups(row.keywordsJson),
        excludeWords: this.parseExcludeWords(row.excludeWordsJson),
        collectPosts: row.collectPosts !== false,
        collectComments: !!row.collectComments,
        collectInteractions: !!row.collectInteractions,
        commentUpdateDays: row.commentUpdateDays || 0,
        interactionUpdateDays: row.interactionUpdateDays || 0,
        applyReason: row.applyReason || '',
        blockLimit: row.blockLimit != null ? String(row.blockLimit) : ''
      }
    },
    openEditApplyDialog(row) {
      if (!this.canEditApply(row)) return
      this.editingApplyId = row.id
      this.form = this.buildFormFromApply(row)
      this.excludeInputVisible = false
      this.excludeInputValue = ''
      this.applyEstimateResult(row)
      this.applyDateAnchor = null
      this.applyDateRangeKey = Date.now()
      this.createVisible = true
      this.$nextTick(() => {
        if (this.$refs.applyForm) {
          this.$refs.applyForm.clearValidate()
        }
        this.warnIfCollectDateRangeInvalid()
      })
    },
    warnIfCollectDateRangeInvalid() {
      if (!Array.isArray(this.form.dateRange) || this.form.dateRange.length !== 2) {
        return
      }
      const message = validateCollectDateRange(this.form.collectMode, this.form.dateRange)
      if (!message) return
      this.$message.warning(message)
      if (this.$refs.applyForm) {
        this.$refs.applyForm.validateField('dateRange')
      }
    },
    ensureCollectDateRangeValid() {
      const message = validateCollectDateRange(this.form.collectMode, this.form.dateRange)
      if (!message) return true
      this.$message.warning(message)
      if (this.$refs.applyForm) {
        this.$refs.applyForm.validateField('dateRange')
      }
      return false
    },
    openApplyDetail(row) {
      this.selectedApply = row
      this.applyDetailVisible = true
      if (row.applyStatus === 'APPROVED' || row.applyStatus === 'PAUSED') {
        this.loadTasks()
      }
    },
    async loadMonitor(showLoading) {
      const useLoading = showLoading !== false
      const params = {
        pageNo: this.monitorPageNo,
        pageSize: this.monitorPageSize,
        negativeOnly: this.monitorTab === 'warning'
      }
      if (this.monitorStatStartDate && this.monitorStatEndDate) {
        params.startDate = this.monitorStatStartDate
        params.endDate = this.monitorStatEndDate
      } else {
        Object.assign(params, this.presetDateRange(this.monitorStatDays))
      }
      const keyword = (this.monitorQuery.keyword || '').trim()
      if (keyword) params.keyword = keyword
      if (this.monitorQuery.platform) params.platform = this.monitorQuery.platform
      if (this.monitorQuery.sentiment) params.sentiment = this.monitorQuery.sentiment
      const author = (this.monitorQuery.author || '').trim()
      if (author) params.author = author
      const range = this.monitorQuery.dateRange
      if (range && range.length === 2) {
        params.pubStartDate = range[0]
        params.pubEndDate = range[1]
      }
      this.monitorLoading = useLoading
      try {
        this.monitorData = await getMonitor(params)
        if (this.monitorData && this.monitorData.pageNo) {
          this.monitorPageNo = this.monitorData.pageNo
        }
      } catch (error) {
        this.monitorData = null
        this.$message.error((error && error.message) || '监测数据加载失败')
      } finally {
        if (useLoading) this.monitorLoading = false
      }
    },
    monitorOverviewParams() {
      const params = {}
      if (this.monitorStatStartDate && this.monitorStatEndDate) {
        params.startDate = this.monitorStatStartDate
        params.endDate = this.monitorStatEndDate
      } else {
        Object.assign(params, this.presetDateRange(this.monitorStatDays))
      }
      return params
    },
    async loadMonitorFilterMeta() {
      // 与 loadOverview 共用 overviewLoading，避免监测页与概览页堆叠触发后端单飞
      if (this.overviewLoading) return
      this.overviewLoading = true
      try {
        const overview = await getOverview(this.monitorOverviewParams())
        if (!overview || overview.status === 'COMPUTING' || overview.status === 'FAILED') {
          return
        }
        this.overview = overview
      } catch (error) {
        // 筛选项统计失败不阻断列表加载
      } finally {
        this.overviewLoading = false
      }
    },
    async refreshMonitorPage() {
      this.monitorPageLoading = true
      try {
        await this.loadMonitorFilterMeta()
        await this.loadMonitor(false)
      } finally {
        this.monitorPageLoading = false
      }
    },
    searchMonitor() {
      this.monitorPageNo = 1
      this.loadMonitor()
    },
    displaySentimentLabel(name) {
      const text = String(name || '')
      if (text === '正向') return '正面'
      if (text === '负向') return '负面'
      return text
    },
    querySentimentValue(name) {
      const text = this.displaySentimentLabel(name)
      if (text === '正面' || text === '负面' || text === '中性') return text
      return ''
    },
    isMonitorGroupActive(groupLabel, name) {
      if (name === '全部') {
        if (groupLabel === '数据来源') return !this.monitorQuery.platform
        if (groupLabel === '情感属性') return !this.monitorQuery.sentiment
        if (groupLabel === '命中主题词') return !(this.monitorQuery.keyword || '').trim()
        return true
      }
      if (groupLabel === '数据来源') return this.monitorQuery.platform === name
      if (groupLabel === '情感属性') return this.monitorQuery.sentiment === this.querySentimentValue(name)
      if (groupLabel === '命中主题词') return (this.monitorQuery.keyword || '').trim() === name
      return false
    },
    applyMonitorGroupFilter(groupLabel, name) {
      if (groupLabel === '数据来源') {
        this.monitorQuery.platform = name === '全部' ? '' : name
      } else if (groupLabel === '情感属性') {
        this.monitorQuery.sentiment = name === '全部' ? '' : this.querySentimentValue(name)
      } else if (groupLabel === '命中主题词') {
        this.monitorQuery.keyword = name === '全部' ? '' : name
      }
      this.searchMonitor()
    },
    isMonitorPlatformActive(item) {
      return item === '全部' ? !this.monitorQuery.platform : this.monitorQuery.platform === item
    },
    selectMonitorPlatform(item) {
      this.monitorQuery.platform = item === '全部' ? '' : item
      this.searchMonitor()
    },
    isMonitorTimeActive(days) {
      return !this.monitorStatStartDate && !this.monitorStatEndDate && this.monitorStatDays === days
    },
    selectMonitorStatDays(days) {
      this.monitorStatStartDate = null
      this.monitorStatEndDate = null
      this.monitorStatDays = Number(days) || 7
      this.monitorPageNo = 1
      this.refreshMonitorPage()
    },
    onMonitorStatDateChange() {
      if (this.monitorStatStartDate && this.monitorStatEndDate &&
        this.monitorStatStartDate > this.monitorStatEndDate) {
        const temp = this.monitorStatStartDate
        this.monitorStatStartDate = this.monitorStatEndDate
        this.monitorStatEndDate = temp
      }
      if (this.monitorStatStartDate && this.monitorStatEndDate) {
        this.monitorPageNo = 1
        this.refreshMonitorPage()
      }
    },
    isMonitorSentimentActive(value) {
      return (this.monitorQuery.sentiment || '') === (value || '')
    },
    selectMonitorSentiment(value) {
      this.monitorQuery.sentiment = value || ''
      this.searchMonitor()
    },
    switchMonitorTab(tab) {
      if (this.monitorTab === tab) return
      const wasWarning = this.monitorTab === 'warning'
      this.monitorTab = tab
      if (tab === 'warning' || wasWarning) {
        this.monitorPageNo = 1
        this.loadMonitor()
      }
    },
    onMonitorPageChange(page) {
      this.monitorPageNo = page
      if (this.monitorTab === 'marked' || this.monitorTab === 'favorite') return
      this.loadMonitor()
    },
    async loadOverview() {
      // 轮询期间允许切换窗口（会 bump seq 取消旧轮询）；仅拦截「同一次 HTTP 尚未返回」的连点
      if (this.overviewLoading && !this.overviewPollTimer) return
      const requestSeq = ++this.overviewRequestSeq
      this.clearOverviewPollTimer()
      const params = {}
      if (this.activeSection === 'report') {
        params.trendGranularity = this.analysisTrendGranularity
      }
      if (this.activeSection === 'bigScreen') {
        if (this.screenTaskId) params.taskId = this.screenTaskId
      }
      if (this.overviewRange && this.overviewRange.length === 2) {
        params.startDate = this.overviewRange[0]
        params.endDate = this.overviewRange[1]
      } else {
        Object.assign(params, this.presetDateRange(this.overviewDays))
      }
      this.overviewLastRequestParams = { ...params }
      this.overviewLoading = true
      this.overviewLoadMessage = ''
      await this.fetchOverviewUntilReady(requestSeq, params, Date.now())
    },
    clearOverviewPollTimer() {
      if (this.overviewPollTimer) {
        window.clearTimeout(this.overviewPollTimer)
        this.overviewPollTimer = null
      }
    },
    async fetchOverviewUntilReady(requestSeq, params, startedAt) {
      let ready = false
      try {
        const overview = await this.withTimeout(
          getOverview(params),
          120000,
          '概览接口响应较慢，请稍后刷新'
        )
        if (requestSeq !== this.overviewRequestSeq) return
        const status = (overview && overview.status) || 'READY'
        if (status === 'COMPUTING') {
          this.overviewLoadMessage = (overview && overview.statusMessage) || '数据量较大，正在计算，请稍候'
          if (Date.now() - startedAt > 5 * 60 * 1000) {
            this.overviewLoadMessage = '概览计算超时，请稍后刷新'
            this.$message.warning(this.overviewLoadMessage)
            this.overviewLoading = false
            return
          }
          this.overviewPollTimer = window.setTimeout(() => {
            // 回调已触发：先清标志，否则 READY 的 finally 会把旧 timeout id 当成「还在轮询」而不关遮罩
            this.overviewPollTimer = null
            this.fetchOverviewUntilReady(requestSeq, params, startedAt)
          }, 3000)
          return
        }
        if (status === 'FAILED') {
          this.overview = null
          this.overviewLoadMessage = (overview && overview.statusMessage) || '概览计算失败'
          this.$message.warning(this.overviewLoadMessage)
          this.overviewLoading = false
          return
        }
        this.overview = overview
        this.overviewLoadMessage = this.overviewHasData ? '已更新' : '暂无数据'
        if (!this.overviewHasData) {
          this.loadOverviewDataRange()
        }
        this.syncRiskDataFromOverview()
        if (this.activeSection === 'bigScreen') {
          this.screenUpdatedAt = new Date().toLocaleString()
        }
        ready = true
      } catch (error) {
        if (requestSeq !== this.overviewRequestSeq) return
        this.overview = null
        this.overviewLoadMessage = (error && error.message) || '概览数据加载失败'
        this.$message.warning(this.overviewLoadMessage)
      } finally {
        if (requestSeq === this.overviewRequestSeq && !this.overviewPollTimer) {
          this.overviewLoading = false
        }
      }
      if (!ready || requestSeq !== this.overviewRequestSeq) return
      await this.$nextTick()
      this.updateDashboardCharts()
      this.updateAnalysisCharts()
      this.updateBigScreenCharts()
    },
    async onScreenTaskChange() {
      await this.loadOverview()
    },
    withTimeout(promise, timeoutMs, message) {
      let timer = null
      const timeout = new Promise((_, reject) => {
        timer = window.setTimeout(() => reject(new Error(message || '请求超时')), timeoutMs)
      })
      return Promise.race([promise, timeout]).finally(() => {
        if (timer) {
          window.clearTimeout(timer)
        }
      })
    },
    syncRiskDataFromOverview() {
      const items = (this.overview && this.overview.riskEvents) || []
      if (!items.length && this.riskData && this.riskData.items && this.riskData.items.length) {
        return
      }
      this.riskData = {
        items,
        levels: this.buildRiskLevels(items)
      }
    },
    buildRiskLevels(items) {
      const counts = { P1: 0, P2: 0, P3: 0, P4: 0 }
      ;(items || []).forEach(item => {
        if (Object.prototype.hasOwnProperty.call(counts, item.level)) {
          counts[item.level] += 1
        }
      })
      return ['P1', 'P2', 'P3', 'P4'].map(name => ({ name, value: counts[name] }))
    },
    overviewQueryParams(maxRows) {
      const params = {}
      if (maxRows && maxRows > 0) {
        params.maxRows = maxRows
      }
      if (this.overviewRange && this.overviewRange.length === 2) {
        params.startDate = this.overviewRange[0]
        params.endDate = this.overviewRange[1]
      } else {
        Object.assign(params, this.presetDateRange(this.overviewDays))
      }
      return params
    },
    riskQueryParams() {
      const params = {}
      if (this.riskStatStartDate && this.riskStatEndDate) {
        params.startDate = this.riskStatStartDate
        params.endDate = this.riskStatEndDate
      } else {
        Object.assign(params, this.presetDateRange(this.riskStatDays))
      }
      if ((this.riskKeyword || '').trim()) {
        params.keyword = this.riskKeyword.trim()
      }
      return params
    },
    async loadRisk(showLoading) {
      const useLoading = showLoading !== false
      // 进行中不重复发请求；showLoading===false 为 refreshRiskPage 内部调用（此时 riskPageLoading 已为 true，不可早退）
      if (this.riskLoading) return
      if (useLoading && this.riskPageLoading) return
      this.riskLoading = true
      try {
        this.riskData = await getRisk(this.riskQueryParams())
        this.riskListPage = 1
        // 风险数据刷新后摘要卡页码重置为 1
        this.riskCardPage = 1
        this.hotspotCardPage = 1
        this.kolRiskCardPage = 1
      } catch (error) {
        this.riskData = null
        this.$message.error((error && error.message) || '风险数据加载失败')
      } finally {
        this.riskLoading = false
      }
    },
    onRiskListSizeChange(size) {
      this.riskListPageSize = size
      this.riskListPage = 1
    },
    async refreshRiskPage() {
      if (this.riskPageLoading || this.riskLoading || this.kolLoading) return
      this.riskPageLoading = true
      try {
        // false：页面级 riskPageLoading 已占用，子请求不可因页面 loading 早退
        await Promise.all([this.loadRisk(false), this.loadKol(false)])
      } finally {
        this.riskPageLoading = false
      }
    },
    selectRiskTask(taskId) {
      const task = this.tasks.find(item => item.id === taskId)
      this.riskKeyword = task ? this.extractTaskKeyword(task) : ''
      this.refreshRiskPage()
    },
    selectRiskStatDays(days) {
      this.riskStatStartDate = null
      this.riskStatEndDate = null
      this.riskStatDays = Number(days) || 7
      this.refreshRiskPage()
    },
    onRiskStatDateChange() {
      if (this.riskStatStartDate && this.riskStatEndDate &&
        this.riskStatStartDate > this.riskStatEndDate) {
        const temp = this.riskStatStartDate
        this.riskStatStartDate = this.riskStatEndDate
        this.riskStatEndDate = temp
      }
      if (this.riskStatStartDate && this.riskStatEndDate) {
        this.refreshRiskPage()
      }
    },
    clearRiskKeyword() {
      this.riskKeyword = ''
      this.riskTaskId = null
      this.refreshRiskPage()
    },
    openHotspotDetail(cluster) {
      this.openRiskDetail({
        id: 'hotspot-' + cluster.keyword,
        title: cluster.sampleTitle || `热点聚集：${cluster.keyword}`,
        content: cluster.sampleContent,
        platform: cluster.samplePlatform || '多平台',
        level: cluster.level,
        score: cluster.score,
        reason: cluster.reason,
        authorName: '-',
        fans: 0,
        exposure: cluster.exposure,
        interaction: cluster.interaction,
        scoreFactors: cluster.scoreFactors || [],
        suggestion: cluster.suggestion,
        sentiment: '负向',
        keyword: cluster.keyword,
        publishTime: cluster.samplePublishTime || null,
        url: cluster.sampleUrl || ''
      })
    },
    openKolRiskDetail(item) {
      this.openRiskDetail({
        id: item.id,
        title: item.sampleTitle || `${item.authorName} 参与风险`,
        content: item.sampleContent,
        platform: item.platform,
        level: item.level,
        score: item.score,
        reason: item.reason,
        authorName: item.authorName,
        fans: item.fans,
        exposure: item.exposure,
        interaction: item.interaction,
        scoreFactors: item.scoreFactors || [],
        suggestion: item.suggestion,
        sentiment: '负向',
        publishTime: item.samplePublishTime || null,
        url: item.sampleUrl || ''
      })
    },
    async loadKol(showLoading) {
      const useLoading = showLoading !== false
      // 与 loadRisk 对称：卡片刷新受 riskPageLoading 门闩；refreshRiskPage 内部传 false 放行
      if (this.kolLoading) return
      if (useLoading && this.riskPageLoading) return
      this.kolLoading = true
      try {
        const params = { maxRows: 20, ...this.riskQueryParams() }
        this.kolData = await getKol(params)
      } catch (error) {
        this.kolData = null
        this.$message.error((error && error.message) || 'KOL 数据加载失败')
      } finally {
        this.kolLoading = false
      }
    },
    eventQueryParams() {
      const params = {}
      if (this.eventStatStartDate && this.eventStatEndDate) {
        params.startDate = this.eventStatStartDate
        params.endDate = this.eventStatEndDate
      } else {
        Object.assign(params, this.presetDateRange(this.eventStatDays))
      }
      return params
    },
    async loadEvents(showLoading) {
      const useLoading = showLoading !== false
      // 进行中不重复发请求；showLoading===false 为 refreshEventsPage 内部调用（此时 eventPageLoading 已为 true，不可早退）
      if (this.eventLoading) return
      if (useLoading && this.eventPageLoading) return
      this.eventLoading = true
      try {
        this.eventData = await getEvents(this.eventQueryParams())
      } catch (error) {
        this.eventData = null
        this.$message.error((error && error.message) || '事件数据加载失败')
      } finally {
        this.eventLoading = false
      }
      if (this.eventItems.length && !this.eventItems.some(item => item.id === this.activeEventId)) {
        this.activeEventId = this.eventItems[0].id
      }
      if (!this.eventItems.length) {
        this.activeEventId = null
      }
    },
    async refreshEventsPage() {
      if (this.eventPageLoading || this.eventLoading) return
      this.eventPageLoading = true
      try {
        await this.loadEvents(false)
      } finally {
        this.eventPageLoading = false
      }
    },
    selectEventStatDays(days) {
      this.eventStatStartDate = null
      this.eventStatEndDate = null
      this.eventStatDays = Number(days) || 7
      this.refreshEventsPage()
    },
    onEventStatDateChange() {
      if (this.eventStatStartDate && this.eventStatEndDate &&
        this.eventStatStartDate > this.eventStatEndDate) {
        const temp = this.eventStatStartDate
        this.eventStatStartDate = this.eventStatEndDate
        this.eventStatEndDate = temp
      }
      if (this.eventStatStartDate && this.eventStatEndDate) {
        this.refreshEventsPage()
      }
    },
    formatStatDate(text) {
      const raw = String(text || '').trim()
      if (/^\d{8}$/.test(raw)) {
        return `${raw.slice(4, 6)}-${raw.slice(6, 8)}`
      }
      if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
        return raw.slice(5, 10)
      }
      return raw.length > 8 ? raw.slice(0, 10) : raw
    },
    formatDateTime(value) {
      if (!value) return '-'
      const text = String(value)
      return text.length >= 16 ? text.slice(0, 16).replace('T', ' ') : text.replace('T', ' ')
    },
    selectEvent(id) {
      this.activeEventId = id
    },
    async loadOverviewDataRange() {
      try {
        this.overviewDataRange = await getDataRange()
      } catch (error) {
        this.overviewDataRange = null
      }
    },
    formatStatDate(value) {
      if (!value) return ''
      const raw = String(value).replace(/[^0-9]/g, '')
      if (raw.length >= 8) {
        return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`
      }
      return String(value)
    },
    extractTaskKeyword(task) {
      if (!task) return ''
      const raw = task.queryConditionJson
      if (raw) {
        try {
          const arr = JSON.parse(raw)
          if (Array.isArray(arr)) {
            for (const group of arr) {
              if (group && Array.isArray(group.words)) {
                const word = group.words.find(w => w && String(w).trim())
                if (word) return String(word).trim()
              }
            }
          }
        } catch (e) {
          // ignore malformed condition json, fall back to task name
        }
      }
      return task.taskName || ''
    },
    selectKeywordTask() {
      this.activeEventId = null
      this.loadKeywordAnalysis()
    },
    selectKeywordDays(days) {
      this.eventStatStartDate = null
      this.eventStatEndDate = null
      this.eventStatDays = days
      this.loadKeywordAnalysis()
    },
    onKeywordDateChange() {
      if (this.eventStatStartDate && this.eventStatEndDate) {
        this.loadKeywordAnalysis()
      }
    },
    async loadKeywordAnalysis() {
      if (!this.keywordTaskId && this.tasks.length) {
        this.keywordTaskId = this.tasks[0].id
      }
      const keyword = this.keywordText
      this.activeBrandName = keyword || null
      if (!this.keywordTaskId || !keyword) {
        this.activeBrandData = null
        this.eventData = null
        return
      }
      // 与 loadEvents 共用 eventLoading，避免关键词页与事件页堆叠触发后端 events 单飞
      if (this.brandLoading || this.eventLoading || this.eventPageLoading) return
      this.brandLoading = true
      this.eventLoading = true
      try {
        const params = this.eventQueryParams()
        const brandParams = Object.assign({}, params, { keyword })
        const [brand, events] = await Promise.all([
          getBrandAnalysis(brandParams),
          getEvents(params)
        ])
        if (this.keywordText === keyword) {
          this.activeBrandData = brand || null
          this.eventData = events || null
        }
      } catch (error) {
        if (this.keywordText === keyword) {
          this.activeBrandData = null
          this.eventData = null
        }
      } finally {
        this.brandLoading = false
        this.eventLoading = false
      }
    },
    reportTypeLabel() {
      return { daily: '日报', weekly: '周报', monthly: '月报' }[this.reportType] || '日报'
    },
    reportTypeText(type) {
      return { daily: '日报', weekly: '周报', monthly: '月报' }[type] || type || '—'
    },
    reportStatusText(status) {
      return {
        PENDING: '排队中',
        RUNNING: '生成中',
        COMPLETED: '已完成',
        FAILED: '失败',
        CANCELLED: '已取消'
      }[status] || status || '—'
    },
    reportStatusType(status) {
      return {
        PENDING: 'info',
        RUNNING: 'warning',
        COMPLETED: 'success',
        FAILED: 'danger',
        CANCELLED: 'info'
      }[status] || 'info'
    },
    async loadReportHistory() {
      this.reportHistoryLoading = true
      try {
        const records = await listReportJobs()
        this.reportHistory = Array.isArray(records) ? records : []
      } catch (error) {
        this.$message.warning((error && error.message) || '历史报告加载失败')
      } finally {
        this.reportHistoryLoading = false
      }
    },
    openReportHistory(row) {
      if (!row || !row.report) return
      this.report = row.report
      this.reportType = row.reportType || this.reportType
      this.reportTaskId = row.taskId || null
      this.reportJobStatusText = `已查看历史报告：${row.queryText || row.jobNo || ''}`
    },
    reportRangeDays() {
      return { daily: 1, weekly: 7, monthly: 30 }[this.reportType] || 1
    },
    reportTrendGranularity() {
      return this.reportType === 'monthly' ? 'week' : 'day'
    },
    applyReportRange() {
      const days = this.reportRangeDays()
      this.overviewRange = null
      this.overviewStartDateTime = null
      this.overviewEndDateTime = null
      this.overviewDays = days
      this.riskStatStartDate = null
      this.riskStatEndDate = null
      this.riskStatDays = days
      this.eventStatStartDate = null
      this.eventStatEndDate = null
      this.eventStatDays = days
      this.analysisTrendGranularity = this.reportTrendGranularity()
    },
    async generateReport() {
      if (!this.reportTaskId) {
        this.$message.warning('请先选择监测任务')
        return
      }
      this.clearReportPollTimer()
      this.reportGenerating = true
      this.reportJobStatusText = '报告任务已提交，正在进入队列...'
      this.report = null
      try {
        const job = await createReportJob({
          reportType: this.reportType,
          taskId: this.reportTaskId
        })
        this.reportJobId = job.id
        if (job.status === 'PENDING') {
          this.reportJobStatusText = `排队等待中：${job.queryText || '系统忙碌，将按顺序自动生成'}`
          this.$message.info('系统正忙，任务已加入排队')
        } else {
          this.reportJobStatusText = `报告生成中：${job.queryText || '智能分析任务'}`
        }
        this.loadReportHistory()
        this.pollReportJob(job.id)
      } catch (error) {
        this.reportGenerating = false
        this.reportJobStatusText = ''
        this.$message.error((error && error.message) || '报告任务提交失败')
      }
    },
    clearReportPollTimer() {
      if (this.reportPollTimer) {
        window.clearTimeout(this.reportPollTimer)
        this.reportPollTimer = null
      }
    },
    async pollReportJob(jobId) {
      if (!jobId) return
      try {
        const job = await getReportJob(jobId)
        if (job.status === 'COMPLETED') {
          this.report = job.report
          this.reportGenerating = false
          this.reportJobStatusText = '报告已生成'
          this.loadReportHistory()
          this.$message.success(`${this.reportTypeLabel()}已生成`)
          return
        }
        if (job.status === 'FAILED') {
          this.reportGenerating = false
          this.reportJobStatusText = job.errorMessage || '报告生成失败'
          this.$message.error(this.reportJobStatusText)
          this.loadReportHistory()
          return
        }
        if (job.status === 'CANCELLED') {
          this.reportGenerating = false
          this.reportJobStatusText = job.errorMessage || '报告任务已取消'
          this.$message.warning(this.reportJobStatusText)
          this.loadReportHistory()
          return
        }
        if (job.status === 'PENDING') {
          this.reportJobStatusText = `排队等待中：${job.queryText || '等待系统空闲后自动生成'}`
        } else {
          const progress = job.progress != null ? `，进度 ${job.progress}%` : ''
          this.reportJobStatusText = `报告生成中${progress}：${job.queryText || ''}`
        }
        this.reportPollTimer = window.setTimeout(() => this.pollReportJob(jobId), 3000)
      } catch (error) {
        this.reportGenerating = false
        this.reportJobStatusText = (error && error.message) || '报告状态查询失败'
        this.$message.error(this.reportJobStatusText)
      }
    },
    buildLocalReport() {
      const now = new Date()
      const label = this.reportTypeLabel()
      const total = this.overview ? Number(this.overview.totalCount || 0) : this.totalMonitorCount
      const negative = this.overview ? Number(this.overview.negativeCount || 0) : 0
      const negativePercent = total > 0 ? Math.round((negative / total) * 100) : 0
      const sections = []
      const trendLines = this.trendData.slice(0, 12).map(item => `${item.label}：${item.value} 条`)
      const sourceLines = this.sourceAnalysis.slice(0, 6).map(item => `${item.label}：${item.value} 条`)
      const sentimentLines = this.sentimentOverview.map(item => `${item.label}：${item.value} 条，占比 ${item.percent}%`)
      const regionLines = this.regionAnalysis.slice(0, 6).map(item => `${item.label}：${item.value} 条`)

      sections.push({
        title: '一、总体概述',
        lines: [
          this.todaySummaryText,
          `统计范围：${this.overviewRangeText}，共采集内容 ${total} 条，负面 ${negative} 条（占比 ${negativePercent}%）。`,
          `累计曝光约 ${this.exposureText}，传播（转发+分享）约 ${this.spreadText}，涉及高影响力账号 ${this.highImpactAccountCount} 个。`
        ]
      })

      sections.push({
        title: '二、数据分析',
        lines: [
          `声量趋势（${this.analysisTrendGranularity === 'week' ? '按周' : '按日'}）：${trendLines.length ? trendLines.join('；') : '暂无趋势数据'}`,
          `平台分布：${sourceLines.length ? sourceLines.join('；') : '暂无平台数据'}`,
          `情感分布：${sentimentLines.length ? sentimentLines.join('；') : '暂无情感数据'}`,
          `地域分布：${regionLines.length ? regionLines.join('；') : '暂无地域数据'}`
        ]
      })

      const hotLines = this.hotKeywordTop.slice(0, 8).map((item, index) => `${index + 1}. ${item.name}（${item.value} 条）`)
      sections.push({ title: '三、主要热点', lines: hotLines.length ? hotLines : ['暂无热点数据'] })

      const eventLines = this.eventItems.slice(0, 5).map((event, index) =>
        `${index + 1}. ${event.name}｜阶段：${event.stage}｜热度 ${event.heat}｜相关 ${event.articleCount} 篇`)
      sections.push({ title: '四、重点事件', lines: eventLines.length ? eventLines : ['暂无聚类事件'] })

      const riskLines = this.riskItems.slice(0, 5).map((item, index) =>
        `${index + 1}. [${item.level}] ${item.title}（${item.reason}）`)
      sections.push({ title: '五、风险预警', lines: riskLines.length ? riskLines : ['当前范围内暂无重大风险'] })

      const brandLines = []
      if (this.activeBrandData) {
        const b = this.activeBrandData
        brandLines.push(`${b.name}：健康指数 ${b.healthIndex}（${b.healthLabel}），声量 ${b.volume}，曝光 ${this.formatCompact(b.exposure)}，负面 ${b.negative}`)
      }
      sections.push({ title: '六、品牌健康', lines: brandLines.length ? brandLines : ['当前范围内暂无品牌数据'] })

      const kolLines = this.kolItems.slice(0, 5).map((kol, index) =>
        `${index + 1}. ${kol.name}（${kol.platform}）：粉丝 ${this.formatCompact(kol.fans)}，平均互动 ${this.formatCompact(this.kolAvgInteraction(kol))}，影响力 ${kol.influenceScore}，情感倾向 ${kol.sentimentTendency}`)
      sections.push({ title: '七、KOL 影响力', lines: kolLines.length ? kolLines : ['当前范围内暂无 KOL 数据'] })

      const suggestions = []
      if (negativePercent >= 20) {
        suggestions.push('负面占比偏高，建议对 P1/P2 风险优先创建工单并安排人工研判。')
      } else {
        suggestions.push('整体舆情平稳，保持常态化监测即可。')
      }
      if (this.eventItems.some(e => ['爆发', '扩散'].includes(e.stage))) {
        suggestions.push('存在处于爆发/扩散阶段的事件，建议密切跟踪传播路径与首发账号。')
      }
      if (this.riskKolRisks.length || this.kolItems.some(item => (item.influenceScore || 0) >= 80 && item.sentimentTendency === '偏负面')) {
        suggestions.push('高影响账号存在负面传播风险，建议持续跟踪其后续发文并进行人工复核。')
      }
      suggestions.push('定期复盘关键词命中规则，减少无效采集与误报。')
      sections.push({ title: '八、处置建议', lines: suggestions })

      this.report = {
        title: `《企业舆情${label}》`,
        generatedAt: now.toLocaleString('zh-CN'),
        range: this.overviewRangeText,
        sections
      }
    },
    async selectOverviewDays(days) {
      this.overviewDays = Number(days) || 7
      this.overviewRange = null
      this.overviewStartDateTime = null
      this.overviewEndDateTime = null
      await this.$nextTick()
      this.loadOverview()
    },
    applyOverviewDateTimeRange() {
      if (!this.overviewStartDateTime || !this.overviewEndDateTime) {
        this.$message.warning('请选择完整的开始时间和结束时间')
        return
      }
      if (new Date(this.overviewStartDateTime).getTime() > new Date(this.overviewEndDateTime).getTime()) {
        this.$message.warning('开始时间不能晚于结束时间')
        return
      }
      this.overviewRange = [this.overviewStartDateTime, this.overviewEndDateTime]
      this.loadOverview()
    },
    submitApply() {
      if (!this.ensureCollectDateRangeValid()) return
      this.$refs.applyForm.validate(async valid => {
        if (!valid) return
        const payload = this.buildApplyPayload()
        try {
          if (this.editingApplyId) {
            await updateApply(this.editingApplyId, payload)
            this.$message.success('申请已更新')
          } else {
            await createApply(payload)
            this.$message.success('已提交审核')
          }
          this.createVisible = false
          this.loadApplies()
        } catch (error) {
          this.$message.error((error && error.message) || (this.editingApplyId ? '更新申请失败' : '提交申请失败'))
        }
      })
    },
    buildKeywordGroups() {
      return this.form.keywordGroups
        .map(group => (group.words || []).map(word => String(word).trim()).filter(Boolean))
        .filter(words => words.length > 0)
        .map((words, index) => ({
          groupNo: index + 1,
          relation: 'OR',
          words
        }))
    },
    splitWords(text) {
      return String(text || '').split(/[,，\n]/).map(item => item.trim()).filter(Boolean)
    },
    async loadApplies() {
      this.applyListPage = 1
      try {
        const applies = await listApplies()
        this.applies = Array.isArray(applies) ? applies : []
        this.appliesOffline = false
      } catch (error) {
        this.applies = []
        this.appliesOffline = true
      }
      this.updateDashboardCharts()
      this.updateAnalysisCharts()
    },
    refreshAppliesAndTasks() {
      this.loadApplies()
      this.loadTasks()
    },
    async loadTasks() {
      try {
        const tasks = await listTasks()
        this.tasks = Array.isArray(tasks) ? tasks : []
        this.tasksOffline = false
      } catch (error) {
        this.tasks = []
        this.currentTask = null
        this.tasksOffline = true
      }
      if (!this.currentTask) {
        this.currentTask = this.tasks[0] || null
      }
      this.updateDashboardCharts()
      this.updateAnalysisCharts()
    },
    parseKeywordGroups(keywordsJson) {
      try {
        const groups = JSON.parse(keywordsJson || '[]')
        if (!Array.isArray(groups) || !groups.length) {
          return [this.newKeywordGroup()]
        }
        const result = groups
          .map(group => this.newKeywordGroup((group.words || []).map(word => String(word).trim()).filter(Boolean)))
          .filter(group => group.words.length > 0)
        return result.length ? result : [this.newKeywordGroup()]
      } catch (error) {
        return [this.newKeywordGroup()]
      }
    },
    parseExcludeWords(excludeWordsJson) {
      try {
        const words = JSON.parse(excludeWordsJson || '[]')
        return Array.isArray(words) ? words.map(word => String(word).trim()).filter(Boolean) : []
      } catch (error) {
        return []
      }
    },
    keywordGroupsFromJson(keywordsJson) {
      try {
        const groups = JSON.parse(keywordsJson || '[]')
        if (!Array.isArray(groups)) return []
        return groups
          .map(group => (group.words || []).map(word => String(word).trim()).filter(Boolean))
          .filter(words => words.length > 0)
      } catch (error) {
        return []
      }
    },
    parseExternalPlatformTasks(json) {
      try {
        const list = JSON.parse(json || '[]')
        return Array.isArray(list) ? list : []
      } catch (error) {
        return []
      }
    },
    linkedTaskForApply(apply) {
      if (!apply || !apply.id) return null
      return this.tasks.find(task => task.applyId === apply.id) || null
    },
    externalTaskText(task) {
      if (!task || !task.externalPlatformTasksJson) return '—'
      try {
        const items = JSON.parse(task.externalPlatformTasksJson)
        if (!Array.isArray(items)) return '—'
        const ids = items.map(item => item && item.externalTaskId).filter(Boolean)
        return ids.length ? ids.join('、') : '—'
      } catch (error) {
        return '—'
      }
    },
    async loadTaskSummary(taskId) {
      if (!taskId) return
      try {
        const summary = await getTaskSummary(taskId)
        this.$set(this.taskSummaryMap, taskId, summary || {})
      } catch (error) {
        // ignore
      }
    },
    async enterResults() {
      await this.loadTasks()
      if (!this.currentTask && this.tasks.length) {
        this.currentTask = this.tasks[0]
      }
      await this.refreshResults()
    },
    async loadResults(row) {
      this.currentTask = row
      this.resultsPageNo = 1
      await this.refreshResults()
    },
    async onResultsTaskChange(taskId) {
      if (!taskId) {
        this.currentTask = null
        this.results = []
        this.resultsTotal = 0
        this.resultsPageNo = 1
        this.currentTaskSummary = { totalCount: 0, negativeCount: 0, positiveCount: 0, neutralCount: 0, platforms: [] }
        return
      }
      const task = this.tasks.find(item => item.id === taskId)
      if (!task) return
      this.currentTask = task
      this.resultsPageNo = 1
      await this.refreshResults()
    },
    onResultsPageChange(pageNo) {
      this.resultsPageNo = pageNo
      this.refreshResults()
    },
    onResultsPageSizeChange(pageSize) {
      this.resultsPageSize = pageSize
      this.resultsPageNo = 1
      this.refreshResults()
    },
    async refreshResults() {
      if (!this.currentTask) {
        this.results = []
        this.resultsTotal = 0
        this.currentTaskSummary = { totalCount: 0, negativeCount: 0, positiveCount: 0, neutralCount: 0, platforms: [] }
        this.updateDashboardCharts()
        this.updateAnalysisCharts()
        return
      }
      try {
        const page = await listResults(this.currentTask.id, this.resultsPageNo, this.resultsPageSize)
        this.results = page && page.records ? page.records : []
        this.resultsTotal = page && page.total != null ? page.total : this.results.length
        await this.loadTaskSummary(this.currentTask.id)
        this.currentTaskSummary = this.taskSummaryMap[this.currentTask.id] || {
          totalCount: this.resultsTotal || 0,
          negativeCount: 0,
          positiveCount: 0,
          neutralCount: 0,
          platforms: []
        }
      } catch (error) {
        this.results = []
        this.resultsTotal = 0
        this.$message.error((error && error.message) || '结果加载失败')
      }
      this.updateDashboardCharts()
      this.updateAnalysisCharts()
    },
    openRiskDetail(item) {
      if (!item) return
      this.riskDetail = item
      this.riskDetailVisible = true
    },
    goToRiskCenter() {
      this.riskDetailVisible = false
      this.goSection('risk')
    },
    async openDetail(row) {
      const resultId = row && row.id
      const hasNumericId = resultId !== undefined && resultId !== null && resultId !== ''
        && !Number.isNaN(Number(resultId))
      if (this.currentTask && this.authUser && hasNumericId) {
        try {
          this.detail = await getResultDetail(this.currentTask.id, resultId, {
            userId: this.authUser.id,
            clientIp: '127.0.0.1',
            actionType: 'VIEW_DETAIL'
          })
        } catch (error) {
          this.detail = row
        }
      } else {
        this.detail = row
      }
      this.detailVisible = true
    },
    handleSectionSelect(section) {
      this.goSection(section)
    },
    sectionFromRoute() {
      const hash = String(window.location.hash || '').replace(/^#\/?/, '').trim()
      const section = hash.split(/[?&/]/)[0] || 'dashboard'
      return this.normalizeRouteSection(section)
    },
    normalizeRouteSection(section) {
      if (section === 'applies' || section === 'tasks') {
        return 'workOrder'
      }
      if (section === 'results') {
        return 'analysis'
      }
      if (section === 'events' || section === 'brand' || section === 'dataAnalysis') {
        return 'keyword'
      }
      let target = ROUTE_SECTIONS.includes(section) ? section : 'dashboard'
      if (target === 'bigScreen' && !this.bigScreenEnabled) {
        target = 'dashboard'
      }
      // 成员管理仅租户管理员可见；无会话时先保留 hash，避免租户管理员刷新被误踢
      if (target === 'groupAdmin' && this.authUser && !this.isGroupLeader) {
        target = 'dashboard'
      }
      return target
    },
    syncBrowserRoute(section, replace) {
      const target = this.normalizeRouteSection(section)
      const hash = '#/' + target
      if (window.location.hash === hash) return
      if (replace) {
        window.history.replaceState({ section: target }, '', hash)
      } else {
        window.history.pushState({ section: target }, '', hash)
      }
    },
    handleBrowserRouteChange() {
      const section = this.sectionFromRoute()
      if (section !== this.activeSection) {
        this.goSection(section, { skipRoute: true })
        return
      }
      // 非租户管理员打开 #/groupAdmin 时已归一到 dashboard，需把地址栏改掉
      this.syncBrowserRoute(section, true)
    },
    navigateOverviewMetric(item) {
      if (!item || !item.targetSection) return
      this.goSectionWithCurrentConditions(item.targetSection)
    },
    navigateOverviewCard(section) {
      if (!section) return
      this.goSectionWithCurrentConditions(section)
    },
    goSectionWithCurrentConditions(section) {
      this.applyOverviewConditionsToSection(section)
      this.goSection(section)
    },
    jumpEventToAnalysis(event) {
      this.applyEventConditionsToMonitor(event, false)
      this.goSection('analysis')
    },
    jumpEventToRisk(event) {
      this.applyEventConditionsToRisk(event)
      this.goSection('risk')
    },
    applyOverviewConditionsToSection(section) {
      const range = this.currentOverviewDateRange()
      if (section === 'analysis') {
        this.applyDateRangeToMonitor(range)
      }
      if (section === 'events') {
        this.applyDateRangeToEvents(range)
      }
      if (section === 'risk') {
        this.applyDateRangeToRisk(range)
        this.riskKeyword = ''
      }
    },
    applyEventConditionsToMonitor(event, negativeOnly) {
      const range = this.currentEventDateRange(event)
      this.applyDateRangeToMonitor(range)
      this.monitorQuery.keyword = event && event.name ? event.name : ''
      this.monitorQuery.sentiment = negativeOnly ? '负面' : ''
      this.monitorQuery.platform = ''
      this.monitorQuery.author = ''
      this.monitorTab = negativeOnly ? 'warning' : 'all'
      this.monitorPageNo = 1
    },
    applyEventConditionsToRisk(event) {
      const range = this.currentEventDateRange(event)
      this.applyDateRangeToRisk(range)
      this.riskKeyword = event && event.name ? event.name : ''
    },
    applyDateRangeToMonitor(range) {
      if (range && range.length === 2) {
        this.monitorStatStartDate = range[0]
        this.monitorStatEndDate = range[1]
        this.monitorQuery.dateRange = [range[0], range[1]]
      } else {
        this.monitorStatStartDate = null
        this.monitorStatEndDate = null
        this.monitorQuery.dateRange = null
        this.monitorStatDays = this.overviewDays || 7
      }
      this.monitorPageNo = 1
    },
    applyDateRangeToEvents(range) {
      if (range && range.length === 2) {
        this.eventStatStartDate = range[0]
        this.eventStatEndDate = range[1]
      } else {
        this.eventStatStartDate = null
        this.eventStatEndDate = null
        this.eventStatDays = this.overviewDays || 7
      }
    },
    applyDateRangeToRisk(range) {
      if (range && range.length === 2) {
        this.riskStatStartDate = range[0]
        this.riskStatEndDate = range[1]
      } else {
        this.riskStatStartDate = null
        this.riskStatEndDate = null
        this.riskStatDays = this.overviewDays || 7
      }
    },
    currentOverviewDateRange() {
      if (this.overviewRange && this.overviewRange.length === 2) {
        return [this.toDateOnly(this.overviewRange[0]), this.toDateOnly(this.overviewRange[1])]
      }
      return null
    },
    currentEventDateRange(event) {
      if (this.eventStatStartDate && this.eventStatEndDate) {
        return [this.eventStatStartDate, this.eventStatEndDate]
      }
      if (event && event.firstSeen && event.lastSeen) {
        return [this.toDateOnly(event.firstSeen), this.toDateOnly(event.lastSeen)]
      }
      return null
    },
    currentKeywordDateRange() {
      if (this.eventStatStartDate && this.eventStatEndDate) {
        return [this.eventStatStartDate, this.eventStatEndDate]
      }
      const preset = this.presetDateRange(this.eventStatDays)
      return [preset.startDate, preset.endDate]
    },
    jumpKeywordToMonitor(sentiment) {
      if (!this.keywordText) return
      const range = this.currentKeywordDateRange()
      // 仅按采集/统计日期过滤，与关键词分析口径一致；不加发布时间(pubDate)过滤，避免历史发布内容被排除导致无数据
      this.monitorStatStartDate = range[0]
      this.monitorStatEndDate = range[1]
      this.monitorQuery.dateRange = null
      this.monitorQuery.keyword = this.keywordText
      this.monitorQuery.sentiment = sentiment || ''
      this.monitorQuery.platform = ''
      this.monitorQuery.author = ''
      this.monitorTab = sentiment === '负面' ? 'warning' : 'all'
      this.monitorPageNo = 1
      this.goSection('analysis')
    },
    jumpKeywordToRisk() {
      if (!this.keywordText) return
      this.applyDateRangeToRisk(this.currentKeywordDateRange())
      this.riskKeyword = this.keywordText
      this.riskTaskId = this.keywordTaskId
      this.goSection('risk')
    },
    toDateOnly(value) {
      if (!value) return ''
      return String(value).slice(0, 10)
    },
    formatDateOnly(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    presetDateRange(days) {
      const safeDays = Math.max(Number(days) || 1, 1)
      const end = new Date()
      const start = new Date(end.getTime())
      start.setDate(end.getDate() - safeDays + 1)
      return {
        startDate: this.formatDateOnly(start),
        endDate: this.formatDateOnly(end)
      }
    },
    goSection(section, options) {
      const opts = options || {}
      const target = this.normalizeRouteSection(section)
      this.activeSection = target
      // skipRoute 只避免新增历史记录，仍要纠正被权限拦住的 hash（如 #/groupAdmin）
      this.syncBrowserRoute(target, !!opts.skipRoute)
      this.$nextTick(() => {
        const main = this.$el && this.$el.querySelector('.el-main')
        if (main) main.scrollTop = 0
      })
      if (target === 'dashboard') this.loadOverview()
      if (target === 'analysis') {
        this.refreshMonitorPage()
      }
      if (target === 'risk') {
        this.loadTasks()
        this.refreshRiskPage()
      }
      if (target === 'keyword') {
        this.loadTasks().then(() => this.loadKeywordAnalysis())
      }
      if (target === 'workOrder') {
        this.loadApplies()
        this.loadTasks()
      }
      if (target === 'bigScreen') {
        this.loadTasks().then(() => this.loadOverview())
      }
      if (target === 'report') {
        this.loadTasks()
        this.loadReportHistory()
      }
    },
    refreshCurrent() {
      if (this.activeSection === 'dashboard') {
        this.loadOverview()
        this.loadApplies()
        this.loadTasks()
        this.updateDashboardCharts()
      }
      if (this.activeSection === 'analysis') {
        this.refreshMonitorPage()
      }
      if (this.activeSection === 'risk') {
        this.refreshRiskPage()
      }
      if (this.activeSection === 'keyword') {
        this.loadTasks().then(() => this.loadKeywordAnalysis())
      }
      if (this.activeSection === 'workOrder') {
        this.loadApplies()
        this.loadTasks()
      }
      if (this.activeSection === 'bigScreen') {
        this.loadTasks().then(() => {
          this.screenUpdatedAt = new Date().toLocaleString()
          this.loadOverview()
        })
        this.updateBigScreenCharts()
      }
      if (this.activeSection === 'report') {
        this.loadTasks()
        this.loadReportHistory()
      }
    },
    applyStatusText(status) {
      return {
        PENDING_REVIEW: '待启用',
        APPROVED: '启用中',
        PAUSED: '暂停',
        REJECTED: '已停用',
        CANCELLED: '已取消'
      }[status] || status
    },
    applyStatusType(status) {
      return {
        PENDING_REVIEW: 'warning',
        APPROVED: 'success',
        PAUSED: 'info',
        REJECTED: 'danger',
        CANCELLED: 'info'
      }[status] || 'info'
    },
    normalizeScreenProvince(raw) {
      if (!raw) return ''
      let text = String(raw)
        .replace(/中华人民共和国/g, '')
        .replace(/中国/g, '')
        .replace(/壮族自治区|回族自治区|维吾尔自治区|自治区|特别行政区/g, '')
        .replace(/省|市/g, '')
        .trim()
      if (!text) return ''
      const provinces = [
        '内蒙古', '黑龙江', '北京', '天津', '上海', '重庆', '河北', '山西', '辽宁', '吉林',
        '江苏', '浙江', '安徽', '福建', '江西', '山东', '河南', '湖北', '湖南',
        '广东', '海南', '四川', '贵州', '云南', '陕西', '甘肃', '青海', '台湾',
        '广西', '西藏', '宁夏', '新疆', '香港', '澳门'
      ]
      for (let i = 0; i < provinces.length; i += 1) {
        const province = provinces[i]
        if (text.indexOf(province) === 0 || text.indexOf(province) >= 0) return province
      }
      const cityMap = [
        [/广州|深圳|东莞|佛山|珠海|中山|惠州/, '广东'],
        [/杭州|宁波|温州|嘉兴|金华|绍兴/, '浙江'],
        [/南京|苏州|无锡|常州|南通|扬州/, '江苏'],
        [/成都|绵阳|德阳/, '四川'],
        [/武汉|宜昌|襄阳/, '湖北'],
        [/西安|咸阳|宝鸡/, '陕西'],
        [/郑州|洛阳|商丘|开封/, '河南'],
        [/福州|厦门|泉州/, '福建'],
        [/合肥|芜湖/, '安徽'],
        [/长沙|株洲/, '湖南'],
        [/济南|青岛|烟台/, '山东']
      ]
      for (let i = 0; i < cityMap.length; i += 1) {
        if (cityMap[i][0].test(text)) return cityMap[i][1]
      }
      return text.split(/[\s/|，,、-]/)[0] || ''
    },
    normalizeSentiment(sentiment) {
      const text = String(sentiment || '').trim().toLowerCase()
      if (/正面|正向|positive|积极/.test(text)) return 'positive'
      if (/负面|负向|negative|消极/.test(text)) return 'negative'
      return 'neutral'
    },
    sentimentColor(sentiment) {
      return {
        positive: '#10b981',
        neutral: '#94a3b8',
        negative: '#ef4444'
      }[this.normalizeSentiment(sentiment)]
    },
    sentimentType(sentiment) {
      return {
        positive: 'success',
        neutral: 'info',
        negative: 'danger'
      }[this.normalizeSentiment(sentiment)] || 'info'
    },
    riskType(level) {
      return {
        高: 'danger',
        中: 'warning',
        低: 'success'
      }[level] || 'info'
    },
    riskCount(level) {
      return this.results.filter(item => item.riskLevel === level).length
    },
    formatCompact(value) {
      const num = Number(value || 0)
      if (num >= 100000000) return (num / 100000000).toFixed(1) + '亿'
      if (num >= 10000) return (num / 10000).toFixed(1) + '万'
      return String(num)
    },
    riskLevelType(level) {
      return {
        P1: 'danger',
        P2: 'warning',
        P3: 'primary',
        P4: 'info'
      }[level] || 'info'
    },
    riskLevelLabel(level) {
      return {
        P1: 'P1 重大',
        P2: 'P2 较大',
        P3: 'P3 一般',
        P4: 'P4 观察'
      }[level] || level
    },
    kolTendencyType(tendency) {
      if (tendency === '偏负面') return 'danger'
      if (tendency === '中性偏负') return 'warning'
      return 'success'
    },
    warningStars(level) {
      const map = { P1: 5, P2: 4, P3: 3, P4: 2 }
      const n = map[level] || 4
      return '★★★★★☆☆☆☆☆'.slice(5 - n, 10 - n)
    },
    riskFactors(item) {
      if (item && item.scoreFactors && item.scoreFactors.length) {
        return item.scoreFactors
      }
      const factors = ['负面情感']
      if ((item.exposure || 0) >= 100000) factors.push('阅读暴涨')
      if ((item.interaction || 0) >= 5000) factors.push('互动暴涨')
      if ((item.score || 0) >= 80) factors.push('传播加速')
      if ((item.fans || 0) >= 100000) factors.push('KOL参与')
      if ((item.id || '').startsWith('hotspot-')) factors.push('热点聚集')
      return factors
    },
    kolTier(row) {
      const fans = row.fans || 0
      if (fans >= 1000000) return '头部'
      if (fans >= 100000) return '腰部'
      if (fans >= 10000) return '肩部'
      return '尾部'
    },
    kolTierType(row) {
      return { 头部: 'danger', 腰部: 'warning', 肩部: 'primary', 尾部: 'info' }[this.kolTier(row)] || 'info'
    },
    kolVerified(row) {
      return (row.fans || 0) >= 500000 ? '蓝V认证' : ((row.fans || 0) >= 50000 ? '黄V认证' : '未认证')
    },
    kolAvgInteraction(row) {
      if (row.avgInteraction != null) return row.avgInteraction
      const posts = row.posts || 0
      if (!posts) return 0
      return Math.round((row.interactions || 0) / posts)
    },
    kolRiskLevel(row) {
      if (row.riskLevel) return row.riskLevel
      const posts = row.posts || 0
      const ratio = posts ? (row.negativePosts || 0) / posts : 0
      if (ratio >= 0.5) return '高'
      if (ratio >= 0.2) return '中'
      return '低'
    },
    kolRiskType(row) {
      return { 高: 'danger', 中: 'warning', 低: 'success' }[this.kolRiskLevel(row)] || 'info'
    },
    openKolProfile(row) {
      this.kolProfile = row
      this.kolProfileVisible = true
    },
    articleKey(item) {
      return item.id != null ? String(item.id) : (item.externalOpinionId || item.title || '')
    },
    isFavorite(item) {
      return this.favoriteIds.includes(this.articleKey(item))
    },
    isMarked(item) {
      return this.markedIds.includes(this.articleKey(item))
    },
    toggleFavorite(item) {
      const key = this.articleKey(item)
      const idx = this.favoriteIds.indexOf(key)
      if (idx >= 0) {
        this.favoriteIds.splice(idx, 1)
      } else {
        this.favoriteIds.push(key)
        this.$message.success('已收藏')
      }
    },
    toggleMark(item) {
      const key = this.articleKey(item)
      const idx = this.markedIds.indexOf(key)
      if (idx >= 0) {
        this.markedIds.splice(idx, 1)
      } else {
        this.markedIds.push(key)
      }
    },
    shareArticle(item) {
      const link = item.sourceUrl || ''
      if (link && navigator.clipboard) {
        navigator.clipboard.writeText(link).then(() => this.$message.success('原文链接已复制，可转发分享')).catch(() => this.$message.info('请手动复制原文链接'))
      } else {
        this.$message.info('该内容暂无原文链接')
      }
    },
    resetMonitorQuery() {
      this.monitorQuery = { keyword: '', platform: '', sentiment: '', author: '', dateRange: null }
      this.monitorTab = 'all'
      this.monitorStatDays = 7
      this.monitorStatStartDate = null
      this.monitorStatEndDate = null
      this.monitorPageNo = 1
      this.refreshMonitorPage()
    },
    eventStageType(stage) {
      return {
        萌芽: 'info',
        发酵: 'primary',
        扩散: 'warning',
        爆发: 'danger',
        衰减: 'success',
        结束: 'info'
      }[stage] || 'info'
    },
    eventStageIndex(stage) {
      return this.lifecycleStages.indexOf(stage)
    },
    brandHealthColor(index) {
      if (index >= 80) return '#10b981'
      if (index >= 60) return '#128bed'
      if (index >= 40) return '#f59e0b'
      return '#ef4444'
    },
    brandHealthType(label) {
      return {
        优秀: 'success',
        良好: 'primary',
        一般: 'warning',
        预警: 'danger'
      }[label] || 'info'
    },
    sentimentClass(sentiment) {
      return {
        positive: 'zm',
        neutral: 'zx',
        negative: 'fm'
      }[this.normalizeSentiment(sentiment)] || 'zx'
    },
    updateDashboardCharts() {
      if (this.activeSection !== 'dashboard') return
      this.$nextTick(() => {
        this.renderDashboardDonutChart()
        this.renderDashboardTrendChart()
        this.renderDashboardBarChart()
        this.renderDashboardRingChart()
      })
    },
    updateAnalysisCharts() {
      if (this.activeSection !== 'analysis') return
      this.$nextTick(() => {
        this.renderTrendChart()
        this.renderRiskChart()
        this.renderSourceChart()
        this.renderKeywordChart()
      })
    },
    updateBigScreenCharts() {
      if (this.activeSection !== 'bigScreen') return
      this.$nextTick(() => {
        this.renderScreenChinaMapChart()
        this.renderScreenSourceChart()
        this.renderScreenInteractionChart()
        this.renderScreenSentimentChart()
        this.renderScreenEventChart()
      })
    },
    getChart(name, refName) {
      const element = this.$refs[refName]
      if (!element) return null
      let chart = this.chartInstances[name]
      if (chart) {
        const dom = chart.getDom && chart.getDom()
        const disposed = chart.isDisposed && chart.isDisposed()
        if (disposed || !dom || dom !== element) {
          try {
            chart.dispose()
          } catch (error) {
            // ignore stale instance
          }
          delete this.chartInstances[name]
          chart = null
        }
      }
      if (!chart) {
        chart = echarts.init(element)
        this.chartInstances[name] = chart
      }
      return chart
    },
    normalizeTrendDateKey(name) {
      const text = String(name || '').trim()
      if (!text) return ''
      if (/^\d{8}$/.test(text)) {
        return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`
      }
      if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10)
      const normalized = text.replace(/\//g, '-')
      if (/^\d{4}-\d{2}-\d{2}/.test(normalized)) return normalized.slice(0, 10)
      const digits = normalized.replace(/\D/g, '')
      if (digits.length >= 8) {
        return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`
      }
      return text
    },
    getOverviewTrendRange() {
      if (this.overviewRange && this.overviewRange.length === 2) {
        return {
          startDate: this.toDateOnly(this.overviewRange[0]),
          endDate: this.toDateOnly(this.overviewRange[1])
        }
      }
      return this.presetDateRange(this.overviewDays)
    },
    buildDailyTrendPoints(range, valueMap) {
      const startText = range && (range.startDate || range[0])
      const endText = range && (range.endDate || range[1])
      if (!startText || !endText) return []
      const startParts = String(startText).slice(0, 10).split('-').map(Number)
      const endParts = String(endText).slice(0, 10).split('-').map(Number)
      if (startParts.length !== 3 || endParts.length !== 3) return []
      const cursor = new Date(startParts[0], startParts[1] - 1, startParts[2])
      const end = new Date(endParts[0], endParts[1] - 1, endParts[2])
      if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime()) || cursor.getTime() > end.getTime()) {
        return []
      }
      const points = []
      while (cursor.getTime() <= end.getTime()) {
        const label = this.formatDateOnly(cursor)
        points.push({ label, value: valueMap[label] || 0, height: 0 })
        cursor.setDate(cursor.getDate() + 1)
      }
      return points
    },
    renderTrendChart() {
      const chart = this.getChart('trend', 'trendChart')
      if (!chart) return
      chart.setOption({
        color: ['#2563eb', '#38bdf8'],
        tooltip: { trigger: 'axis' },
        grid: { left: 38, right: 20, top: 34, bottom: 36 },
        xAxis: {
          type: 'category',
          data: this.trendData.map(item => item.label),
          axisLine: { lineStyle: { color: '#dbe7f5' } },
          axisLabel: { color: '#64748b' }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: '#edf2f7' } },
          axisLabel: { color: '#64748b' }
        },
        series: [
          {
            name: '声量',
            type: 'bar',
            barWidth: 22,
            itemStyle: { borderRadius: [8, 8, 0, 0] },
            data: this.trendData.map(item => item.value)
          },
          {
            name: '趋势',
            type: 'line',
            smooth: true,
            symbolSize: 8,
            lineStyle: { width: 3 },
            data: this.trendData.map(item => item.value)
          }
        ]
      })
    },
    renderDashboardDonutChart() {
      const chart = this.getChart('dashboardDonut', 'dashboardDonutChart')
      if (!chart) return
      chart.setOption({
        color: this.sentimentOverview.map(item => item.color),
        tooltip: { trigger: 'item' },
        legend: { bottom: 2, textStyle: { color: '#64748b', fontSize: 11 } },
        series: [{
          name: '情感占比',
          type: 'pie',
          radius: ['56%', '74%'],
          center: ['50%', '42%'],
          label: { formatter: '{d}%', color: '#334155' },
          itemStyle: { borderColor: '#fff', borderWidth: 3 },
          data: this.sentimentOverview.map(item => ({
            name: item.label,
            value: item.value
          }))
        }]
      })
    },
    renderDashboardTrendChart() {
      const chart = this.getChart('dashboardTrend', 'dashboardTrendChart')
      if (!chart) return
      const labels = this.trendData.map(item => item.label)
      const volume = this.trendData.map(item => item.value)
      if (!labels.length) {
        chart.clear()
        chart.setOption({
          title: {
            text: '暂无趋势数据',
            left: 'center',
            top: 'center',
            textStyle: { color: '#94a3b8', fontSize: 14, fontWeight: 400 }
          },
          xAxis: { show: false },
          yAxis: { show: false },
          series: []
        })
        chart.resize()
        return
      }
      chart.setOption({
        color: ['#128bed'],
        tooltip: { trigger: 'axis' },
        legend: { top: 0, data: ['总声量'], textStyle: { color: '#64748b' } },
        grid: { left: 42, right: 24, top: 38, bottom: 32 },
        xAxis: { type: 'category', data: labels, axisLabel: { color: '#64748b' }, axisLine: { lineStyle: { color: '#dbe7f5' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#edf2f7' } }, axisLabel: { color: '#64748b' }, minInterval: 1 },
        series: [
          { name: '总声量', type: 'line', smooth: true, data: volume, symbolSize: 7, lineStyle: { width: 3 }, areaStyle: { opacity: 0.08 } }
        ]
      }, true)
      chart.resize()
    },
    renderDashboardBarChart() {
      const chart = this.getChart('dashboardBar', 'dashboardBarChart')
      if (!chart) return
      const items = this.sourceAnalysis.length ? this.sourceAnalysis : [
        { label: '微博', value: 42 },
        { label: '微信', value: 34 },
        { label: '新闻', value: 25 },
        { label: '论坛', value: 18 }
      ]
      chart.setOption({
        color: ['#54b7ff'],
        tooltip: { trigger: 'axis' },
        grid: { left: 36, right: 12, top: 18, bottom: 28 },
        xAxis: { type: 'category', data: items.map(item => item.label), axisLabel: { color: '#64748b' }, axisLine: { lineStyle: { color: '#dbe7f5' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#edf2f7' } }, axisLabel: { color: '#64748b' } },
        series: [{ type: 'bar', barWidth: 24, data: items.map(item => item.value), itemStyle: { borderRadius: [8, 8, 0, 0] } }]
      })
    },
    renderDashboardRingChart() {
      const chart = this.getChart('dashboardRing', 'dashboardRingChart')
      if (!chart) return
      chart.setOption({
        color: ['#128bed', '#54b7ff', '#8bdbff', '#d7eefc'],
        tooltip: { trigger: 'item' },
        legend: { bottom: 0, textStyle: { color: '#64748b', fontSize: 11 } },
        series: [{
          type: 'pie',
          radius: ['52%', '72%'],
          center: ['50%', '42%'],
          label: { formatter: '{b}', color: '#64748b' },
          itemStyle: { borderColor: '#fff', borderWidth: 3 },
          data: [
            { name: '新闻', value: 38 },
            { name: '社媒', value: 31 },
            { name: '论坛', value: 18 },
            { name: '短视频', value: 13 }
          ]
        }]
      })
    },
    renderScreenChinaMapChart() {
      const chart = this.getChart('screenChinaMap', 'screenChinaMapChart')
      if (!chart) return
      const hits = this.screenProvinceHits
      const maxHit = Math.max(...hits.map(item => Number(item.value || 0)), 1)
      chart.setOption({
        tooltip: {
          trigger: 'item',
          formatter: params => {
            if (params.seriesType === 'effectScatter') {
              return `${params.data.name}<br/>命中热度：${params.data.value[2]}`
            }
            const count = params.value || 0
            return `${params.name}<br/>省内 IP 命中：${count}`
          }
        },
        visualMap: {
          show: false,
          min: 0,
          max: maxHit,
          inRange: {
            color: ['rgba(8,52,102,0.95)', '#1268b3', '#00fffc']
          }
        },
        geo: {
          map: 'china',
          roam: false,
          zoom: 1.2,
          label: {
            show: true,
            color: '#8edcff',
            fontSize: 10
          },
          itemStyle: {
            areaColor: 'rgba(8,52,102,0.95)',
            borderColor: '#1dc4ff',
            borderWidth: 1,
            shadowColor: 'rgba(0, 255, 252, 0.35)',
            shadowBlur: 12
          },
          emphasis: {
            label: { color: '#fff' },
            itemStyle: {
              areaColor: '#00fffc'
            }
          }
        },
        series: [
          {
            name: '省份命中',
            type: 'map',
            map: 'china',
            geoIndex: 0,
            data: hits,
            emphasis: {
              label: { color: '#fff' }
            }
          },
          {
            name: 'IP 点位',
            type: 'effectScatter',
            coordinateSystem: 'geo',
            rippleEffect: { brushType: 'stroke', scale: 4 },
            symbolSize: data => Math.max(8, Math.min(22, 6 + Number(data[2] || 0))),
            itemStyle: {
              color: '#fed52f',
              shadowBlur: 12,
              shadowColor: '#fed52f'
            },
            label: {
              show: true,
              formatter: '{b}',
              position: 'right',
              color: '#fff',
              fontSize: 11
            },
            data: this.screenMapPoints,
            zlevel: 2
          }
        ]
      })
    },
    renderScreenSourceChart() {
      const chart = this.getChart('screenSource', 'screenSourceChart')
      if (!chart) return
      chart.setOption({
        color: ['#00fffc', '#239aff', '#fed52f', '#36bea6', '#f62d51'],
        tooltip: { trigger: 'item' },
        legend: { bottom: 0, textStyle: { color: '#69d9ff', fontSize: 11 } },
        series: [{
          name: '数据来源',
          type: 'pie',
          radius: ['45%', '68%'],
          center: ['50%', '42%'],
          label: { color: '#d8f7ff', formatter: '{b}\n{d}%' },
          data: this.sourceAnalysis.map(item => ({ name: item.label, value: item.value }))
        }]
      })
    },
    renderScreenInteractionChart() {
      const chart = this.getChart('screenInteraction', 'screenInteractionChart')
      if (!chart) return
      const items = this.screenAuthorInteractions.slice().reverse()
      chart.setOption({
        color: ['#03b4f5'],
        grid: { left: 78, right: 16, top: 18, bottom: 18 },
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        xAxis: { type: 'value', show: false },
        yAxis: {
          type: 'category',
          data: items.map(item => item.name),
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#d8f7ff', fontSize: 11 }
        },
        series: [{
          type: 'bar',
          data: items.map(item => item.value),
          barWidth: 10,
          itemStyle: { borderRadius: 8 },
          label: { show: true, position: 'right', color: '#00fffc' }
        }]
      })
    },
    renderScreenSentimentChart() {
      const chart = this.getChart('screenSentiment', 'screenSentimentChart')
      if (!chart) return
      chart.setOption({
        color: ['#00fffc', '#fed52f', '#239aff'],
        tooltip: { trigger: 'item' },
        series: [{
          type: 'pie',
          radius: ['48%', '72%'],
          center: ['50%', '50%'],
          label: { color: '#d8f7ff', formatter: '{b} {d}%' },
          data: this.sentimentOverview.map(item => ({ name: item.label, value: item.value }))
        }]
      })
    },
    renderScreenEventChart() {
      const chart = this.getChart('screenEvent', 'screenEventChart')
      if (!chart) return
      const events = this.screenHotEvents.slice().reverse()
      chart.setOption({
        color: ['#00fffc'],
        grid: { left: 92, right: 36, top: 8, bottom: 8, containLabel: false },
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        xAxis: {
          type: 'value',
          show: false
        },
        yAxis: {
          type: 'category',
          data: events.map(item => item.name),
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#69d9ff', fontSize: 10, width: 84, overflow: 'truncate' }
        },
        series: [{
          type: 'bar',
          barWidth: 8,
          data: events.map(item => item.value),
          itemStyle: { borderRadius: [0, 8, 8, 0] },
          label: { show: true, position: 'right', color: '#d8f7ff', fontSize: 10 }
        }]
      })
    },
    renderRiskChart() {
      const chart = this.getChart('risk', 'riskChart')
      if (!chart) return
      chart.setOption({
        tooltip: { trigger: 'item' },
        legend: { bottom: 0, textStyle: { color: '#64748b' } },
        series: [{
          name: '风险等级',
          type: 'pie',
          radius: ['48%', '72%'],
          center: ['50%', '43%'],
          avoidLabelOverlap: true,
          label: { formatter: '{b}\n{c}', color: '#334155' },
          itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 3 },
          data: this.riskAnalysis.map(item => ({
            name: item.label,
            value: item.value,
            itemStyle: { color: item.color }
          }))
        }]
      })
    },
    renderSourceChart() {
      const chart = this.getChart('source', 'sourceChart')
      if (!chart) return
      chart.setOption({
        color: ['#1d4ed8'],
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 44, right: 16, top: 20, bottom: 26 },
        xAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: '#edf2f7' } },
          axisLabel: { color: '#64748b' }
        },
        yAxis: {
          type: 'category',
          data: this.sourceAnalysis.map(item => item.label).reverse(),
          axisLine: { lineStyle: { color: '#dbe7f5' } },
          axisLabel: { color: '#64748b' }
        },
        series: [{
          name: '来源数量',
          type: 'bar',
          barWidth: 14,
          itemStyle: { borderRadius: [0, 8, 8, 0] },
          data: this.sourceAnalysis.map(item => item.value).reverse()
        }]
      })
    },
    renderKeywordChart() {
      const chart = this.getChart('keyword', 'keywordChart')
      if (!chart) return
      const colors = ['#2563eb', '#10b981', '#ef4444', '#f59e0b', '#8b5cf6', '#38bdf8']
      chart.setOption({
        tooltip: {
          formatter: params => `${params.data[2]}：${params.data[3]}`
        },
        grid: { left: 12, right: 12, top: 12, bottom: 12 },
        xAxis: { show: false, min: 0, max: 100 },
        yAxis: { show: false, min: 0, max: 100 },
        series: [{
          type: 'scatter',
          symbolSize: data => Math.max(34, Math.min(82, data[3] * 4)),
          label: {
            show: true,
            formatter: params => params.data[2],
            color: '#fff',
            fontSize: 12
          },
          data: this.keywordAnalysis.map((item, index) => [
            [18, 68, 38, 72, 58, 82][index],
            [70, 62, 34, 42, 78, 24][index],
            item.label,
            item.value
          ]),
          itemStyle: {
            color: params => colors[params.dataIndex % colors.length],
            opacity: 0.9
          }
        }]
      })
    },
    resizeCharts() {
      Object.values(this.chartInstances).forEach(chart => chart && chart.resize())
    }
  }
}
</script>

<style>
body {
  margin: 0;
  background: #eef3f9;
  color: #1f2937;
}

.opinion-console {
  min-height: 100vh;
}

.big-screen {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  color: #d8f7ff;
  background:
    radial-gradient(circle at 50% 42%, rgba(0, 168, 255, 0.18), transparent 32%),
    linear-gradient(180deg, #061b36 0%, #020b1f 100%);
  display: grid;
  grid-template-rows: 82px minmax(0, 1fr) 168px;
}

.big-screen * {
  box-sizing: border-box;
}

.big-screen-bg {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(rgba(21, 96, 160, 0.12) 1px, transparent 1px),
    linear-gradient(90deg, rgba(21, 96, 160, 0.12) 1px, transparent 1px);
  background-size: 42px 42px;
  opacity: 0.55;
  pointer-events: none;
}

.big-screen-header,
.screen-content,
.screen-footer {
  position: relative;
  z-index: 1;
}

.big-screen-header {
  display: grid;
  grid-template-columns: 120px 1fr 560px;
  align-items: center;
  padding: 0 24px;
  min-width: 0;
}

.screen-back,
.screen-refresh {
  border: 1px solid rgba(3, 180, 245, 0.45);
  color: #69d9ff;
  background: rgba(3, 180, 245, 0.08);
  border-radius: 2px;
  height: 30px;
  cursor: pointer;
}

.screen-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
}

.screen-title h1 {
  margin: 0;
  color: #fff;
  font-size: 34px;
  letter-spacing: 8px;
  text-shadow: 0 0 18px rgba(0, 255, 252, 0.7);
}

.screen-title-line {
  width: 190px;
  height: 16px;
  background: linear-gradient(90deg, transparent, #03b4f5, transparent);
  opacity: 0.9;
}

.screen-controls {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  color: #69d9ff;
  font-size: 12px;
  min-width: 0;
}

.screen-controls .el-input__inner {
  width: 132px;
  height: 30px;
  color: #d8f7ff;
  background: rgba(3, 180, 245, 0.08);
  border-color: rgba(3, 180, 245, 0.45);
}

.screen-task-select {
  min-width: 220px;
}

.screen-task-select .el-input__inner {
  width: 220px;
}

.screen-task-option-meta {
  float: right;
  margin-left: 12px;
  color: #69d9ff;
  font-size: 12px;
  opacity: 0.75;
}

.screen-empty {
  color: #69d9ff;
  opacity: 0.7;
  font-size: 12px;
  padding: 12px 0;
  text-align: center;
}

.screen-content {
  min-height: 0;
  display: grid;
  grid-template-columns: 27% 46% 27%;
  gap: 16px;
  padding: 0 22px;
}

.screen-column {
  display: grid;
  gap: 16px;
  min-width: 0;
  min-height: 0;
}

.screen-column.left,
.screen-column.right {
  grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
}

.screen-column.center {
  grid-template-rows: minmax(0, 1fr) minmax(150px, 0.34fr);
}

.screen-panel,
.screen-map-panel {
  position: relative;
  overflow: hidden;
  min-height: 0;
  border: 1px solid rgba(3, 180, 245, 0.35);
  background: linear-gradient(180deg, rgba(5, 36, 74, 0.86), rgba(1, 18, 45, 0.84));
  box-shadow:
    inset 0 0 28px rgba(3, 180, 245, 0.16),
    0 0 18px rgba(3, 180, 245, 0.08);
}

.screen-panel::before,
.screen-map-panel::before {
  content: "";
  position: absolute;
  inset: 0;
  border: 1px solid rgba(0, 255, 252, 0.08);
  pointer-events: none;
}

.screen-panel-title {
  height: 40px;
  padding-left: 18px;
  display: flex;
  align-items: center;
  color: #fff;
  font-size: 17px;
  letter-spacing: 2px;
  background: linear-gradient(90deg, rgba(3, 180, 245, 0.24), transparent);
}

.screen-panel-title::before {
  content: "";
  width: 4px;
  height: 16px;
  margin-right: 8px;
  background: #00fffc;
  box-shadow: 0 0 10px #00fffc;
}

.screen-stat-grid {
  height: calc(100% - 40px);
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  padding: 18px;
  min-height: 0;
}

.screen-stat-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background: rgba(3, 180, 245, 0.08);
  border: 1px solid rgba(3, 180, 245, 0.16);
}

.screen-stat-item i {
  font-size: 34px;
}

.screen-stat-item strong {
  display: block;
  color: #fff;
  font-size: 30px;
  text-align: right;
}

.screen-stat-item span {
  color: #9feaff;
  font-size: 13px;
}

.screen-stat-item.publicsentiment i {
  color: #00fffc;
}

.screen-stat-item.nosensitive i {
  color: #fed52f;
}

.screen-stat-item.sensitive i {
  color: #239aff;
}

.screen-chart {
  width: 100%;
  height: calc(100% - 40px);
  min-height: 0;
}

.screen-mini-chart {
  width: 100%;
  height: calc(100% - 40px);
  min-height: 0;
}

.screen-map-panel {
  display: flex;
  align-items: center;
  justify-content: center;
}

.screen-map-chart {
  width: 100%;
  height: 100%;
  min-height: 0;
}

.china-map-shell {
  position: relative;
  width: 88%;
  height: 88%;
}

.map-glow {
  position: absolute;
  inset: 12% 16%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 255, 252, 0.2), transparent 62%);
  filter: blur(2px);
}

.china-map-shape {
  position: absolute;
  inset: 10% 8%;
  clip-path: polygon(15% 38%, 25% 20%, 43% 18%, 55% 27%, 73% 25%, 86% 42%, 78% 56%, 68% 53%, 61% 72%, 44% 68%, 31% 78%, 22% 61%, 8% 55%);
  background:
    linear-gradient(135deg, rgba(3, 180, 245, 0.24), rgba(0, 255, 252, 0.05)),
    repeating-linear-gradient(45deg, rgba(105, 217, 255, 0.16), rgba(105, 217, 255, 0.16) 1px, transparent 1px, transparent 10px);
  border: 1px solid rgba(0, 255, 252, 0.45);
  box-shadow: inset 0 0 60px rgba(3, 180, 245, 0.28);
}

.map-point {
  position: absolute;
  color: #d8f7ff;
  font-size: 12px;
  transform: translate(-50%, -50%);
}

.map-point i {
  display: block;
  width: 9px;
  height: 9px;
  margin: 0 auto 4px;
  border-radius: 50%;
  background: #00fffc;
  box-shadow: 0 0 14px #00fffc;
}

.map-point.hot i {
  background: #f62d51;
  box-shadow: 0 0 14px #f62d51;
}

.map-point.warn i {
  background: #fed52f;
  box-shadow: 0 0 14px #fed52f;
}

.map-ring {
  position: absolute;
  left: 50%;
  top: 50%;
  border: 1px solid rgba(0, 255, 252, 0.25);
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

.map-ring.one {
  width: 54%;
  height: 36%;
}

.map-ring.two {
  width: 76%;
  height: 52%;
}

.screen-event-list {
  display: grid;
  gap: 10px;
  padding: 14px 18px;
  min-height: 0;
}

.screen-event-row {
  display: grid;
  grid-template-columns: 24px 1fr 36% 48px;
  align-items: center;
  gap: 10px;
  color: #d8f7ff;
  font-size: 12px;
}

.screen-event-row span {
  width: 22px;
  height: 22px;
  line-height: 22px;
  text-align: center;
  color: #001426;
  background: #00fffc;
}

.screen-event-row div {
  height: 6px;
  border-radius: 999px;
  background: rgba(3, 180, 245, 0.16);
  overflow: hidden;
}

.screen-event-row div i {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #239aff, #00fffc);
}

.screen-event-row em {
  color: #00fffc;
  font-style: normal;
  text-align: right;
}

.screen-news-list {
  height: calc(100% - 40px);
  padding: 10px 14px;
  overflow: hidden;
  min-height: 0;
  position: relative;
}

.screen-news-list::before,
.screen-news-list::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 28px;
  z-index: 2;
  pointer-events: none;
}

.screen-news-list::before {
  top: 0;
  background: linear-gradient(180deg, rgba(0, 17, 34, 0.96), rgba(0, 17, 34, 0));
}

.screen-news-list::after {
  bottom: 0;
  background: linear-gradient(0deg, rgba(0, 17, 34, 0.96), rgba(0, 17, 34, 0));
}

.screen-news-scroll-track {
  animation: screen-news-scroll-down var(--screen-news-scroll-duration, 24s) linear infinite;
  will-change: transform;
}

.screen-news-scroll-track:hover {
  animation-play-state: paused;
}

.screen-news-scroll-track.static {
  animation: none;
}

.screen-news-list article {
  display: grid;
  grid-template-columns: 58px 1fr 38px;
  gap: 10px;
  margin-bottom: 8px;
  padding: 9px 8px;
  border-bottom: 1px solid rgba(3, 180, 245, 0.14);
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(3, 180, 245, 0.12), rgba(4, 27, 61, 0.18));
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.screen-news-list article:hover {
  border-color: rgba(0, 255, 252, 0.34);
  box-shadow: 0 0 16px rgba(0, 255, 252, 0.14);
  transform: translateX(-2px);
}

.screen-news-source {
  text-align: center;
  color: #03b4f5;
  font-size: 11px;
}

.screen-news-source i {
  display: block;
  font-size: 18px;
}

.screen-news-content h3 {
  margin: 0;
  color: #fff;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.screen-news-content p {
  margin: 5px 0 0;
  color: #03b4f5;
  font-size: 12px;
}

.screen-news-list em {
  align-self: start;
  padding: 2px 4px;
  border-radius: 2px;
  background: rgba(245, 249, 255, 0.18);
  font-size: 11px;
  font-style: normal;
}

.screen-news-list .zm {
  color: #36bea6;
}

.screen-news-list .zx {
  color: #ffbc34;
}

.screen-news-list .fm {
  color: #f62d51;
}

@keyframes screen-news-scroll-down {
  from {
    transform: translateY(-50%);
  }
  to {
    transform: translateY(0);
  }
}

.screen-footer {
  display: grid;
  grid-template-columns: 27% 46% 27%;
  gap: 16px;
  padding: 16px 22px 18px;
  min-height: 0;
}

.screen-panel.mini {
  min-height: 0;
}

.screen-panel.mini.wide {
  display: block;
}

.screen-ticker {
  height: calc(100% - 40px);
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px 18px;
  padding: 10px 16px;
  color: #d8f7ff;
  font-size: 12px;
  overflow: hidden;
}

@media (max-height: 820px) {
  .big-screen {
    grid-template-rows: 66px minmax(0, 1fr) 138px;
  }

  .big-screen-header {
    padding: 0 16px;
    grid-template-columns: 96px 1fr 500px;
  }

  .screen-title h1 {
    font-size: 26px;
    letter-spacing: 5px;
  }

  .screen-title-line {
    width: 120px;
  }

  .screen-content,
  .screen-footer {
    gap: 10px;
    padding-left: 14px;
    padding-right: 14px;
  }

  .screen-footer {
    padding-top: 10px;
    padding-bottom: 12px;
  }

  .screen-column {
    gap: 10px;
  }

  .screen-column.center {
    grid-template-rows: minmax(0, 1fr) minmax(118px, 0.28fr);
  }

  .screen-panel-title {
    height: 32px;
    font-size: 14px;
  }

  .screen-chart,
  .screen-news-list,
  .screen-mini-chart,
  .screen-ticker {
    height: calc(100% - 32px);
  }

  .screen-stat-grid {
    height: calc(100% - 32px);
    gap: 8px;
    padding: 10px;
  }

  .screen-stat-item {
    padding: 0 12px;
  }

  .screen-stat-item i {
    font-size: 24px;
  }

  .screen-stat-item strong {
    font-size: 22px;
  }

  .screen-event-list {
    gap: 6px;
    padding: 8px 12px;
  }

  .screen-news-list {
    padding: 6px 10px;
  }

  .screen-news-list article {
    padding: 6px 0;
  }
}

@media (max-width: 1360px) {
  .big-screen-header {
    grid-template-columns: 90px 1fr 460px;
  }

  .screen-controls {
    gap: 5px;
  }

  .screen-controls .el-input__inner {
    width: 112px;
  }

  .screen-title h1 {
    font-size: 26px;
    letter-spacing: 4px;
  }

  .screen-title-line {
    width: 90px;
  }
}

.app-shell {
  min-height: 100vh;
}

.sidebar {
  background: linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%);
  border-right: 1px solid #dbe7f5;
  padding: 24px 18px;
  box-sizing: border-box;
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
}

.brand-mark {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: linear-gradient(135deg, #1d4ed8, #38bdf8);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
}

.brand h1 {
  margin: 0;
  font-size: 20px;
  color: #0f172a;
}

.brand p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
}

.side-menu {
  border-right: 0;
}

.side-menu .mdi {
  width: 20px;
  margin-right: 8px;
  font-size: 18px;
  text-align: center;
  vertical-align: middle;
}

.side-menu .el-menu-item {
  border-radius: 12px;
  margin-bottom: 8px;
}

.side-menu .el-menu-item.is-active {
  background: #e0edff !important;
  font-weight: 700;
}

.side-card {
  margin-top: 28px;
  padding: 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #dbeafe;
  box-shadow: 0 12px 30px rgba(29, 78, 216, 0.08);
}

.side-card span,
.eyebrow {
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.side-card strong {
  display: block;
  margin: 8px 0;
  color: #0f172a;
}

.side-card p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}

.app-header {
  height: auto !important;
  padding: 22px 28px;
  background: rgba(255, 255, 255, 0.92);
  border-bottom: 1px solid #dbe7f5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  backdrop-filter: blur(12px);
}

.header-copy h2 {
  margin: 5px 0;
  color: #0f172a;
  font-size: 24px;
}

.header-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-help-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #c9d7ea;
  border-radius: 50%;
  background: #fff;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s ease;
}

.page-help-btn i {
  font-size: 18px;
  line-height: 1;
}

.page-help-btn:hover {
  color: #128bed;
  border-color: #128bed;
  box-shadow: 0 4px 12px rgba(18, 139, 237, 0.16);
}

.screen-help-btn {
  border-color: rgba(3, 180, 245, 0.55);
  background: rgba(3, 28, 58, 0.55);
  color: #7dd3fc;
}

.screen-help-btn:hover {
  color: #e0f2fe;
  border-color: #03b4f5;
  box-shadow: 0 0 12px rgba(3, 180, 245, 0.35);
}

.page-help-body {
  padding: 4px 4px 24px;
}

.page-help-intro {
  margin: 0 0 16px;
  padding: 12px 14px;
  background: #f5f9ff;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  color: #475569;
  font-size: 13px;
  line-height: 1.6;
}

.page-help-group {
  margin-bottom: 18px;
}

.page-help-group h4 {
  margin: 0 0 10px;
  font-size: 14px;
  color: #0f172a;
}

.page-help-item {
  padding: 12px 0;
  border-bottom: 1px dashed #e2e8f0;
}

.page-help-item:last-child {
  border-bottom: none;
}

.page-help-field {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.page-help-meta {
  display: grid;
  grid-template-columns: 64px 1fr;
  gap: 8px;
  margin-top: 4px;
}

.page-help-label {
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.5;
}

.page-help-meta p {
  margin: 0;
  font-size: 12px;
  color: #475569;
  line-height: 1.55;
}

.header-copy p,
.section-header p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}
.header-user {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 8px;
  border-radius: 10px;
  border: 1px solid #e2e8f2;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
}
.header-user:hover { border-color: #128bed; box-shadow: 0 4px 12px rgba(18, 139, 237, 0.14); }
.header-user .mdi { font-size: 30px; color: #2f5bff; }
.header-user-info { display: flex; flex-direction: column; line-height: 1.2; }
.header-user-info strong { font-size: 13px; color: #1f2d3d; }
.header-user-info span { font-size: 11px; color: #8a97a8; }

.content {
  padding: 24px 28px 36px;
}

.page-section {
  max-width: 1480px;
  margin: 0 auto;
}

/* 首页 / 分析页 / 风险页 / 关键词页整页宿主很长。Element 默认 .el-loading-spinner { position:absolute; top:50% } 相对整段高度，
   即使用 top:28% 在约 4000px 的页面上仍落在首屏下方。sticky + vh 相对视口中上部，
   且不离开 loading mask，避免 fixed 相对整屏水平居中压到侧栏。
   子选择器只命中宿主直接子 mask：分析页列表局部 .monitor-list-panel、
   风险页 KOL 卡片 .panel-card 的 mask 都不是 .risk-center-page / .daily-monitor-page 的直接子节点，不受影响。
   关键词页也覆盖：v-loading 挂在 .keyword-page-section，不要扩成泛化的 .page-section。 */
.dashboard-page-section > .el-loading-mask .el-loading-spinner,
.daily-monitor-page > .el-loading-mask .el-loading-spinner,
.risk-center-page > .el-loading-mask .el-loading-spinner,
.keyword-page-section > .el-loading-mask .el-loading-spinner {
  position: sticky;
  top: 28vh;
  margin-top: 0;
}

.hero-panel {
  min-height: 150px;
  padding: 28px;
  margin-bottom: 18px;
  border-radius: 22px;
  background:
    radial-gradient(circle at 85% 20%, rgba(56, 189, 248, 0.34), transparent 28%),
    linear-gradient(135deg, #0f3b82, #1d4ed8);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 18px 38px rgba(29, 78, 216, 0.22);
}

.hero-panel .eyebrow,
.hero-panel p {
  color: rgba(255, 255, 255, 0.76);
}

.hero-panel h3 {
  margin: 8px 0;
  font-size: 28px;
}

.hero-actions {
  display: flex;
  gap: 10px;
}

.image-like-dashboard {
  display: grid;
  gap: 14px;
  color: #263445;
}

.overview-header,
.metric-strip,
.overview-card {
  background: #fff;
  border: 1px solid #e7edf5;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
}

.overview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
}

.overview-header h3 {
  margin: 4px 0 0;
  font-size: 20px;
}

.overview-actions {
  display: flex;
  gap: 8px;
}

.overview-timebar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 16px;
  padding: 12px 16px;
  background: #fff;
  border: 1px solid #e7edf5;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
}

.overview-timebar-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #1f2d3d;
}

.overview-timebar-label i {
  color: #128bed;
  font-size: 18px;
}

.overview-timebar-presets {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.time-pill {
  border: 1px solid #d7e0ec;
  background: #f5f8fc;
  color: #4a5a6d;
  padding: 5px 14px;
  border-radius: 16px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.time-pill:hover {
  border-color: #128bed;
  color: #128bed;
}

.time-pill.active {
  background: #128bed;
  border-color: #128bed;
  color: #fff;
}

.time-pill.loading {
  opacity: 0.72;
}

.overview-timebar-custom {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

.overview-timebar-custom .el-date-editor.el-input {
  width: 190px;
}

.overview-timebar-separator {
  font-size: 12px;
  color: #8a97a8;
}

.overview-timebar-hint {
  font-size: 12px;
  color: #8a97a8;
}

.metric-strip {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0;
  padding: 14px 10px;
}

@media (max-width: 1200px) {
  .metric-strip {
    grid-template-columns: repeat(3, 1fr);
  }
}

.metric-tile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 18px;
  border-right: 1px solid #edf2f7;
  transition: background 0.2s ease, transform 0.2s ease;
}

.metric-tile:last-child {
  border-right: 0;
}

.clickable-metric {
  cursor: pointer;
}

.clickable-metric:hover {
  background: #f8fbff;
  transform: translateY(-1px);
}

.metric-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.metric-icon.blue {
  color: #128bed;
  background: #e7f4ff;
}

.metric-icon.red {
  color: #ef4444;
  background: #fff1f1;
}

.metric-icon.green {
  color: #10b981;
  background: #ecfdf5;
}

.metric-icon.purple {
  color: #8b5cf6;
  background: #f4f0ff;
}

.metric-tile span,
.metric-tile em {
  display: block;
  color: #94a3b8;
  font-size: 12px;
  font-style: normal;
}

.metric-tile strong {
  display: block;
  margin: 2px 0;
  color: #0f172a;
  font-size: 22px;
}

.dashboard-grid {
  display: grid;
  gap: 14px;
}

.dashboard-grid.top {
  grid-template-columns: 1fr 1fr;
}

/* 负面/风险相关卡片强调：红色主题、左侧强调条、更醒目的标题 */
.overview-card.negative-emphasis {
  border: 1px solid #f7c6c6;
  border-left: 4px solid #ef4444;
  background: linear-gradient(180deg, #fff6f6 0%, #ffffff 42%);
  box-shadow: 0 10px 26px rgba(239, 68, 68, 0.12);
}
.overview-card.negative-emphasis .overview-card-title strong {
  color: #d92d20;
}
.overview-card.negative-emphasis .overview-card-title i {
  color: #ef4444;
}

.dashboard-grid.middle,
.dashboard-grid.bottom {
  grid-template-columns: 1fr 1fr;
}

.overview-card {
  min-width: 0;
  padding: 14px;
}

.overview-card.wide {
  padding: 0;
}

.overview-card-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.overview-card.wide .overview-card-title {
  padding: 14px 16px 0;
}

.overview-card-title strong {
  color: #263445;
  font-size: 15px;
}

.overview-card-title span {
  color: #98a4b3;
  font-size: 12px;
}

.nav-card-title {
  cursor: pointer;
}

.nav-card-title:hover strong,
.nav-card-title:focus strong {
  color: #1d4ed8;
}

.compact-news-item {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 0;
  border-bottom: 1px solid #f0f3f7;
  transition: background 0.2s ease;
}

.compact-news-item.clickable {
  cursor: pointer;
}

.compact-news-item.clickable:hover {
  background: #f8fbff;
}

.compact-news-item:last-child {
  border-bottom: 0;
}

.compact-news-item h4,
.overview-news-row h4,
.warning-row h4 {
  margin: 0;
  color: #263445;
  font-size: 13px;
  line-height: 1.45;
}

.compact-news-item p {
  margin: 4px 0 0;
  color: #94a3b8;
  font-size: 12px;
}

.keyword-hit-card {
  padding: 14px;
  background: linear-gradient(90deg, #fff 0%, #f3f9ff 100%);
}

.keyword-hit-card h2 {
  margin: 0 0 12px;
  color: #34495e;
  font-size: 16px;
  line-height: 1;
  font-weight: 800;
}

.keyword-hit-card p {
  margin: 0 0 14px;
  color: #a1adba;
  font-size: 13px;
  font-weight: 300;
}

.keyword-hit-list {
  display: grid;
  gap: 15px;
}

.keyword-hit-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.keyword-hit-meta strong,
.keyword-hit-meta span {
  color: #34495e;
  font-size: 14px;
  line-height: 1;
  font-weight: 700;
}

.keyword-hit-track {
  height: 6px;
  border-radius: 999px;
  background: #f3f5f7;
  overflow: hidden;
}

.keyword-hit-track i {
  display: block;
  height: 100%;
  min-width: 32px;
}

.dashboard-chart {
  width: 100%;
  height: 238px;
}

.dashboard-chart.donut,
.dashboard-chart.small {
  height: 230px;
}

.hidden-echart {
  display: none;
}

.sentiment-donut-box {
  min-height: 230px;
  display: grid;
  grid-template-columns: 150px 1fr;
  align-items: center;
  gap: 18px;
}

.css-donut {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.04);
}

.css-donut-inner {
  width: 86px;
  height: 86px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08);
}

.css-donut-inner strong {
  color: #0f172a;
  font-size: 22px;
  line-height: 1;
}

.css-donut-inner span {
  margin-top: 6px;
  color: #94a3b8;
  font-size: 12px;
}

.sentiment-legend {
  display: grid;
  gap: 12px;
}

.sentiment-legend div {
  display: grid;
  grid-template-columns: 10px 1fr auto auto;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 12px;
}

.sentiment-legend i {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.sentiment-legend strong {
  color: #0f172a;
  font-size: 14px;
}

.sentiment-legend em {
  color: #128bed;
  font-style: normal;
  font-weight: 700;
}

.dashboard-chart.trend {
  height: 280px;
}

.overview-news-row,
.warning-row {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  gap: 10px;
  padding: 13px 16px;
  border-top: 1px solid #edf2f7;
}

.news-source-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e7f4ff;
  color: #128bed;
  display: flex;
  align-items: center;
  justify-content: center;
}

.news-content h4 {
  cursor: pointer;
}

.news-content h4:hover {
  color: #128bed;
}

.news-content p,
.warning-row p {
  margin: 5px 0;
  color: #697586;
  font-size: 12px;
  line-height: 1.6;
}

.news-content div {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  color: #9aa6b2;
  font-size: 12px;
}

.news-tags {
  display: flex;
  gap: 6px;
  align-items: flex-start;
}

.word-cloud-demo {
  min-height: 260px;
  display: flex;
  align-content: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px 14px;
  padding: 18px;
  background: linear-gradient(135deg, #fbfdff, #f2f8ff);
}

.word-cloud-demo span {
  font-weight: 700;
  line-height: 1.1;
}

.mini-rank-table {
  width: 100%;
  border-collapse: collapse;
}

.mini-rank-table tr {
  border-bottom: 1px solid #edf2f7;
}

.mini-rank-table td {
  padding: 10px 4px;
  color: #64748b;
  font-size: 13px;
}

.mini-rank-table td:nth-child(2),
.mini-rank-table td:nth-child(3) {
  text-align: right;
  color: #263445;
}

.warning-row {
  grid-template-columns: 26px minmax(0, 1fr) 70px;
  align-items: flex-start;
}

.warning-row > i {
  color: #ef4444;
  font-size: 20px;
}

.warning-row > span {
  color: #ef4444;
  font-size: 12px;
}

.daily-monitor-page {
  color: #333;
  min-height: 480px;
  position: relative;
}

.monitor-breadcrumb {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 54px;
  padding: 0 18px;
  margin-bottom: 12px;
  background: #fff;
  border: 1px solid #e7edf5;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
}

.monitor-breadcrumb span,
.monitor-breadcrumb i {
  color: #8896a8;
  font-size: 13px;
  margin-right: 8px;
}

.monitor-breadcrumb strong {
  color: #128bed;
  font-size: 13px;
}

.monitor-total {
  color: #7b8794;
  font-size: 13px;
}

.monitor-total strong {
  color: #128bed;
  font-size: 22px;
  margin: 0 5px;
}

.monitor-filter-card {
  margin-bottom: 14px;
  background: #fff;
  border: 1px solid #e7edf5;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
}

.selected-line,
.filter-line,
.more-filter-row {
  display: flex;
  align-items: flex-start;
  min-height: 42px;
  padding: 0 14px;
  border-bottom: 1px solid #f0f3f7;
}

.more-filter-row {
  border-bottom: 0;
}

.filter-label {
  width: 100px;
  flex: none;
  padding-top: 12px;
  color: #616b78;
  font-size: 13px;
}

.filter-label.active {
  color: #128bed;
}

.selected-tags,
.filter-options,
.dropdown-filters {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0;
}

.selected-tags span {
  position: relative;
  max-width: 230px;
  padding: 3px 22px 3px 8px;
  color: #128bed;
  border: 1px solid #128bed;
  border-radius: 5px;
  background: #fff;
  font-size: 12px;
}

.selected-tags span::after {
  content: "X";
  position: absolute;
  right: 7px;
  top: 3px;
  color: #128bed;
  font-size: 12px;
}

.selected-tags .empty-tag {
  border: none;
  color: #94a3b8;
  padding: 3px 8px;
  max-width: none;
}

.selected-tags .empty-tag::after {
  content: none;
}

.filter-pill,
.badge-option,
.dropdown-filter {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: #f4f6f9;
  color: #747f88;
  font-size: 12px;
  cursor: pointer;
}

.filter-pill em {
  margin-left: 2px;
  color: #b6b6b6;
  font-style: normal;
}

.filter-pill.active,
.badge-option.active {
  color: #fff;
  background: #128bed;
}

.more-filter {
  flex: none;
  margin-top: 9px;
  border: 0;
  background: transparent;
  color: #979797;
  font-size: 12px;
  cursor: pointer;
}

.more-filter:hover {
  color: #128bed;
}

.monitor-workspace {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

.monitor-left-panel,
.monitor-list-panel {
  background: #fff;
  border: 1px solid #e7edf5;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
}

.monitor-left-panel {
  padding: 14px;
}

.side-filter-box {
  padding-bottom: 14px;
  margin-bottom: 14px;
  border-bottom: 1px solid #edf2f7;
}

.side-filter-box:last-child {
  margin-bottom: 0;
  border-bottom: 0;
}

.side-filter-box h4 {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 10px;
  color: #3f4a56;
  font-size: 14px;
  font-weight: 600;
}

.side-filter-box h4 .mdi {
  color: #128bed;
  font-size: 17px;
}

.badge-option {
  margin: 0 6px 8px 0;
}

.badge-option .mdi {
  margin-right: 4px;
  font-size: 14px;
}

.badge-option.positive {
  color: #0f8f55;
  background: #e8f8ef;
}

.badge-option.neutral {
  color: #64748b;
  background: #eef2f7;
}

.badge-option.negative {
  color: #d64545;
  background: #fff0ef;
}

.date-range-demo {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.date-range-demo .monitor-date-input {
  width: 100%;
  max-width: 100%;
}

.date-range-demo >>> .el-date-editor.el-input,
.date-range-demo >>> .el-date-editor.el-input__inner {
  width: 100% !important;
}

.date-range-demo >>> .el-input__inner {
  padding-left: 28px;
  padding-right: 8px;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.monitor-list-panel {
  min-width: 0;
}

.monitor-query-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 0 14px;
  border-bottom: 1px solid #eef2f7;
  margin-bottom: 10px;
}
.monitor-query-bar .mq-keyword { width: 200px; }
.monitor-query-bar .mq-select { width: 130px; }
.monitor-query-bar .mq-date { width: 240px; }
.warning-stars { color: #f7b500; font-size: 12px; letter-spacing: 1px; margin-right: 4px; }
.risk-factors { display: flex; flex-wrap: wrap; gap: 6px; margin: 6px 0; }
.risk-factor-chip {
  font-size: 12px;
  color: #d4380d;
  background: #fff2e8;
  border: 1px solid #ffd8bf;
  border-radius: 4px;
  padding: 1px 8px;
}
.event-insight-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}
.first-account { display: flex; align-items: center; gap: 12px; }
.first-account .mdi { font-size: 40px; color: #f0a020; }
.first-account strong { font-size: 15px; color: #1f2d3d; }
.first-account p { margin: 4px 0 0; color: #8a97a8; font-size: 12px; }
.kol-profile-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 20px 16px;
}
.kol-profile-head .mdi { font-size: 52px; color: #2f5bff; }
.kol-profile-head h3 { margin: 0; font-size: 18px; }
.kol-profile-head p { margin: 4px 0 0; color: #8a97a8; font-size: 13px; }
.kol-profile { padding: 20px 20px 30px; }
@media (max-width: 1100px) {
  .event-insight-row { grid-template-columns: 1fr; }
}
.article-tags .mediatag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: #6b7a8d;
  background: #eef2f7;
  border-radius: 4px;
  padding: 0 6px;
}
.article-actions .is-fav { color: #f0a020; }
.article-actions .is-fav .mdi { color: #f0a020; }

.monitor-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #edf2f7;
}

.monitor-toolbar strong {
  display: block;
  color: #263445;
  font-size: 16px;
}

.monitor-toolbar span {
  color: #97a3b1;
  font-size: 12px;
}

.monitor-search {
  display: flex;
  gap: 8px;
  width: 340px;
}

.monitor-tabs {
  display: flex;
  gap: 24px;
  padding: 0 16px;
  height: 44px;
  align-items: center;
  border-bottom: 1px solid #edf2f7;
}

.monitor-tabs span {
  color: #64748b;
  font-size: 13px;
  cursor: pointer;
}

.monitor-tabs span.active {
  color: #128bed;
  font-weight: 600;
}

.monitor-article {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) 96px;
  gap: 10px;
  padding: 18px 16px;
  border-bottom: 1px solid #edf2f7;
}

.monitor-article:hover {
  background: #fbfdff;
}

.article-title-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.article-title-row h3 {
  margin: 0;
  color: #263445;
  font-size: 16px;
  line-height: 1.5;
  cursor: pointer;
}

.article-title-row h3:hover {
  color: #128bed;
}

.article-main p {
  margin: 8px 0 10px;
  color: #697586;
  font-size: 13px;
  line-height: 1.7;
}

.article-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}

.ntag,
.govtag,
.ipotag,
.eventlable,
.industrylable,
.categorylable {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 2px;
  font-size: 12px;
  line-height: 22px;
}

.ntag {
  color: #128bed;
  background: #e7f4ff;
}

.ipotag {
  color: #747f88;
  background: #fff7f6;
}

.eventlable {
  color: #85621b;
  background: #fff2d6;
}

.industrylable {
  color: #1c6356;
  background: #d7f2ed;
}

.categorylable {
  color: #153385;
  background: #d4e0ff;
}

.article-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  color: #9aa6b2;
  font-size: 12px;
}

.article-meta .mdi {
  margin-right: 3px;
  color: #128bed;
  font-size: 14px;
}

.article-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.article-actions .el-button {
  margin-left: 0;
}

.article-actions .mdi {
  margin-right: 4px;
  font-size: 14px;
}

.more-filter .fa,
.dropdown-filter .fa {
  margin-left: 4px;
}

.monitor-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  color: #8a96a6;
  font-size: 12px;
}

.analysis-hero {
  min-height: 150px;
  padding: 28px;
  margin-bottom: 18px;
  border-radius: 22px;
  background:
    radial-gradient(circle at 18% 20%, rgba(96, 165, 250, 0.24), transparent 28%),
    linear-gradient(135deg, #0f172a, #1e3a8a);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 18px 38px rgba(15, 23, 42, 0.2);
}

.analysis-hero .eyebrow,
.analysis-hero p,
.analysis-score span,
.analysis-score p {
  color: rgba(255, 255, 255, 0.72);
}

.analysis-hero h3 {
  margin: 8px 0;
  font-size: 28px;
}

.analysis-score {
  min-width: 190px;
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.1);
  text-align: center;
}

.analysis-score strong {
  display: block;
  margin: 8px 0;
  font-size: 42px;
}

.analysis-grid {
  margin-bottom: 18px;
}

.chart-card {
  min-height: 330px;
}

.trend-chart {
  height: 238px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 14px;
  padding-top: 16px;
}

.trend-item {
  flex: 1;
  text-align: center;
  color: #64748b;
  font-size: 12px;
}

.trend-bar-wrap {
  height: 170px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin-bottom: 8px;
  border-radius: 999px;
  background: #eef4fb;
}

.trend-bar {
  width: 42%;
  min-height: 18px;
  border-radius: 999px;
  background: linear-gradient(180deg, #38bdf8, #2563eb);
}

.trend-item strong {
  display: block;
  color: #0f172a;
  margin-bottom: 4px;
}

.progress-row + .progress-row {
  margin-top: 22px;
}

.progress-row > div {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  color: #475569;
}

.source-list {
  display: grid;
  gap: 12px;
}

.source-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafc;
  color: #475569;
}

.source-item strong {
  color: #1d4ed8;
  font-size: 18px;
}

.keyword-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  min-height: 156px;
}

.advice-list {
  margin: 0;
  padding-left: 18px;
  color: #475569;
  line-height: 1.9;
}

.stat-grid {
  margin-bottom: 18px;
}

.stat-card {
  min-height: 118px;
  padding: 18px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid #e5edf7;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);
  margin-bottom: 18px;
}

.stat-card span {
  color: #64748b;
  font-size: 13px;
}

.stat-card strong {
  display: block;
  margin: 10px 0 6px;
  font-size: 32px;
  color: #0f172a;
}

.stat-card p {
  margin: 0;
  color: #94a3b8;
  font-size: 12px;
}

.stat-card.blue {
  border-top: 4px solid #2563eb;
}

.stat-card.green {
  border-top: 4px solid #10b981;
}

.stat-card.orange {
  border-top: 4px solid #f59e0b;
}

.stat-card.purple {
  border-top: 4px solid #8b5cf6;
}

.panel-card {
  border: 0;
  border-radius: 18px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.07);
  overflow: hidden;
}

.panel-card + .panel-card {
  margin-top: 18px;
}

.section-sub {
  font-size: 12px;
  color: #8a97a8;
  margin-left: 8px;
}

.empty-hint {
  padding: 30px;
  text-align: center;
  color: #9aa7b8;
}

.empty-hint.small {
  padding: 12px;
  font-size: 13px;
}

.today-focus-row {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 14px;
}

.today-summary-card .today-summary-text {
  margin: 8px 0 0;
  line-height: 1.9;
  color: #33465c;
  font-size: 14px;
}

.latest-warning-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px dashed #eef2f7;
  cursor: pointer;
}

.latest-warning-item:hover .latest-warning-title {
  color: #128bed;
}

.latest-warning-title {
  font-size: 13px;
  color: #33465c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.risk-events-card {
  padding-bottom: 4px;
}

.risk-event-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 4px;
  border-bottom: 1px dashed #eef2f7;
  cursor: pointer;
}

.risk-event-row:hover {
  background: #f8fbff;
}

.risk-event-score {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 64px;
  flex-shrink: 0;
}

.risk-event-score strong {
  font-size: 20px;
  color: #ef4444;
}

.risk-event-main {
  flex: 1;
  min-width: 0;
}

.risk-event-main h4 {
  margin: 0 0 4px;
  font-size: 14px;
  color: #1f2d3d;
}

.risk-event-main p {
  margin: 0 0 6px;
  color: #64748b;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.risk-event-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  color: #94a3b8;
  font-size: 12px;
}

.risk-event-arrow {
  color: #cbd5e1;
  font-size: 20px;
}

.warning-row.clickable {
  cursor: pointer;
}

.warning-row.clickable:hover h4 {
  color: #128bed;
}

.risk-detail-header {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 18px;
}

.risk-score-ring.large {
  width: 72px;
  height: 72px;
  flex-shrink: 0;
}

.risk-score-ring.large strong {
  width: 56px;
  height: 56px;
  font-size: 20px;
}

.risk-detail-header h2 {
  margin: 8px 0 6px;
  font-size: 18px;
}

.risk-detail-factors {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 0;
}

.risk-detail-content {
  margin: 16px 0;
  padding: 14px 16px;
  border-radius: 14px;
  background: #f8fafc;
}

.risk-detail-content p {
  margin: 6px 0 0;
  line-height: 1.8;
  color: #334155;
  white-space: pre-wrap;
  word-break: break-word;
}

.risk-detail-suggestion {
  padding: 14px 16px;
  border-radius: 14px;
  background: #fff7ed;
  color: #9a3412;
}

.risk-detail-suggestion p {
  margin: 6px 0 0;
  line-height: 1.7;
}

.risk-detail-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.channel-selected-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}

.keyword-editor {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.keyword-group {
  border: 1px dashed #d5dbe6;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fafbff;
}

.keyword-group-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.keyword-group-name {
  font-size: 13px;
  font-weight: 600;
  color: #1f2d3d;
}

.keyword-group-relation {
  font-size: 12px;
  color: #94a3b8;
}

.keyword-group-remove {
  margin-left: auto;
  padding: 0;
  color: #f56c6c;
}

.keyword-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.keyword-tags .el-tag {
  margin: 0;
}

.keyword-tag-input {
  width: 120px;
}

.keyword-tag-add {
  border-style: dashed;
  color: #2f5bff;
  border-color: #b6c4ff;
  background: #fff;
}

.keyword-group-new {
  align-self: flex-start;
  border-style: dashed;
}

.keyword-editor-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
}

.keyword-recommend-entry {
  padding: 0;
  color: #2f5bff;
  font-size: 13px;
}

.keyword-recommend-tip {
  font-size: 12px;
  color: #94a3b8;
}

.keyword-recommend-dialog .recommend-schemes {
  max-height: 380px;
  overflow-y: auto;
}

.keyword-recommend-dialog .el-radio-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.recommend-scheme {
  border: 1px solid #e4e9f2;
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.recommend-scheme:hover {
  border-color: #b6c4ff;
}

.recommend-scheme.is-selected {
  border-color: #2f5bff;
  background: #f5f8ff;
}

.recommend-scheme-head {
  display: flex;
  align-items: center;
}

.recommend-scheme-name {
  font-size: 13px;
  font-weight: 600;
  color: #1f2d3d;
}

.recommend-scheme-desc {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
}

.recommend-scheme-rule {
  margin-top: 6px;
  font-size: 12px;
  color: #1f2d3d;
  line-height: 1.6;
}

.recommend-scheme-label {
  display: inline-block;
  min-width: 48px;
  color: #94a3b8;
}

.recommend-scheme-dup {
  margin-top: 6px;
  font-size: 12px;
  color: #e6a23c;
}

.detail-keyword-groups {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-keyword-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.detail-keyword-group-name {
  font-size: 12px;
  color: #94a3b8;
}

.detail-keyword-group .el-tag,
.detail-exclude-tag {
  margin: 0;
}

.channel-cell-text {
  font-size: 12px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.muted-text { color: #94a3b8; font-size: 12px; }

.apply-external-block {
  margin-top: 16px;
}

.apply-external-block h4 {
  margin: 0 0 8px;
  font-size: 14px;
}

.external-task-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
}

.results-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.results-task-select {
  width: 240px;
}

.results-pagination {
  margin-top: 16px;
  text-align: right;
}

.results-task-option-meta {
  float: right;
  margin-left: 16px;
  color: #94a3b8;
  font-size: 12px;
}

.task-stats-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 10px;
}

.task-stat-item {
  display: flex;
  flex-direction: column;
  min-width: 72px;
}

.task-stat-item span {
  font-size: 11px;
  color: #64748b;
}

.task-stat-item strong {
  font-size: 20px;
  color: #0f172a;
}

.task-stat-item.negative strong { color: #dc2626; }
.task-stat-item.positive strong { color: #16a34a; }

.task-platform-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-left: auto;
}

.task-platform-chip {
  font-size: 11px;
  padding: 4px 8px;
  background: #e2e8f0;
  border-radius: 999px;
  color: #334155;
}

.insight-page {
  display: grid;
  gap: 16px;
}

.keyword-task-select {
  width: 280px;
}

.keyword-section-title {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 4px 2px 2px;
  border-left: 3px solid #128bed;
  padding-left: 10px;
  margin-top: 4px;
}

.keyword-section-title strong {
  font-size: 15px;
  color: #1f2d3d;
}

.keyword-section-title strong .mdi {
  color: #128bed;
  margin-right: 4px;
}

.keyword-section-title span {
  font-size: 12px;
  color: #90a4be;
}

.keyword-event-switch {
  margin-left: auto;
  width: 240px;
}

.keyword-empty-alert {
  margin: 2px 0 4px;
}

.risk-task-select {
  margin-left: auto;
  width: 180px;
}

.risk-level-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

.risk-type-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}

.risk-type-card .section-header {
  align-items: center;
}

.risk-type-desc {
  margin: 0 0 10px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}

.risk-type-item {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  gap: 2px 8px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
}

.risk-type-item:last-child { border-bottom: none; }
.risk-type-item:hover { background: #f8fafc; margin: 0 -8px; padding: 8px; border-radius: 8px; }

.risk-type-item-title {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.risk-type-item-meta {
  grid-column: 2;
  font-size: 11px;
  color: #94a3b8;
}

.risk-type-card.negative >>> .el-card__header { border-bottom-color: #fecaca; }
.risk-type-card.hotspot >>> .el-card__header { border-bottom-color: #fde68a; }
.risk-type-card.kol >>> .el-card__header { border-bottom-color: #bfdbfe; }

.risk-card-pagination {
  display: flex;
  justify-content: center;
  padding-top: 10px;
}

.risk-card-pagination >>> .el-pagination {
  padding: 0;
  font-weight: 400;
}

.risk-level-tile {
  border-radius: 14px;
  padding: 16px 18px;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 2px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
}

.risk-level-tile.p1 { background: linear-gradient(135deg, #ef4444, #b91c1c); }
.risk-level-tile.p2 { background: linear-gradient(135deg, #f59e0b, #d97706); }
.risk-level-tile.p3 { background: linear-gradient(135deg, #3b82f6, #2563eb); }
.risk-level-tile.p4 { background: linear-gradient(135deg, #64748b, #475569); }

.risk-level-name { font-size: 13px; opacity: 0.9; }
.risk-level-tile strong { font-size: 30px; line-height: 1.2; }
.risk-level-tile em { font-size: 12px; opacity: 0.85; font-style: normal; }

.risk-list-filter {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 4px 0 12px;
  border-bottom: 1px solid #eef2f7;
  margin-bottom: 4px;
}

.risk-list-filter-item {
  width: 140px;
}

.risk-list-filter-search {
  width: 220px;
}

.risk-list-filter-total {
  margin-left: auto;
  font-size: 12px;
  color: #8a94a6;
}

.risk-list-pagination {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
}

.risk-item {
  display: flex;
  gap: 16px;
  padding: 16px 4px;
  border-bottom: 1px solid #eef2f7;
  cursor: pointer;
}

.risk-item:hover {
  background: #f8fbff;
}

.risk-item-score {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 76px;
}

.risk-score-ring {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: conic-gradient(#ef4444 calc(var(--score) * 1%), #f1f5f9 0);
}

.risk-score-ring strong {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: #fff;
  display: grid;
  place-items: center;
  font-size: 16px;
  color: #ef4444;
}

.risk-item-main { flex: 1; }
.risk-item-main h4 { margin: 0 0 6px; font-size: 15px; color: #1f2d3d; }
.risk-reason { margin: 0 0 8px; color: #e0662b; font-size: 13px; }
.risk-item-meta { display: flex; flex-wrap: wrap; gap: 14px; color: #8a97a8; font-size: 12px; }
.risk-item-meta i { margin-right: 3px; }
.risk-item-action { display: flex; align-items: center; }

.analytics-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.mini-bar-chart {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 180px;
  padding-top: 10px;
}

.mini-bar-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  gap: 6px;
}

.mini-bar {
  width: 60%;
  min-height: 6px;
  border-radius: 6px 6px 0 0;
  background: linear-gradient(180deg, #54b7ff, #128bed);
}

.mini-bar-col span { font-size: 11px; color: #8a97a8; }

.rank-bar-list { display: grid; gap: 12px; }
.rank-bar-row, .sentiment-bar-row { display: flex; align-items: center; gap: 10px; }
.rank-bar-label { width: 72px; font-size: 13px; color: #33465c; }
.rank-bar-track {
  flex: 1;
  height: 10px;
  border-radius: 6px;
  background: #eef2f7;
  overflow: hidden;
}
.rank-bar-track i {
  display: block;
  height: 100%;
  border-radius: 6px;
  background: #2f5bff;
}
.rank-bar-value { width: 52px; text-align: right; font-size: 13px; color: #1f2d3d; }
.sentiment-bars { display: grid; gap: 12px; }
.sentiment-bar-row span:first-child { width: 42px; font-size: 13px; }
.sentiment-bar-row span:last-child { width: 44px; text-align: right; font-size: 13px; }

.hot-keyword-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px 24px;
}

.hot-keyword-item { display: flex; align-items: center; gap: 10px; }
.hot-keyword-rank {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #eef2f7;
  color: #64748b;
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 600;
}
.hot-keyword-rank.top { background: #f02f5f; color: #fff; }
.hot-keyword-name { width: 84px; font-size: 13px; color: #1f2d3d; }
.hot-keyword-track { flex: 1; height: 8px; border-radius: 5px; background: #eef2f7; overflow: hidden; }
.hot-keyword-track i { display: block; height: 100%; background: linear-gradient(90deg, #54b7ff, #2f5bff); }
.hot-keyword-value { width: 44px; text-align: right; font-size: 12px; color: #64748b; }

.kol-name { display: flex; align-items: center; gap: 6px; }
.kol-name i { color: #2f5bff; font-size: 18px; }
.kol-influence { display: flex; align-items: center; gap: 8px; }
.kol-influence .rank-bar-track { width: 70px; }

.event-center-page {
  min-height: 480px;
  position: relative;
  display: grid;
  gap: 14px;
}

.event-timebar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 16px;
  padding: 12px 16px;
  background: #fff;
  border: 1px solid #e7edf5;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
}

.event-timebar-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #1f2d3d;
}

.event-timebar-label i {
  color: #128bed;
  font-size: 18px;
}

.event-timebar-presets {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.event-timebar-custom {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  flex-wrap: wrap;
}

.event-date-input {
  width: 140px;
}

.event-timebar-hint {
  font-size: 12px;
  color: #8a97a8;
}

.event-detail-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
}

.event-center {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  align-items: start;
}

.event-list-panel {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
  padding: 12px;
  max-height: calc(100vh - 160px);
  overflow-y: auto;
}

.event-list-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 6px 12px;
}

.event-list-head strong { display: flex; align-items: center; gap: 6px; color: #1f2d3d; }

.event-list-item {
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  margin-bottom: 6px;
}

.event-list-item:hover { background: #f5f8fc; }
.event-list-item.active { background: #eef4ff; border-color: #cddcff; }

.event-list-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.event-name { font-size: 14px; font-weight: 600; color: #1f2d3d; }
.event-list-meta { display: flex; justify-content: space-between; font-size: 12px; color: #8a97a8; margin: 6px 0; }
.event-heat { color: #f0662b; }
.event-heat-track { height: 6px; border-radius: 4px; background: #eef2f7; overflow: hidden; }
.event-heat-track i { display: block; height: 100%; background: linear-gradient(90deg, #ffae28, #f0662b); }

.event-detail-panel { display: grid; gap: 16px; }

.event-detail-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  background: #fff;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
}

.event-detail-head h3 { margin: 0 0 8px; font-size: 20px; color: #1f2d3d; }
.event-detail-tags { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.event-time { font-size: 12px; color: #8a97a8; }

.event-detail-metrics {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 10px;
  font-size: 13px;
  color: #5a6b80;
}
.event-detail-metrics span { display: inline-flex; align-items: center; gap: 4px; }
.event-detail-metrics .mdi { color: #128bed; }
.event-detail-metrics .neg { color: #e5504d; }
.event-detail-metrics .neg .mdi { color: #e5504d; }

.event-jump-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.event-heat-badge {
  text-align: center;
  padding: 6px 18px;
  border-radius: 12px;
  background: linear-gradient(135deg, #ffae28, #f0662b);
  color: #fff;
}
.event-heat-badge span { font-size: 12px; display: block; opacity: 0.9; }
.event-heat-badge strong { font-size: 26px; }

.event-stat-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
.event-stat {
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.05);
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.event-stat span { font-size: 12px; color: #8a97a8; }
.event-stat strong { font-size: 22px; color: #1f2d3d; }
.event-stat.clickable {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.event-stat.clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(18, 139, 237, 0.12);
}

.event-lifecycle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
  position: relative;
}
.event-lifecycle::before {
  content: '';
  position: absolute;
  left: 40px;
  right: 40px;
  top: 30px;
  height: 2px;
  background: #e7edf5;
  z-index: 0;
}
.lifecycle-node {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 1;
}
.lifecycle-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #d7e0ec;
}
.lifecycle-node.done .lifecycle-dot { background: #35b99f; border-color: #35b99f; }
.lifecycle-node.current .lifecycle-dot {
  background: #f0662b;
  border-color: #f0662b;
  box-shadow: 0 0 0 5px rgba(240, 102, 43, 0.18);
}
.lifecycle-label { font-size: 13px; color: #64748b; }
.lifecycle-node.current .lifecycle-label { color: #f0662b; font-weight: 600; }

.event-summary-card .today-summary-text { display: flex; gap: 8px; align-items: flex-start; }
.event-summary-card .today-summary-text i { color: #2f5bff; font-size: 18px; margin-top: 2px; }

.event-sample { padding: 10px 0; border-bottom: 1px dashed #eef2f7; }
.event-sample h4 { margin: 0 0 4px; font-size: 14px; color: #1f2d3d; cursor: pointer; }
.event-sample h4:hover { color: #128bed; }
.event-sample p {
  margin: 0 0 6px;
  color: #5a6a7d;
  font-size: 13px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.event-sample-meta { display: flex; gap: 12px; align-items: center; font-size: 12px; color: #8a97a8; }

@media (max-width: 1100px) {
  .event-center { grid-template-columns: 1fr; }
  .event-list-panel { max-height: none; }
  .event-stat-row { grid-template-columns: repeat(2, 1fr); }
}

.wo-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.report-job-status {
  margin: 10px 0 0;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f4f8ff;
  color: #4f6f95;
  font-size: 13px;
}

.report-history {
  margin-top: 14px;
}

.report-history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  color: #1f2d3d;
}

.report-history-table {
  margin-bottom: 12px;
}

.report-history-empty {
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px dashed #d8e2ef;
  border-radius: 8px;
  color: #8a97a8;
  font-size: 13px;
}

.report-empty {
  text-align: center;
  padding: 60px 20px;
  color: #8a97a8;
}
.report-empty .mdi { font-size: 56px; color: #c7d2e0; }
.report-empty p { margin-top: 14px; font-size: 14px; }
.report-doc {
  max-width: 820px;
  margin: 0 auto;
  padding: 20px 10px;
  color: #1f2d3d;
  line-height: 1.9;
}
.report-doc h1 { text-align: center; font-size: 24px; margin: 0 0 10px; }
.report-doc .report-meta { text-align: center; color: #8a97a8; font-size: 13px; margin-bottom: 24px; }
.report-section { margin-bottom: 20px; }
.report-section h2 {
  font-size: 17px;
  color: #128bed;
  border-left: 4px solid #128bed;
  padding-left: 10px;
  margin: 0 0 10px;
}
.report-section ul { margin: 0; padding-left: 22px; }
.report-section li { margin-bottom: 6px; font-size: 14px; }

.work-order-detail {
  padding: 20px 20px 30px;
}
.work-order-block {
  margin-top: 18px;
  padding: 14px 16px;
  border: 1px solid #edf2f7;
  border-radius: 12px;
  background: #f8fafc;
}
.work-order-block h4 {
  margin: 0 0 8px;
  color: #1f2d3d;
  font-size: 14px;
}
.work-order-block p {
  margin: 0 0 6px;
  color: #5f6f89;
  line-height: 1.7;
  white-space: pre-wrap;
}

.ai-agent-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
}
.ai-agent-card {
  padding: 18px;
  border: 1px solid #e5edf7;
  border-radius: 16px;
  background: linear-gradient(180deg, #f8fbff 0%, #fff 100%);
}
.ai-agent-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.ai-agent-head h3 {
  margin: 0 0 6px;
  color: #1f2d3d;
  font-size: 16px;
}
.ai-agent-head p {
  margin: 0;
  color: #64748b;
  line-height: 1.6;
}
.ai-agent-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin: 16px 0;
}
.ai-agent-metrics div {
  padding: 10px;
  border-radius: 12px;
  background: #eef6ff;
}
.ai-agent-metrics span {
  display: block;
  color: #64748b;
  font-size: 12px;
}
.ai-agent-metrics strong {
  display: block;
  margin-top: 4px;
  color: #128bed;
  font-size: 18px;
}
.ai-agent-insights {
  margin: 0;
  padding-left: 18px;
  color: #475569;
  line-height: 1.8;
}

.ai-agent-run-log {
  margin-top: 18px;
  padding: 16px;
  border: 1px solid #e5edf7;
  border-radius: 16px;
  background: #f8fafc;
}

.mini-section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.mini-section-title span {
  color: #64748b;
  font-size: 12px;
}

.overview-empty-alert {
  margin: 0 0 16px;
}
.brand-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.brand-add {
  display: flex;
  align-items: center;
  gap: 8px;
}
.brand-tab-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.brand-tab-close {
  color: #9aa7b8;
  font-size: 12px;
  border-radius: 50%;
  padding: 2px;
  transition: all 0.2s ease;
}
.brand-tab-close:hover { color: #f56c6c; background: rgba(245, 108, 108, 0.12); }
.brand-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f2;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
}
.brand-tab:hover { border-color: #128bed; }
.brand-tab.active { border-color: #128bed; box-shadow: 0 8px 20px rgba(18, 139, 237, 0.16); }
.brand-tab-name { font-size: 14px; font-weight: 600; color: #1f2d3d; }

.brand-detail { display: grid; gap: 16px; }

.brand-health-card {
  display: flex;
  align-items: center;
  gap: 24px;
  background: #fff;
  border-radius: 16px;
  padding: 20px 26px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
}
.brand-health-ring {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: conic-gradient(var(--health-color) calc(var(--health) * 1%), #eef2f7 0);
  flex-shrink: 0;
}
.brand-health-inner {
  width: 92px;
  height: 92px;
  border-radius: 50%;
  background: #fff;
  display: grid;
  place-items: center;
  text-align: center;
}
.brand-health-inner strong { font-size: 30px; color: #1f2d3d; }
.brand-health-inner span { font-size: 12px; color: #8a97a8; }
.brand-health-meta h3 { margin: 0 0 8px; font-size: 22px; color: #1f2d3d; }
.brand-health-meta p { margin: 8px 0 0; color: #8a97a8; font-size: 13px; }

.brand-stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
.brand-stat {
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.05);
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.brand-stat span { font-size: 12px; color: #8a97a8; }
.brand-stat strong { font-size: 22px; color: #1f2d3d; }
.brand-stat.negative strong { color: #ef4444; }
.brand-stat.clickable { cursor: pointer; transition: transform 0.2s ease, box-shadow 0.2s ease; }
.brand-stat.clickable:hover { transform: translateY(-1px); box-shadow: 0 12px 28px rgba(18, 139, 237, 0.14); }

.sentiment-bar-row.clickable { cursor: pointer; border-radius: 8px; padding: 2px 4px; transition: background 0.15s ease; }
.sentiment-bar-row.clickable:hover { background: #f2f7ff; }

.section-header.clickable-header { cursor: pointer; }
.section-header.clickable-header:hover strong { color: #128bed; }

.brand-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

@media (max-width: 1100px) {
  .brand-grid { grid-template-columns: 1fr; }
  .brand-stat-grid { grid-template-columns: repeat(2, 1fr); }
  .brand-health-card { flex-direction: column; text-align: center; }
}

@media (max-width: 1100px) {
  .today-focus-row,
  .analytics-grid,
  .hot-keyword-grid,
  .risk-type-grid { grid-template-columns: 1fr; }
  .risk-level-strip { grid-template-columns: repeat(2, 1fr); }
}

.card-header,
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-header h3 {
  margin: 4px 0;
  color: #0f172a;
  font-size: 20px;
}

.apply-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.data-table {
  width: 100%;
}

.auth-loading {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 14px;
}

.table-action-group {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.table-action-group .el-button-group {
  display: inline-flex;
  vertical-align: middle;
}

.hint {
  color: #909399;
  font-size: 13px;
}

.days-select {
  width: 90px;
  margin: 0 8px;
}

.apply-drawer >>> .el-drawer__header {
  margin-bottom: 0;
  padding: 18px 24px;
  border-bottom: 1px solid #eef2f7;
  font-size: 16px;
  font-weight: 600;
  color: #1f2d3d;
}

.apply-drawer >>> .el-drawer__body {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;
  overflow: hidden;
}

.apply-drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px 12px;
}

.apply-drawer-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 24px;
  border-top: 1px solid #eef2f7;
  background: #fff;
}

.apply-advanced-collapse {
  border-top: none;
  border-bottom: none;
  margin-bottom: 8px;
}

.apply-advanced-title {
  font-size: 13px;
  color: #909399;
}

.apply-advanced-title .el-icon-warning-outline {
  color: #e6a23c;
  margin-right: 4px;
}

.apply-update-text {
  font-size: 13px;
  color: #606266;
}

.apply-section-divider {
  margin: 8px 0 16px;
  padding-top: 14px;
  border-top: 1px solid #eef2f7;
  font-size: 14px;
  font-weight: 600;
  color: #1f2d3d;
  text-align: center;
}

.apply-estimate-value {
  margin-left: 12px;
  color: #128bed;
  font-weight: 600;
}

.apply-estimate-meta {
  margin-top: 6px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}

.apply-estimate-error {
  margin-top: 6px;
  color: #b91c1c;
  font-size: 12px;
  line-height: 1.5;
}

.detail {
  padding: 0 24px 24px;
}

.detail-card {
  margin: 16px 0;
}

@media (max-width: 900px) {
  .app-shell {
    display: block;
  }

  .sidebar {
    width: 100% !important;
    min-height: auto;
  }

  .app-header,
  .hero-panel,
  .section-header {
    display: block;
  }

  .header-actions {
    margin-top: 14px;
  }
}
</style>
